/**
 * A Prompt is a ...
 * You can ...
 * Read more about Prompts
 * in this [article](<LINK_TO_KB_ARTICLE>).
 */
export interface PromptProxy {
  /**
   * @format GUID
   * @readonly
   */
  id?: string;
}

export interface GenerationCompletedResultEvent {
  /**
   * @format GUID
   * @readonly
   */
  predictionId?: string;
  generationResult?: GenerateContentModelResponse;
  /** @maxLength 10000 */
  errorMessage?: string | null;
}

export interface GenerateContentModelResponse
  extends GenerateContentModelResponseResponseOneOf {
  /** OpenAI chat completion response. */
  openAiChatCompletionResponse?: OpenaiproxyV1CreateChatCompletionResponse;
  /** Azure OpenAI chat completion response. */
  azureChatCompletionResponse?: CreateChatCompletionResponse;
  /** Google Gemini generate content response. */
  googleGeminiGenerateContentResponse?: GenerateContentResponse;
  /** Anthropic Claude via Amazon Bedrock generate content response. */
  anthropicClaudeResponse?: InvokeAnthropicClaudeModelResponse;
  /** Anthropic Claude via Google vertex generate content response. */
  googleAnthropicClaudeResponse?: V1InvokeAnthropicClaudeModelResponse;
  /** Native Anthropic API proxy generate content response. */
  invokeAnthropicModelResponse?: InvokeAnthropicModelResponse;
  /** Llama via Amazon Bedrock text completion response. */
  llamaModelResponse?: InvokeLlamaModelResponse;
  /** Invoke Amazon Converse API response. */
  amazonConverseResponse?: InvokeConverseResponse;
  /** Llama via ML Platform text completion response. */
  mlPlatformLlamaModelResponse?: InvokeMlPlatformLlamaModelResponse;
  /** Perplexity chat completion response. */
  perplexityChatCompletionResponse?: InvokeChatCompletionResponse;
  /** OpenAI image generation response. */
  openAiCreateImageResponse?: CreateImageResponse;
  /** Stability AI text to image response. */
  stabilityAiTextToImageResponse?: V1TextToImageResponse;
  /** Stability AI generate core response. */
  stabilityAiGenerateCoreResponse?: GenerateCoreResponse;
  /** Stability AI - Stable Diffusion 3.0 & 3.5 response. */
  stabilityAiStableDiffusionResponse?: GenerateStableDiffusionResponse;
  /** Black Forest Labs image generation response. */
  blackForestLabsGenerateImageResponse?: GenerateAnImageResponse;
  /** Replicate image generation response. */
  replicateCreatePredictionResponse?: CreatePredictionResponse;
  /** Stability AI - Edit Image with prompt response. */
  stabilityAiEditImageWithPromptResponse?: EditImageWithPromptResponse;
  /** Runware AI - Flux TextToImage response. */
  runwareTextToImageResponse?: TextToImageResponse;
  /** Google AI - Generate Image with Imagen Model response. */
  googleGenerateImageResponse?: V1GenerateImageResponse;
  /** Google AI - Generate Video response. */
  googleGenerateVideoResponse?: GenerateVideoResponse;
  /** ML generate image response. */
  mlPlatformGenerateImageResponse?: GenerateImageMlPlatformResponse;
  /** OpenAI image creation response. */
  openAiCreateOpenAiImageResponse?: CreateImageOpenAiResponse;
  /** OpenAI image edit response. */
  openAiEditOpenAiImageResponse?: EditImageOpenAiResponse;
  /** Google create chat completion response. */
  googleCreateChatCompletionResponse?: V1CreateChatCompletionResponse;
  /** ML Platform - invoke an OpenAI-type interface with a JSON string */
  mlPlatformOpenAiRawResponse?: InvokeMlPlatformOpenAIChatCompletionRawResponse;
  /** Runware Video inference response */
  runwareVideoInferenceResponse?: VideoInferenceResponse;
  /** Open AI Responses API response */
  openAiResponsesResponse?: V1OpenAiResponsesResponse;
  /** Open AI Responses API response via Azure */
  azureOpenAiResponsesResponse?: OpenAiResponsesResponse;
  /** OpenAI video generation response */
  openAiCreateVideoResponse?: CreateVideoResponse;
  /** Fireworks - OpenAI payload */
  fireworksInvokeCustomOpenAiResponse?: InvokeCustomOpenAiModelResponse;
  /** Bytedance - Create content task response (video generation) */
  bytedanceCreateContentTaskResponse?: CreateContentTaskResponse;
  /** Bytedance - Generate image response (Seedream models) */
  bytedanceGenerateImageResponse?: GenerateImageResponse;
  /** Extracted generated content data from the model's response. */
  generatedContent?: GeneratedContent;
  /** Extracted cost of the request in microcents. */
  cost?: string | null;
  /** Token usage information. */
  tokenUsage?: V1TokenUsage;
  /** Metadata about the response, such as finish reason. */
  responseMetadata?: ResponseMetadata;
}

/** @oneof */
export interface GenerateContentModelResponseResponseOneOf {
  /** OpenAI chat completion response. */
  openAiChatCompletionResponse?: OpenaiproxyV1CreateChatCompletionResponse;
  /** Azure OpenAI chat completion response. */
  azureChatCompletionResponse?: CreateChatCompletionResponse;
  /** Google Gemini generate content response. */
  googleGeminiGenerateContentResponse?: GenerateContentResponse;
  /** Anthropic Claude via Amazon Bedrock generate content response. */
  anthropicClaudeResponse?: InvokeAnthropicClaudeModelResponse;
  /** Anthropic Claude via Google vertex generate content response. */
  googleAnthropicClaudeResponse?: V1InvokeAnthropicClaudeModelResponse;
  /** Native Anthropic API proxy generate content response. */
  invokeAnthropicModelResponse?: InvokeAnthropicModelResponse;
  /** Llama via Amazon Bedrock text completion response. */
  llamaModelResponse?: InvokeLlamaModelResponse;
  /** Invoke Amazon Converse API response. */
  amazonConverseResponse?: InvokeConverseResponse;
  /** Llama via ML Platform text completion response. */
  mlPlatformLlamaModelResponse?: InvokeMlPlatformLlamaModelResponse;
  /** Perplexity chat completion response. */
  perplexityChatCompletionResponse?: InvokeChatCompletionResponse;
  /** OpenAI image generation response. */
  openAiCreateImageResponse?: CreateImageResponse;
  /** Stability AI text to image response. */
  stabilityAiTextToImageResponse?: V1TextToImageResponse;
  /** Stability AI generate core response. */
  stabilityAiGenerateCoreResponse?: GenerateCoreResponse;
  /** Stability AI - Stable Diffusion 3.0 & 3.5 response. */
  stabilityAiStableDiffusionResponse?: GenerateStableDiffusionResponse;
  /** Black Forest Labs image generation response. */
  blackForestLabsGenerateImageResponse?: GenerateAnImageResponse;
  /** Replicate image generation response. */
  replicateCreatePredictionResponse?: CreatePredictionResponse;
  /** Stability AI - Edit Image with prompt response. */
  stabilityAiEditImageWithPromptResponse?: EditImageWithPromptResponse;
  /** Runware AI - Flux TextToImage response. */
  runwareTextToImageResponse?: TextToImageResponse;
  /** Google AI - Generate Image with Imagen Model response. */
  googleGenerateImageResponse?: V1GenerateImageResponse;
  /** Google AI - Generate Video response. */
  googleGenerateVideoResponse?: GenerateVideoResponse;
  /** ML generate image response. */
  mlPlatformGenerateImageResponse?: GenerateImageMlPlatformResponse;
  /** OpenAI image creation response. */
  openAiCreateOpenAiImageResponse?: CreateImageOpenAiResponse;
  /** OpenAI image edit response. */
  openAiEditOpenAiImageResponse?: EditImageOpenAiResponse;
  /** Google create chat completion response. */
  googleCreateChatCompletionResponse?: V1CreateChatCompletionResponse;
  /** ML Platform - invoke an OpenAI-type interface with a JSON string */
  mlPlatformOpenAiRawResponse?: InvokeMlPlatformOpenAIChatCompletionRawResponse;
  /** Runware Video inference response */
  runwareVideoInferenceResponse?: VideoInferenceResponse;
  /** Open AI Responses API response */
  openAiResponsesResponse?: V1OpenAiResponsesResponse;
  /** Open AI Responses API response via Azure */
  azureOpenAiResponsesResponse?: OpenAiResponsesResponse;
  /** OpenAI video generation response */
  openAiCreateVideoResponse?: CreateVideoResponse;
  /** Fireworks - OpenAI payload */
  fireworksInvokeCustomOpenAiResponse?: InvokeCustomOpenAiModelResponse;
  /** Bytedance - Create content task response (video generation) */
  bytedanceCreateContentTaskResponse?: CreateContentTaskResponse;
  /** Bytedance - Generate image response (Seedream models) */
  bytedanceGenerateImageResponse?: GenerateImageResponse;
}

/** Model generation result, at least one of the fields should be present */
export interface GeneratedContent {
  /**
   * Zero or more textual results. Only present when the model returned a text.
   * @maxSize 1000
   */
  texts?: TextContent[];
  /**
   * Zero or more images. Only present when the model returned an image.
   * @maxSize 1000
   */
  images?: MediaContent[];
  /**
   * Zero or more videos. Only present when the model returned a video.
   * @maxSize 1000
   */
  videos?: MediaContent[];
  /**
   * Zero or more thinking texts. Only present when the model returned a thought.
   * @maxSize 1000
   */
  thinkingTexts?: ThinkingTextContent[];
  /**
   * Zero or more tool call requests. Only present when the model requested to call a tool.
   * @maxSize 1000
   */
  tools?: ToolUseContent[];
}

export interface TextContent {
  /**
   * Generated text
   * @maxLength 1000000
   */
  generatedText?: string | null;
  /**
   * Optional. An opaque signature for the thought so it can be reused in subsequent requests. A base64-encoded string.
   * @maxLength 10000000
   */
  thoughtSignature?: string | null;
}

export interface MediaContent {
  /**
   * Mime type, e.g. "image/jpeg" or "video/mp4"
   * @maxLength 500
   */
  mimeType?: string | null;
  /**
   * Wix Media Platform (WixMP) url where the image or video is stored.
   * @maxLength 5000
   */
  url?: string;
  /**
   * Optional. An opaque signature for the thought so it can be reused in subsequent requests. A base64-encoded string.
   * @maxLength 10000000
   */
  thoughtSignature?: string | null;
}

export interface ThinkingTextContent {
  /**
   * The thought text of the model thinking
   * @maxLength 1000000
   */
  thoughtText?: string | null;
  /**
   * Optional. An opaque signature for the thought so it can be reused in subsequent requests. A base64-encoded string.
   * @maxLength 10000000
   */
  thoughtSignature?: string | null;
}

export interface ToolUseContent {
  /**
   * Tool use id
   * @maxLength 100
   */
  id?: string | null;
  /**
   * Tool use name
   * @maxLength 1000
   */
  name?: string;
  /** Tool use input */
  input?: Record<string, any> | null;
  /**
   * Optional. An opaque signature for the thought so it can be reused in subsequent requests. A base64-encoded string.
   * @maxLength 10000000
   */
  thoughtSignature?: string | null;
}

export interface V1TokenUsage {
  /** Number of input tokens used in the request. */
  inputTokens?: number | null;
  /** Number of output tokens generated by the model. */
  outputTokens?: number | null;
  /** Total number of tokens used in the request. */
  totalTokens?: number | null;
  /** cache creation token usage */
  cacheCreationTokens?: number | null;
  /** cache read token usage */
  cacheReadTokens?: number | null;
  /** thought tokens usage */
  thoughtsTokens?: number | null;
  /** tool use tokens usage */
  toolUseTokens?: number | null;
}

export interface ResponseMetadata {
  /**
   * Finish reason of the model response.
   * @maxLength 1000
   */
  finishReason?: string | null;
}

export interface OpenaiproxyV1CreateChatCompletionResponse {
  /**
   * A unique identifier for the chat completion.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * Description of the response object. Will be equal to "chat.completion" for chat completion.
   * @maxLength 100
   */
  object?: string | null;
  /** Timestamp for when the response was created. */
  created?: number | null;
  /** Model that produced the completion. */
  model?: OpenaiproxyV1ModelWithLiterals;
  /** A list of chat completion choices. Can be more than one if n is greater than 1. */
  choices?: OpenaiproxyV1CreateChatCompletionResponseChoice[];
  /** TokenUsage object describing the tokens usage per request. */
  usage?: OpenaiproxyV1CreateChatCompletionResponseTokenUsage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /**
   * This fingerprint represents the backend configuration that the model runs with.
   * Can be used in conjunction with the seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
}

export interface OpenaiproxyV1ChatCompletionMessage {
  /** The role of the message author. */
  role?: OpenaiproxyV1ChatCompletionMessageMessageRoleWithLiterals;
  /**
   * The contents of the message. content is required for all messages, and may be null for assistant messages with function calls.
   * @maxLength 1000000000
   */
  content?: string | null;
  /**
   * The name of the author of this message. name is required if role is function, and it should be the name of
   * the function whose response is in the content. May contain a-z, A-Z, 0-9, and underscores, with a maximum length of 64 characters.
   * @minLength 1
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The name and arguments of a function that should be called, as generated by the model.
   * @deprecated
   * @replacedBy tool_calls
   */
  functionCall?: V1ChatCompletionMessageFunctionWithArgs;
  /**
   * The tool calls generated by the model, such as function calls.
   * @maxSize 1000
   */
  toolCalls?: V1ChatCompletionMessageToolCall[];
  /**
   * Tool call that this message is responding to.
   * @maxLength 100
   */
  toolCallId?: string | null;
  /**
   * An array of content parts with a defined type,each can be of type text or image_url when passing in images.
   * If defined, content field will be ignored.
   * You can pass multiple images by adding multiple image_url content parts.
   * Image input is only supported when using the gpt-4-visual-preview model.
   * @maxSize 5
   */
  contentParts?: OpenaiproxyV1ChatCompletionMessageContentPart[];
}

export interface V1ChatCompletionMessageFunctionWithArgs {
  /**
   * The name of the function to call.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The arguments to call the function with, as generated by the model in JSON format.
   * Note that the model does not always generate valid JSON, and may hallucinate parameters not defined by
   * your function schema. Validate the arguments in your code before calling your function.
   * @maxLength 1000000
   */
  arguments?: string | null;
}

export interface OpenaiproxyV1ChatCompletionMessageImageUrlContent {
  /**
   * The URL of the image, must be a valid wix-mp URL.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * By controlling the detail parameter, which has three options, low, high, or auto,
   * you have control over how the model processes the image and generates its textual understanding.
   * more info and cost calculation : https://platform.openai.com/docs/guides/vision/low-or-high-fidelity-image-understanding
   * @maxLength 100
   */
  detail?: string | null;
}

export enum OpenaiproxyV1ChatCompletionMessageMessageRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
  FUNCTION = 'FUNCTION',
  TOOL = 'TOOL',
  /**
   * Developer-provided instructions that the model should follow, regardless of messages sent by the user.
   * With o1 models and newer, developer messages replace the previous system messages.
   */
  DEVELOPER = 'DEVELOPER',
}

/** @enumType */
export type OpenaiproxyV1ChatCompletionMessageMessageRoleWithLiterals =
  | OpenaiproxyV1ChatCompletionMessageMessageRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT'
  | 'SYSTEM'
  | 'FUNCTION'
  | 'TOOL'
  | 'DEVELOPER';

export interface V1ChatCompletionMessageToolCall {
  /**
   * The ID of the tool call.
   * @maxLength 100
   */
  id?: string;
  /**
   * The type of the tool. Currently, only function is supported.
   * @maxLength 100
   */
  type?: string;
  /** The function that the model called. */
  function?: V1ChatCompletionMessageFunctionWithArgs;
}

export interface OpenaiproxyV1ChatCompletionMessageContentPart
  extends OpenaiproxyV1ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: OpenaiproxyV1ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
  /**
   * The type of the content part. Can be text or image_url.
   * @maxLength 100
   */
  type?: string | null;
}

/** @oneof */
export interface OpenaiproxyV1ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: OpenaiproxyV1ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
}

export interface CreateChatCompletionResponsePromptTokenDetails {
  /** Audio input tokens present in the prompt. */
  audioTokens?: number | null;
  /** Cached tokens present in the prompt. */
  cachedTokens?: number | null;
}

export interface CreateChatCompletionResponseCompletionTokenDetails {
  /** Reasoning tokens present in the completion. */
  reasoningTokens?: number | null;
  /** Audio tokens present in the completion. */
  audioTokens?: number | null;
  /** Accepted prediction tokens. */
  acceptedPredictionTokens?: number | null;
  /** Rejected prediction tokens. */
  rejectedPredictionTokens?: number | null;
}

export enum OpenaiproxyV1Model {
  UNKNOWN = 'UNKNOWN',
  GPT_3_5_TURBO = 'GPT_3_5_TURBO',
  GPT_3_5_TURBO_0301 = 'GPT_3_5_TURBO_0301',
  GPT_4 = 'GPT_4',
  GPT_4_0314 = 'GPT_4_0314',
  GPT_4_32K = 'GPT_4_32K',
  GPT_4_32K_0314 = 'GPT_4_32K_0314',
  GPT_3_5_TURBO_0613 = 'GPT_3_5_TURBO_0613',
  GPT_3_5_TURBO_16K = 'GPT_3_5_TURBO_16K',
  GPT_3_5_TURBO_16K_0613 = 'GPT_3_5_TURBO_16K_0613',
  GPT_4_0613 = 'GPT_4_0613',
  GPT_4_32K_0613 = 'GPT_4_32K_0613',
  GPT_3_5_TURBO_1106 = 'GPT_3_5_TURBO_1106',
  GPT_4_1106_PREVIEW = 'GPT_4_1106_PREVIEW',
  GPT_4_VISION_PREVIEW = 'GPT_4_VISION_PREVIEW',
  GPT_4_TURBO_PREVIEW = 'GPT_4_TURBO_PREVIEW',
  GPT_4_0125_PREVIEW = 'GPT_4_0125_PREVIEW',
  GPT_3_5_TURBO_0125 = 'GPT_3_5_TURBO_0125',
  GPT_4_TURBO_2024_04_09 = 'GPT_4_TURBO_2024_04_09',
  GPT_4O_2024_05_13 = 'GPT_4O_2024_05_13',
  GPT_4O_MINI_2024_07_18 = 'GPT_4O_MINI_2024_07_18',
  GPT_4O_2024_08_06 = 'GPT_4O_2024_08_06',
  O1_PREVIEW = 'O1_PREVIEW',
  O1_PREVIEW_2024_09_12 = 'O1_PREVIEW_2024_09_12',
  O1_MINI = 'O1_MINI',
  O1_MINI_2024_09_12 = 'O1_MINI_2024_09_12',
  GPT_4O_2024_11_20 = 'GPT_4O_2024_11_20',
  O1_2024_12_17 = 'O1_2024_12_17',
  O3_MINI_2025_01_31 = 'O3_MINI_2025_01_31',
  GPT_4_OLD = 'GPT_4_OLD',
  GPT_4_1_2025_04_14 = 'GPT_4_1_2025_04_14',
  GPT_4_1_MINI_2025_04_14 = 'GPT_4_1_MINI_2025_04_14',
  GPT_4_1_NANO_2025_04_14 = 'GPT_4_1_NANO_2025_04_14',
  O3_2025_04_16 = 'O3_2025_04_16',
  O4_MINI_2025_04_16 = 'O4_MINI_2025_04_16',
  GPT_EXP = 'GPT_EXP',
  GPT_EXP_2 = 'GPT_EXP_2',
  GPT_5_2025_08_07 = 'GPT_5_2025_08_07',
  GPT_5_MINI_2025_08_07 = 'GPT_5_MINI_2025_08_07',
  GPT_5_NANO_2025_08_07 = 'GPT_5_NANO_2025_08_07',
  GPT_5_2_2025_12_11_COMPLETION = 'GPT_5_2_2025_12_11_COMPLETION',
  GPT_5_1_2025_11_13_COMPLETION = 'GPT_5_1_2025_11_13_COMPLETION',
  GPT_5_4_2026_03_05_COMPLETION = 'GPT_5_4_2026_03_05_COMPLETION',
  GPT_5_4_MINI_2026_03_17_COMPLETION = 'GPT_5_4_MINI_2026_03_17_COMPLETION',
  GPT_5_4_NANO_2026_03_17_COMPLETION = 'GPT_5_4_NANO_2026_03_17_COMPLETION',
}

/** @enumType */
export type OpenaiproxyV1ModelWithLiterals =
  | OpenaiproxyV1Model
  | 'UNKNOWN'
  | 'GPT_3_5_TURBO'
  | 'GPT_3_5_TURBO_0301'
  | 'GPT_4'
  | 'GPT_4_0314'
  | 'GPT_4_32K'
  | 'GPT_4_32K_0314'
  | 'GPT_3_5_TURBO_0613'
  | 'GPT_3_5_TURBO_16K'
  | 'GPT_3_5_TURBO_16K_0613'
  | 'GPT_4_0613'
  | 'GPT_4_32K_0613'
  | 'GPT_3_5_TURBO_1106'
  | 'GPT_4_1106_PREVIEW'
  | 'GPT_4_VISION_PREVIEW'
  | 'GPT_4_TURBO_PREVIEW'
  | 'GPT_4_0125_PREVIEW'
  | 'GPT_3_5_TURBO_0125'
  | 'GPT_4_TURBO_2024_04_09'
  | 'GPT_4O_2024_05_13'
  | 'GPT_4O_MINI_2024_07_18'
  | 'GPT_4O_2024_08_06'
  | 'O1_PREVIEW'
  | 'O1_PREVIEW_2024_09_12'
  | 'O1_MINI'
  | 'O1_MINI_2024_09_12'
  | 'GPT_4O_2024_11_20'
  | 'O1_2024_12_17'
  | 'O3_MINI_2025_01_31'
  | 'GPT_4_OLD'
  | 'GPT_4_1_2025_04_14'
  | 'GPT_4_1_MINI_2025_04_14'
  | 'GPT_4_1_NANO_2025_04_14'
  | 'O3_2025_04_16'
  | 'O4_MINI_2025_04_16'
  | 'GPT_EXP'
  | 'GPT_EXP_2'
  | 'GPT_5_2025_08_07'
  | 'GPT_5_MINI_2025_08_07'
  | 'GPT_5_NANO_2025_08_07'
  | 'GPT_5_2_2025_12_11_COMPLETION'
  | 'GPT_5_1_2025_11_13_COMPLETION'
  | 'GPT_5_4_2026_03_05_COMPLETION'
  | 'GPT_5_4_MINI_2026_03_17_COMPLETION'
  | 'GPT_5_4_NANO_2026_03_17_COMPLETION';

export interface OpenaiproxyV1CreateChatCompletionResponseChoice {
  /** Index of this Choice in choices array. */
  index?: number | null;
  /** ChatCompletionMessage object that defines the message. */
  message?: OpenaiproxyV1ChatCompletionMessage;
  /**
   * Reason why the message generation was stopped.
   * @maxLength 100
   */
  finishReason?: string | null;
}

export interface OpenaiproxyV1CreateChatCompletionResponseTokenUsage {
  /** Number of LLM tokens required to encode input. */
  inputTokens?: number | null;
  /** Number of LLM tokens required to encode output. */
  outputTokens?: number | null;
  /** Total number of LLM tokens used for the request. */
  totalTokens?: number | null;
  /** Breakdown of tokens used in the prompt. */
  promptTokenDetails?: CreateChatCompletionResponsePromptTokenDetails;
  /** Breakdown of tokens used in the completion. */
  completionTokenDetails?: CreateChatCompletionResponseCompletionTokenDetails;
}

export interface CreateChatCompletionResponse {
  /**
   * A unique identifier for the chat completion.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * Description of the response object. Will be equal to "chat.completion" for chat completion.
   * @maxLength 100
   */
  object?: string | null;
  /** Timestamp for when the response was created. */
  created?: number | null;
  /** Model that produced the completion. */
  model?: V1ModelWithLiterals;
  /** A list of chat completion choices. Can be more than one if n is greater than 1. */
  choices?: CreateChatCompletionResponseChoice[];
  /** TokenUsage object describing the tokens usage per request. */
  usage?: CreateChatCompletionResponseTokenUsage;
  /** Cost of the request in micro cents. */
  microcentsSpent?: string | null;
  /**
   * This fingerprint represents the backend configuration that the model runs with.
   * Can be used in conjunction with the seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
}

export interface V1ChatCompletionMessage {
  /** The role of the message author. */
  role?: ChatCompletionMessageMessageRoleWithLiterals;
  /**
   * The contents of the message. content is required for all messages, and may be null for assistant messages with function calls.
   * @maxLength 1000000000
   */
  content?: string | null;
  /**
   * The name of the author of this message. name is required if role is function, and it should be the name of
   * the function whose response is in the content. May contain a-z, A-Z, 0-9, and underscores, with a maximum length of 64 characters.
   * @minLength 1
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The name and arguments of a function that should be called, as generated by the model.
   * @deprecated
   * @replacedBy tool_calls
   */
  functionCall?: FunctionWithArgs;
  /**
   * The tool calls generated by the model, such as function calls.
   * @maxSize 1000
   */
  toolCalls?: ToolCall[];
  /**
   * Tool call that this message is responding to.
   * @maxLength 100
   */
  toolCallId?: string | null;
  /**
   * An array of content parts with a defined type,each can be of type text or image_url when passing in images.
   * If defined, content field will be ignored.
   * You can pass multiple images by adding multiple image_url content parts.
   * Image input is only supported when using the gpt-4-visual-preview model.
   * @maxSize 5
   */
  contentParts?: ChatCompletionMessageContentPart[];
}

export interface FunctionWithArgs {
  /**
   * The name of the function to call.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The arguments to call the function with, as generated by the model in JSON format.
   * Note that the model does not always generate valid JSON, and may hallucinate parameters not defined by
   * your function schema. Validate the arguments in your code before calling your function.
   * @maxLength 1000000
   */
  arguments?: string | null;
}

export interface ChatCompletionMessageImageUrlContent {
  /**
   * The URL of the image, must be a valid wix-mp URL.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * By controlling the detail parameter, which has three options, low, high, or auto,
   * you have control over how the model processes the image and generates its textual understanding.
   * more info and cost calculation : https://platform.openai.com/docs/guides/vision/low-or-high-fidelity-image-understanding
   * @maxLength 100
   */
  detail?: string | null;
}

export enum ChatCompletionMessageMessageRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
  FUNCTION = 'FUNCTION',
  TOOL = 'TOOL',
  /**
   * Developer-provided instructions that the model should follow, regardless of messages sent by the user.
   * With o1 models and newer, developer messages replace the previous system messages.
   */
  DEVELOPER = 'DEVELOPER',
}

/** @enumType */
export type ChatCompletionMessageMessageRoleWithLiterals =
  | ChatCompletionMessageMessageRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT'
  | 'SYSTEM'
  | 'FUNCTION'
  | 'TOOL'
  | 'DEVELOPER';

export interface ToolCall {
  /**
   * The ID of the tool call.
   * @maxLength 100
   */
  id?: string;
  /**
   * The type of the tool. Currently, only function is supported.
   * @maxLength 100
   */
  type?: string;
  /** The function that the model called. */
  function?: FunctionWithArgs;
}

export interface ChatCompletionMessageContentPart
  extends ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
  /**
   * The type of the content part. Can be text or image_url.
   * @maxLength 100
   */
  type?: string | null;
}

/** @oneof */
export interface ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
}

export interface PromptTokenDetails {
  /** Audio input tokens present in the prompt. */
  audioTokens?: number | null;
  /** Cached tokens present in the prompt. */
  cachedTokens?: number | null;
}

export interface CompletionTokenDetails {
  /** Reasoning tokens present in the completion. */
  reasoningTokens?: number | null;
  /** Audio tokens present in the completion. */
  audioTokens?: number | null;
  /** Accepted prediction tokens. */
  acceptedPredictionTokens?: number | null;
  /** Rejected prediction tokens. */
  rejectedPredictionTokens?: number | null;
}

export enum V1Model {
  UNKNOWN = 'UNKNOWN',
  GPT_3_5_TURBO = 'GPT_3_5_TURBO',
  GPT_3_5_TURBO_1106 = 'GPT_3_5_TURBO_1106',
  GPT_4_0613 = 'GPT_4_0613',
  GPT_3_5_TURBO_0125 = 'GPT_3_5_TURBO_0125',
  GPT_4O_2024_05_13 = 'GPT_4O_2024_05_13',
  /** New models for Migration */
  GPT_4O_MINI_2024_07_18 = 'GPT_4O_MINI_2024_07_18',
  GPT_4_1_MINI_2025_04_14 = 'GPT_4_1_MINI_2025_04_14',
  GPT_4_1_NANO_2025_04_14 = 'GPT_4_1_NANO_2025_04_14',
  GPT_4_1_2025_04_14 = 'GPT_4_1_2025_04_14',
  GPT_4O_2024_11_20 = 'GPT_4O_2024_11_20',
  O4_MINI_2025_04_16 = 'O4_MINI_2025_04_16',
  GPT_5_2_2025_12_11_COMPLETION = 'GPT_5_2_2025_12_11_COMPLETION',
}

/** @enumType */
export type V1ModelWithLiterals =
  | V1Model
  | 'UNKNOWN'
  | 'GPT_3_5_TURBO'
  | 'GPT_3_5_TURBO_1106'
  | 'GPT_4_0613'
  | 'GPT_3_5_TURBO_0125'
  | 'GPT_4O_2024_05_13'
  | 'GPT_4O_MINI_2024_07_18'
  | 'GPT_4_1_MINI_2025_04_14'
  | 'GPT_4_1_NANO_2025_04_14'
  | 'GPT_4_1_2025_04_14'
  | 'GPT_4O_2024_11_20'
  | 'O4_MINI_2025_04_16'
  | 'GPT_5_2_2025_12_11_COMPLETION';

export interface CreateChatCompletionResponseChoice {
  /** Index of this Choice in choices array. */
  index?: number | null;
  /** ChatCompletionMessage object that defines the message. */
  message?: V1ChatCompletionMessage;
  /**
   * Reason why the message generation was stopped.
   * @maxLength 100
   */
  finishReason?: string | null;
}

export interface CreateChatCompletionResponseTokenUsage {
  /** Number of LLM tokens required to encode input. */
  inputTokens?: number | null;
  /** Number of LLM tokens required to encode output. */
  outputTokens?: number | null;
  /** Total number of LLM tokens used for the request. */
  totalTokens?: number | null;
  /** Breakdown of tokens used in the prompt. */
  promptTokenDetails?: PromptTokenDetails;
  /** Breakdown of tokens used in the completion. */
  completionTokenDetails?: CompletionTokenDetails;
}

export interface GenerateContentResponse {
  /**
   * The generated response.
   * @maxSize 1000
   */
  candidates?: Candidate[];
  /** The usage metadata. */
  usageMetadata?: UsageMetadata;
  /** Cost of the request in micro cents */
  microcentsSpent?: string | null;
}

export interface Candidate {
  /** The generated response content. */
  content?: CandidateContent;
  /** The reason why the model stopped generating tokens. If empty, the model has not stopped generating the tokens. */
  finishReason?: FinishReasonWithLiterals;
  /**
   * The safety ratings of the response.
   * @maxSize 100
   */
  safetyRatings?: SafetyRating[];
  /** The citation metadata of the response. */
  citationMetadata?: CandidateCitationMetadata;
  /** Output only. Metadata specifies sources used to ground generated content. */
  groundingMetadata?: GroundingMetadata;
  /**
   * Optional. Output only. Details the reason why the model stopped generating tokens.
   * This is populated only when finishReason is set.
   * @maxLength 100000
   */
  finishMessage?: string | null;
  /**
   * The raw finish reason string as returned by Google's API.
   * Preserves the original value even when it cannot be mapped to the FinishReason enum,
   * ensuring forward compatibility when Google adds new finish reason values.
   * @maxLength 1000
   */
  finishReasonStr?: string | null;
}

export interface CandidateContent {
  /**
   * The generated response content.
   * @maxSize 1000
   */
  parts?: CandidateContentPart[];
}

export interface FunctionCall {
  /**
   * Required. The name of the function to call. Must be a-z, A-Z, 0-9, or contain underscores and dashes, with a maximum length of 63.
   * @maxLength 64
   */
  name?: string | null;
  /** Optional. The function parameters and values in JSON object format. */
  args?: Record<string, any> | null;
}

export interface ExecutableCode {
  /** Required. Programming language of the code. */
  language?: LanguageWithLiterals;
  /**
   * Required. The code to be executed.
   * @maxLength 100000
   */
  code?: string;
}

export enum Language {
  /** Unspecified language. This value should not be used. */
  LANGUAGE_UNSPECIFIED = 'LANGUAGE_UNSPECIFIED',
  /** Python >= 3.10, with numpy and simpy available. */
  PYTHON = 'PYTHON',
}

/** @enumType */
export type LanguageWithLiterals = Language | 'LANGUAGE_UNSPECIFIED' | 'PYTHON';

export interface V1CodeExecutionResult {
  /** Required. Outcome of the code execution. */
  outcome?: OutcomeWithLiterals;
  /**
   * Optional. Contains stdout when code execution is successful, stderr or other description otherwise.
   * @maxLength 100000
   */
  output?: string | null;
}

export enum Outcome {
  /** Unspecified status. This value should not be used. */
  OUTCOME_UNSPECIFIED = 'OUTCOME_UNSPECIFIED',
  /** Code execution completed successfully. */
  OUTCOME_OK = 'OUTCOME_OK',
  /** Code execution finished but with a failure. stderr should contain the reason. */
  OUTCOME_FAILED = 'OUTCOME_FAILED',
  /** Code execution ran for too long, and was cancelled. There may or may not be a partial output present. */
  OUTCOME_DEADLINE_EXCEEDED = 'OUTCOME_DEADLINE_EXCEEDED',
}

/** @enumType */
export type OutcomeWithLiterals =
  | Outcome
  | 'OUTCOME_UNSPECIFIED'
  | 'OUTCOME_OK'
  | 'OUTCOME_FAILED'
  | 'OUTCOME_DEADLINE_EXCEEDED';

/**
 * Raw media bytes.
 * Text should not be sent as raw bytes, use the 'text' field.
 */
export interface Blob {
  /**
   * The IANA standard MIME type of the source data.
   * Examples: - image/png - image/jpeg
   * If an unsupported MIME type is provided, an error will be returned.
   * For a complete list of supported types, see https://ai.google.dev/gemini-api/docs/file-prompting-strategies#supported_file_formats.
   * @maxLength 100
   */
  mimeType?: string;
  /**
   * Represents raw bytes for media formats. Will be fetched from the passed URL in request, and uploaded to WixMP URL in response.
   * @format WEB_URL
   */
  data?: string;
}

export interface CandidateContentPart {
  /**
   * The text generated by the model.
   * @maxLength 100000
   */
  text?: string | null;
  /** function call */
  functionCall?: FunctionCall;
  /**
   * Code generated by the model that is meant to be executed, and the result returned to the model.
   * Only generated when using the CodeExecution tool, in which the code will be automatically executed, and a corresponding CodeExecutionResult will also be generated.
   */
  executableCode?: ExecutableCode;
  /**
   * Result of executing the ExecutableCode.
   * Only generated when using the CodeExecution, and always follows a part containing the ExecutableCode.
   */
  codeExecutionResult?: V1CodeExecutionResult;
  /** Inline media bytes. */
  inlineData?: Blob;
  /**
   * Thought flag indicates that the content part is a thought.
   * @readonly
   */
  thought?: boolean | null;
  /**
   * Optional. An opaque signature for the thought so it can be reused in subsequent requests. A base64-encoded string.
   * @maxLength 10000000
   */
  thoughtSignature?: string | null;
}

export enum FinishReason {
  UNKNOWN_FINISH_REASON = 'UNKNOWN_FINISH_REASON',
  /** The finish reason is unspecified. */
  UNSPECIFIED = 'UNSPECIFIED',
  /** Natural stop point of the model or provided stop sequence. */
  STOP = 'STOP',
  /** The maximum number of tokens as specified in the request was reached. */
  MAX_TOKENS = 'MAX_TOKENS',
  /**
   * The token generation was stopped as the response was flagged for safety reasons.
   * Note that Candidate.content is empty if content filters block the output.
   */
  SAFETY = 'SAFETY',
  /** The token generation was stopped as the response was flagged for unauthorized citations. */
  RECITATION = 'RECITATION',
  /** All other reasons that stopped the token */
  OTHER = 'OTHER',
  /** The response candidate content was flagged for using an unsupported language. */
  LANGUAGE = 'LANGUAGE',
  /** Token generation stopped because the content contains forbidden terms. */
  BLOCKLIST = 'BLOCKLIST',
  /** Token generation stopped for potentially containing prohibited content. */
  PROHIBITED_CONTENT = 'PROHIBITED_CONTENT',
  /** Token generation stopped because the content potentially contains Sensitive Personally Identifiable Information (SPII). */
  SPII = 'SPII',
  /** The function call generated by the model is invalid. */
  MALFORMED_FUNCTION_CALL = 'MALFORMED_FUNCTION_CALL',
  /** Token generation stopped because generated images contain safety violations. */
  IMAGE_SAFETY = 'IMAGE_SAFETY',
  /** Model generated a tool call but no tools were enabled in the request. */
  UNEXPECTED_TOOL_CALL = 'UNEXPECTED_TOOL_CALL',
  /** Model called too many tools consecutively, thus the system exited execution. */
  TOO_MANY_TOOL_CALLS = 'TOO_MANY_TOOL_CALLS',
  /** Image generation stopped because generated images has other prohibited content. */
  IMAGE_PROHIBITED_CONTENT = 'IMAGE_PROHIBITED_CONTENT',
  /** Image generation stopped because of other miscellaneous issue. */
  IMAGE_OTHER = 'IMAGE_OTHER',
  /** The model was expected to generate an image, but none was generated. */
  NO_IMAGE = 'NO_IMAGE',
  /** Image generation stopped due to recitation. */
  IMAGE_RECITATION = 'IMAGE_RECITATION',
  /** Request has at least one thought signature missing. */
  MISSING_THOUGHT_SIGNATURE = 'MISSING_THOUGHT_SIGNATURE',
}

/** @enumType */
export type FinishReasonWithLiterals =
  | FinishReason
  | 'UNKNOWN_FINISH_REASON'
  | 'UNSPECIFIED'
  | 'STOP'
  | 'MAX_TOKENS'
  | 'SAFETY'
  | 'RECITATION'
  | 'OTHER'
  | 'LANGUAGE'
  | 'BLOCKLIST'
  | 'PROHIBITED_CONTENT'
  | 'SPII'
  | 'MALFORMED_FUNCTION_CALL'
  | 'IMAGE_SAFETY'
  | 'UNEXPECTED_TOOL_CALL'
  | 'TOO_MANY_TOOL_CALLS'
  | 'IMAGE_PROHIBITED_CONTENT'
  | 'IMAGE_OTHER'
  | 'NO_IMAGE'
  | 'IMAGE_RECITATION'
  | 'MISSING_THOUGHT_SIGNATURE';

export interface SafetyRating {
  /** The safety category that the response belongs to. */
  category?: HarmCategoryWithLiterals;
  /** The probability that the response belongs to the specified safety category. */
  probability?: HarmProbabilityWithLiterals;
  /** The probability score that the response belongs to the specified safety category. */
  probabilityScore?: number | null;
  /**
   * The severity of the response's safety rating.
   * @maxLength 100
   */
  severity?: string | null;
  /** the severity score of the response's safety rating. */
  severityScore?: number | null;
  /**
   * A boolean flag associated with a safety attribute that indicates if the model's input or output was blocked.
   * If blocked is true, then the errors field in the response contains one or more error codes.
   * If blocked is false, then the response doesn't include the errors field.
   */
  blocked?: boolean | null;
}

export enum HarmCategory {
  UNKNOWN_CATEGORY = 'UNKNOWN_CATEGORY',
  HARM_CATEGORY_SEXUALLY_EXPLICIT = 'HARM_CATEGORY_SEXUALLY_EXPLICIT',
  HARM_CATEGORY_HATE_SPEECH = 'HARM_CATEGORY_HATE_SPEECH',
  HARM_CATEGORY_HARASSMENT = 'HARM_CATEGORY_HARASSMENT',
  HARM_CATEGORY_DANGEROUS_CONTENT = 'HARM_CATEGORY_DANGEROUS_CONTENT',
}

/** @enumType */
export type HarmCategoryWithLiterals =
  | HarmCategory
  | 'UNKNOWN_CATEGORY'
  | 'HARM_CATEGORY_SEXUALLY_EXPLICIT'
  | 'HARM_CATEGORY_HATE_SPEECH'
  | 'HARM_CATEGORY_HARASSMENT'
  | 'HARM_CATEGORY_DANGEROUS_CONTENT';

export enum HarmProbability {
  UNKNOWN_PROBABILITY = 'UNKNOWN_PROBABILITY',
  NEGLIGIBLE = 'NEGLIGIBLE',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

/** @enumType */
export type HarmProbabilityWithLiterals =
  | HarmProbability
  | 'UNKNOWN_PROBABILITY'
  | 'NEGLIGIBLE'
  | 'LOW'
  | 'MEDIUM'
  | 'HIGH';

export interface CandidateCitationMetadata {
  /**
   * The citations of the response.
   * @maxSize 1000
   */
  citations?: CandidateCitationMetadataCitation[];
}

export interface PublicationDate {
  /** The year of the publication date. */
  year?: number | null;
  /** The month of the publication date. */
  month?: number | null;
  /** The day of the publication date. */
  day?: number | null;
}

export interface CandidateCitationMetadataCitation {
  /** An integer that specifies where a citation starts in the content. */
  startIndex?: number | null;
  /** An integer that specifies where a citation ends in the content. */
  endIndex?: number | null;
  /**
   * The URI of a citation source. Examples of a URI source might be a news website or a GitHub repository.
   * @format WEB_URL
   */
  uri?: string | null;
  /**
   * The title of a citation source. Examples of source titles might be that of a news article or a book.
   * @maxLength 500
   */
  title?: string | null;
  /**
   * The license associated with a citation.
   * @maxLength 500
   */
  license?: string | null;
  /** The date a citation was published. Its valid formats are YYYY, YYYY-MM, and YYYY-MM-DD. */
  publicationDate?: PublicationDate;
}

/** Metadata returned to client when grounding is enabled. */
export interface GroundingMetadata {
  /**
   * Optional. Web search queries for the following-up web search.
   * @maxSize 1000
   * @maxLength 1000
   */
  webSearchQueries?: string[];
  /** Optional. Google search entry for the following-up web searches. */
  searchEntryPoint?: SearchEntryPoint;
  /**
   * List of supporting references retrieved from specified grounding source.
   * @maxSize 1000
   */
  groundingChunks?: GroundingChunk[];
  /**
   * Optional. List of grounding support.
   * @maxSize 1000
   */
  groundingSupports?: GroundingSupport[];
  /** Optional. Output only. Retrieval metadata. */
  retrievalMetadata?: RetrievalMetadata;
}

/** Google search entry point. */
export interface SearchEntryPoint {
  /**
   * Optional. Web content snippet that can be embedded in a web page or an app webview.
   * @maxLength 10000000
   */
  renderedContent?: string | null;
  /** Optional. Base64 encoded JSON representing array of <search term, search url> tuple. */
  sdkBlob?: Uint8Array | null;
}

/** Grounding chunk. */
export interface GroundingChunk extends GroundingChunkChunkTypeOneOf {
  /** Grounding chunk from the web. */
  web?: Web;
  /** Grounding chunk from context retrieved by the retrieval tools. */
  retrievedContext?: RetrievedContext;
}

/** @oneof */
export interface GroundingChunkChunkTypeOneOf {
  /** Grounding chunk from the web. */
  web?: Web;
  /** Grounding chunk from context retrieved by the retrieval tools. */
  retrievedContext?: RetrievedContext;
}

/** Chunk from the web. */
export interface Web {
  /**
   * URI reference of the chunk.
   * @format WEB_URL
   */
  uri?: string | null;
  /**
   * Title of the chunk.
   * @maxLength 1000
   */
  title?: string | null;
}

/** Chunk from context retrieved by the retrieval tools. */
export interface RetrievedContext {
  /**
   * URI reference of the attribution.
   * @format WEB_URL
   */
  uri?: string | null;
  /**
   * Title of the attribution.
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * Text of the attribution.
   * @maxLength 100000
   */
  text?: string | null;
}

/** Grounding support. */
export interface GroundingSupport {
  /** Segment of the content this support belongs to. */
  segment?: Segment;
  /**
   * A list of indices (into 'grounding_chunk') specifying the
   * citations associated with the claim. For instance [1,3,4] means
   * that grounding_chunk[1], grounding_chunk[3],
   * grounding_chunk[4] are the retrieved content attributed to the claim.
   * @maxSize 1000
   */
  groundingChunkIndices?: number[];
  /**
   * Confidence score of the support references. Ranges from 0 to 1. 1 is the
   * most confident. This list must have the same size as the
   * grounding_chunk_indices.
   * @maxSize 1000
   */
  confidenceScores?: number[];
}

/** Segment of the content. */
export interface Segment {
  /** Output only. The index of a Part object within its parent Content object. */
  partIndex?: number | null;
  /**
   * Output only. Start index in the given Part, measured in bytes. Offset from
   * the start of the Part, inclusive, starting at zero.
   */
  startIndex?: number;
  /**
   * Output only. End index in the given Part, measured in bytes. Offset from
   * the start of the Part, exclusive, starting at zero.
   */
  endIndex?: number;
  /**
   * Output only. The text corresponding to the segment from the response.
   * @maxLength 100000
   */
  text?: string;
}

/** Metadata related to retrieval in the grounding flow. */
export interface RetrievalMetadata {
  /**
   * Optional. Score indicating how likely information from Google Search could
   * help answer the prompt. The score is in the range `[0, 1]`, where 0 is the
   * least likely and 1 is the most likely. This score is only populated when
   * Google Search grounding and dynamic retrieval is enabled. It will be
   * compared to the threshold to determine whether to trigger Google Search.
   */
  googleSearchDynamicRetrievalScore?: number | null;
}

export interface UsageMetadata {
  /** Number of tokens in the request. */
  promptTokenCount?: number | null;
  /** Number of tokens in the response. */
  candidatesTokenCount?: number | null;
  /** Number of tokens in the request and response(s). */
  totalTokenCount?: number | null;
  /** Optional. Number of tokens of thoughts for thinking models. */
  thoughtsTokenCount?: number | null;
  /** Optional. Number of cached input tokens. */
  cachedContentTokenCount?: number | null;
  /**
   * Output only. List of modalities that were processed in the request input.
   * @maxSize 10
   */
  promptTokensDetails?: ModalityTokenCount[];
  /**
   * Output only. List of modalities that were returned in the response.
   * @maxSize 10
   */
  candidatesTokensDetails?: ModalityTokenCount[];
  /**
   * Output only. List of modalities that were cached from the input
   * @maxSize 10
   */
  cacheTokensDetails?: ModalityTokenCount[];
}

export interface ModalityTokenCount {
  /** The modality associated with this token count. */
  modality?: ModalityWithLiterals;
  /** Number of tokens. */
  tokenCount?: string | null;
}

export enum Modality {
  UNKNOWN_MODALITY = 'UNKNOWN_MODALITY',
  /** Indicates the model should return text. */
  TEXT = 'TEXT',
  /** Indicates the model should return images. */
  IMAGE = 'IMAGE',
  /** Indicates the model should return audio. */
  AUDIO = 'AUDIO',
}

/** @enumType */
export type ModalityWithLiterals =
  | Modality
  | 'UNKNOWN_MODALITY'
  | 'TEXT'
  | 'IMAGE'
  | 'AUDIO';

export interface InvokeAnthropicClaudeModelResponse {
  /**
   * The unique identifier for the response. The format and length of the ID might change over time.
   * @maxLength 512
   */
  responseId?: string;
  /**
   * The ID for the Anthropic Claude model that made the request.
   * @maxLength 512
   */
  model?: string;
  /**
   * The reason why Anthropic Claude stopped generating the response:
   * `end_turn` – The model reached a natural stopping point.
   * `max_tokens` – The generated text exceeded the value of the max_tokens input field or exceeded the maximum number of tokens that the model supports.
   * `stop_sequence` – The model generated one of the stop sequences that you specified in the stop_sequences input field.
   * @maxLength 512
   */
  stopReason?: string | null;
  /**
   * Which custom stop sequence was generated, if any.
   * @maxLength 512
   */
  stopSequence?: string | null;
  /** The type of response. */
  responseType?: ResponseTypeTypeWithLiterals;
  /** The conversational role of the generated message. The value is always `ASSISTANT`. */
  role?: RoleWithLiterals;
  /**
   * The content generated by the model.
   * DEPRECATED - this field only returns text content that was generated. For full output including text and tool_use blocks use `content_blocks` field.
   * @maxLength 1000000000
   * @maxSize 4096
   * @deprecated The content generated by the model.
   * DEPRECATED - this field only returns text content that was generated. For full output including text and tool_use blocks use `content_blocks` field.
   * @replacedBy content_blocks
   * @targetRemovalDate 2024-11-01
   */
  content?: string[];
  /** Container for the number of tokens that you supplied in the request and the number tokens of that the model generated in the response. */
  usage?: Usage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /**
   * Content generated by the model.
   * This is an array of content blocks, each of which has a type that determines its shape.
   */
  contentBlocks?: ContentBlock[];
}

export enum ResponseTypeType {
  UNKNOWN = 'UNKNOWN',
  MESSAGE = 'MESSAGE',
}

/** @enumType */
export type ResponseTypeTypeWithLiterals =
  | ResponseTypeType
  | 'UNKNOWN'
  | 'MESSAGE';

export enum Role {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
}

/** @enumType */
export type RoleWithLiterals = Role | 'UNKNOWN' | 'USER' | 'ASSISTANT';

export interface Usage {
  /** The number of input tokens in the request. */
  inputTokens?: number;
  /** The number tokens of that the model generated in the response. */
  outputTokens?: number;
  /** Number of tokens written to the cache when creating a new entry */
  cacheCreationInputTokens?: number | null;
  /** Number of tokens retrieved from the cache for this request */
  cacheReadInputTokens?: number | null;
}

export interface ContentBlock extends ContentBlockTypeOneOf {
  /**
   * @maxLength 1000000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: Text;
  /** Image image = 2; // Image content. */
  imageUrl?: V1ImageUrl;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: ToolUse;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: ToolResult;
  /** Represents Claude’s internal thought process. */
  thinking?: Thinking;
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   */
  redactedThinking?: RedactedThinking;
}

/** @oneof */
export interface ContentBlockTypeOneOf {
  /**
   * @maxLength 1000000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: Text;
  /** Image image = 2; // Image content. */
  imageUrl?: V1ImageUrl;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: ToolUse;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: ToolResult;
  /** Represents Claude’s internal thought process. */
  thinking?: Thinking;
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   */
  redactedThinking?: RedactedThinking;
}

export interface Text {
  /**
   * Text content.
   * @maxLength 1000000000
   */
  text?: string;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: CacheControl;
}

export interface CacheControl {
  /** Currently, “ephemeral” is the only supported cache type */
  type?: TypeWithLiterals;
}

export enum Type {
  UNKNOWN = 'UNKNOWN',
  EPHEMERAL = 'EPHEMERAL',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNKNOWN' | 'EPHEMERAL';

export interface V1ImageUrl {
  /**
   * The URL must be a valid wix mp or wix static URL.
   * @maxLength 100000
   */
  url?: string | null;
  /** Media type of the image. */
  mediaType?: MediaTypeWithLiterals;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: CacheControl;
}

export enum MediaType {
  UNKNOWN = 'UNKNOWN',
  /** image/jpeg */
  IMAGE_JPEG = 'IMAGE_JPEG',
  /** image/png */
  IMAGE_PNG = 'IMAGE_PNG',
  /** image/webp */
  IMAGE_WEBP = 'IMAGE_WEBP',
  /** image/gif */
  IMAGE_GIF = 'IMAGE_GIF',
}

/** @enumType */
export type MediaTypeWithLiterals =
  | MediaType
  | 'UNKNOWN'
  | 'IMAGE_JPEG'
  | 'IMAGE_PNG'
  | 'IMAGE_WEBP'
  | 'IMAGE_GIF';

export interface ToolUse {
  /**
   * Tool use id
   * @maxLength 100
   */
  id?: string | null;
  /**
   * Tool use name
   * @maxLength 1000
   */
  name?: string | null;
  /** Tool use input */
  input?: Record<string, any> | null;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: CacheControl;
}

export interface ToolResult {
  /**
   * Tool use id
   * @maxLength 100
   */
  toolUseId?: string | null;
  /** Tool result is error. */
  isError?: boolean | null;
  /**
   * Tool result content.
   * @maxSize 4096
   */
  content?: SimpleContentBlock[];
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: CacheControl;
}

export interface SimpleContentBlock extends SimpleContentBlockTypeOneOf {
  /**
   * @maxLength 1000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  imageUrl?: V1ImageUrl;
}

/** @oneof */
export interface SimpleContentBlockTypeOneOf {
  /**
   * @maxLength 1000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  imageUrl?: V1ImageUrl;
}

export interface Thinking {
  /**
   * Cryptographic token which verifies that the thinking block was generated by Claude, and is verified when thinking blocks are passed back to the API.
   * @maxLength 1000000
   */
  signature?: string;
  /**
   * Text content of a Thinking block.
   * @maxLength 1000000
   */
  thinking?: string;
}

export interface RedactedThinking {
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   * @maxLength 1000000
   */
  data?: string;
}

export interface V1InvokeAnthropicClaudeModelResponse {
  /**
   * The unique identifier for the response. The format and length of the ID might change over time.
   * @maxLength 512
   */
  responseId?: string;
  /**
   * The ID for the Anthropic Claude model that made the request.
   * @maxLength 512
   */
  model?: string;
  /**
   * The reason why Anthropic Claude stopped generating the response:
   * `end_turn` – The model reached a natural stopping point.
   * `max_tokens` – The generated text exceeded the value of the max_tokens input field or exceeded the maximum number of tokens that the model supports.
   * `stop_sequence` – The model generated one of the stop sequences that you specified in the stop_sequences input field.
   * @maxLength 512
   */
  stopReason?: string | null;
  /**
   * Which custom stop sequence was generated, if any.
   * @maxLength 512
   */
  stopSequence?: string | null;
  /** The type of response. */
  responseType?: GoogleproxyV1ResponseTypeTypeWithLiterals;
  /** The conversational role of the generated message. The value is always `ASSISTANT`. */
  role?: V1MessageRoleRoleWithLiterals;
  /**
   * The content generated by the model.
   * DEPRECATED - this field only returns text content that was generated. For full output including text and tool_use blocks use `content_blocks` field.
   * @maxLength 1000000000
   * @maxSize 4096
   * @deprecated The content generated by the model.
   * DEPRECATED - this field only returns text content that was generated. For full output including text and tool_use blocks use `content_blocks` field.
   * @replacedBy content_blocks
   * @targetRemovalDate 2024-11-01
   */
  content?: string[];
  /** Container for the number of tokens that you supplied in the request and the number tokens of that the model generated in the response. */
  usage?: GoogleproxyV1Usage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /**
   * Content generated by the model.
   * This is an array of content blocks, each of which has a type that determines its shape.
   * @maxSize 1000
   */
  contentBlocks?: GoogleproxyV1ContentBlock[];
}

export enum GoogleproxyV1ResponseTypeType {
  UNKNOWN = 'UNKNOWN',
  MESSAGE = 'MESSAGE',
}

/** @enumType */
export type GoogleproxyV1ResponseTypeTypeWithLiterals =
  | GoogleproxyV1ResponseTypeType
  | 'UNKNOWN'
  | 'MESSAGE';

export enum V1MessageRoleRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
}

/** @enumType */
export type V1MessageRoleRoleWithLiterals =
  | V1MessageRoleRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT';

export interface GoogleproxyV1Usage {
  /** The number of input tokens in the request. */
  inputTokens?: number;
  /** The number tokens of that the model generated in the response. */
  outputTokens?: number;
  /** Number of tokens written to the cache when creating a new entry */
  cacheCreationInputTokens?: number | null;
  /** Number of tokens retrieved from the cache for this request */
  cacheReadInputTokens?: number | null;
}

export interface GoogleproxyV1ContentBlock
  extends GoogleproxyV1ContentBlockTypeOneOf {
  /**
   * @maxLength 1000000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: GoogleproxyV1Text;
  /** Image image = 2; // Image content. */
  imageUrl?: GoogleproxyV1ImageUrl;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: GoogleproxyV1ToolUse;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: GoogleproxyV1ToolResult;
  /** Represents Claude’s internal thought process. */
  thinking?: GoogleproxyV1Thinking;
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   */
  redactedThinking?: GoogleproxyV1RedactedThinking;
}

/** @oneof */
export interface GoogleproxyV1ContentBlockTypeOneOf {
  /**
   * @maxLength 1000000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: GoogleproxyV1Text;
  /** Image image = 2; // Image content. */
  imageUrl?: GoogleproxyV1ImageUrl;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: GoogleproxyV1ToolUse;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: GoogleproxyV1ToolResult;
  /** Represents Claude’s internal thought process. */
  thinking?: GoogleproxyV1Thinking;
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   */
  redactedThinking?: GoogleproxyV1RedactedThinking;
}

export interface GoogleproxyV1Text {
  /**
   * Text content.
   * @maxLength 1000000000
   */
  text?: string;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: GoogleproxyV1CacheControl;
}

export interface GoogleproxyV1CacheControl {
  /** Currently, “ephemeral” is the only supported cache type */
  type?: V1CacheControlTypeWithLiterals;
}

export enum V1CacheControlType {
  UNKNOWN = 'UNKNOWN',
  EPHEMERAL = 'EPHEMERAL',
}

/** @enumType */
export type V1CacheControlTypeWithLiterals =
  | V1CacheControlType
  | 'UNKNOWN'
  | 'EPHEMERAL';

export interface GoogleproxyV1ImageUrl {
  /**
   * The URL must be a valid wix mp or wix static URL.
   * @maxLength 100000
   */
  url?: string | null;
  /** Media type of the image. */
  mediaType?: V1ImageMediaTypeMediaTypeWithLiterals;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: GoogleproxyV1CacheControl;
}

export enum V1ImageMediaTypeMediaType {
  UNKNOWN = 'UNKNOWN',
  /** image/jpeg */
  IMAGE_JPEG = 'IMAGE_JPEG',
  /** image/png */
  IMAGE_PNG = 'IMAGE_PNG',
  /** image/webp */
  IMAGE_WEBP = 'IMAGE_WEBP',
  /** image/gif */
  IMAGE_GIF = 'IMAGE_GIF',
}

/** @enumType */
export type V1ImageMediaTypeMediaTypeWithLiterals =
  | V1ImageMediaTypeMediaType
  | 'UNKNOWN'
  | 'IMAGE_JPEG'
  | 'IMAGE_PNG'
  | 'IMAGE_WEBP'
  | 'IMAGE_GIF';

export interface GoogleproxyV1ToolUse {
  /**
   * Tool use id
   * @maxLength 100
   */
  id?: string | null;
  /**
   * Tool use name
   * @maxLength 1000
   */
  name?: string | null;
  /** Tool use input */
  input?: Record<string, any> | null;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: GoogleproxyV1CacheControl;
}

export interface GoogleproxyV1ToolResult {
  /**
   * Tool use id
   * @maxLength 100
   */
  toolUseId?: string | null;
  /** Tool result is error. */
  isError?: boolean | null;
  /**
   * Tool result content.
   * @maxSize 4096
   */
  content?: V1SimpleContentBlock[];
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: GoogleproxyV1CacheControl;
}

export interface V1SimpleContentBlock extends V1SimpleContentBlockTypeOneOf {
  /**
   * @maxLength 1000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: GoogleproxyV1Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  imageUrl?: GoogleproxyV1ImageUrl;
}

/** @oneof */
export interface V1SimpleContentBlockTypeOneOf {
  /**
   * @maxLength 1000000
   * @deprecated
   * @replacedBy text_content
   * @targetRemovalDate 2025-10-01
   */
  text?: string;
  /** Text content. */
  textContent?: GoogleproxyV1Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  imageUrl?: GoogleproxyV1ImageUrl;
}

export interface GoogleproxyV1Thinking {
  /**
   * Cryptographic token which verifies that the thinking block was generated by Claude, and is verified when thinking blocks are passed back to the API.
   * @maxLength 1000000
   */
  signature?: string;
  /**
   * Text content of a Thinking block.
   * @maxLength 1000000
   */
  thinking?: string;
}

export interface GoogleproxyV1RedactedThinking {
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   * @maxLength 1000000
   */
  data?: string;
}

export interface InvokeAnthropicModelResponse {
  /**
   * The unique identifier for the response. The format and length of the ID might change over time.
   * @maxLength 512
   */
  responseId?: string;
  /**
   * The ID for the Anthropic Claude model that made the request.
   * @maxLength 512
   */
  model?: string;
  /**
   * The reason why Anthropic Claude stopped generating the response:
   * "end_turn": the model reached a natural stopping point
   * "max_tokens": we exceeded the requested max_tokens or the model's maximum
   * "stop_sequence": one of your provided custom stop_sequences was generated
   * "tool_use": the model invoked one or more tools
   * "pause_turn": we paused a long-running turn. You may provide the response back as-is in a subsequent request to let the model continue.
   * "refusal": when streaming classifiers intervene to handle potential policy violations
   * @maxLength 512
   */
  stopReason?: string | null;
  /**
   * Which custom stop sequence was generated, if any.
   * @maxLength 512
   */
  stopSequence?: string | null;
  /** The type of response. */
  type?: V1ResponseTypeTypeWithLiterals;
  /** The conversational role of the generated message. The value is always `ASSISTANT`. */
  role?: MessageRoleRoleWithLiterals;
  /** Container for the number of tokens that you supplied in the request and the number tokens of that the model generated in the response. */
  usage?: V1Usage;
  /**
   * Information about the container used in this request.
   * This will be non-null if a container tool (e.g. code execution) was used.
   */
  container?: Container;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /**
   * Content generated by the model.
   * This is an array of content blocks, each of which has a type that determines its shape.
   * @maxSize 4096
   */
  content?: V1ContentBlock[];
}

export enum V1ResponseTypeType {
  UNKNOWN = 'UNKNOWN',
  MESSAGE = 'MESSAGE',
}

/** @enumType */
export type V1ResponseTypeTypeWithLiterals =
  | V1ResponseTypeType
  | 'UNKNOWN'
  | 'MESSAGE';

export enum MessageRoleRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
}

/** @enumType */
export type MessageRoleRoleWithLiterals =
  | MessageRoleRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT';

export interface V1Usage {
  /** Breakdown of cached tokens by TTL */
  cacheCreation?: UsageCacheCreation;
  /** The number of input tokens used to create the cache entry. */
  cacheCreationInputTokens?: number | null;
  /** The number of input tokens read from the cache. */
  cacheReadInputTokens?: number | null;
  /** The number of input tokens which were used. */
  inputTokens?: number;
  /** The number of output tokens which were used. */
  outputTokens?: number;
  /** The number of server tool requests. */
  serverToolUse?: UsageServerToolUse;
  /**
   * If the request used the priority, standard, or batch tier.
   * Available options: standard, priority, batch
   * @maxLength 500
   */
  serviceTier?: string | null;
}

export interface UsageCacheCreation {
  /** The number of input tokens used to create the 1 hour cache entry. */
  ephemeral1hInputTokens?: number;
  /** The number of input tokens used to create the 5 minute cache entry. */
  ephemeral5mInputTokens?: number;
}

export interface UsageServerToolUse {
  /** The number of web search tool requests. */
  webSearchRequests?: number;
  /** The number of web fetch tool requests. */
  webFetchRequests?: number;
}

export interface Container {
  /**
   * The time at which the container will expire.
   * @maxLength 100
   */
  expiresAt?: string;
  /**
   * Identifier for the container used in this request
   * @maxLength 512
   */
  id?: string;
}

/** Content object used in both request and response */
export interface V1ContentBlock extends V1ContentBlockTypeOneOf {
  /** Text content. */
  textContent?: V1Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  image?: AnthropicV1ImageUrl;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: V1ToolUse;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: V1ToolResult;
  /** Represents Claude’s internal thought process. */
  thinking?: V1Thinking;
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   */
  redactedThinking?: V1RedactedThinking;
  /**
   * Assistant requests an MCP tool call; client should execute it on the named MCP server
   * and later reply with mcp_tool_result referencing the same id.
   */
  mcpToolUse?: McpToolUse;
  /** User returns results of an MCP tool call; tool_use_id must equal the McpToolUse.id. Content carries output (text/image) or an error. */
  mcpToolResult?: V1ToolResult;
  /** Assistant announces an Anthropic-run server tool call (e.g., "web_search", "code_execution"). */
  serverToolUse?: ServerToolUse;
  /** Server tool result for Web Search. */
  webSearchToolResult?: WebSearchToolResult;
  /** Server tool result for Code Execution. */
  codeExecutionToolResult?: CodeExecutionToolResult;
  /** User attaches a file for the Code Execution container. */
  containerUpload?: ContainerUpload;
  /** Citable document. For future citations, resend this block in later requests so it remains in context. */
  document?: DocumentContent;
  /** Server tool result for Web Fetch. */
  webFetchToolResult?: WebFetchToolResult;
}

/** @oneof */
export interface V1ContentBlockTypeOneOf {
  /** Text content. */
  textContent?: V1Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  image?: AnthropicV1ImageUrl;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: V1ToolUse;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: V1ToolResult;
  /** Represents Claude’s internal thought process. */
  thinking?: V1Thinking;
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   */
  redactedThinking?: V1RedactedThinking;
  /**
   * Assistant requests an MCP tool call; client should execute it on the named MCP server
   * and later reply with mcp_tool_result referencing the same id.
   */
  mcpToolUse?: McpToolUse;
  /** User returns results of an MCP tool call; tool_use_id must equal the McpToolUse.id. Content carries output (text/image) or an error. */
  mcpToolResult?: V1ToolResult;
  /** Assistant announces an Anthropic-run server tool call (e.g., "web_search", "code_execution"). */
  serverToolUse?: ServerToolUse;
  /** Server tool result for Web Search. */
  webSearchToolResult?: WebSearchToolResult;
  /** Server tool result for Code Execution. */
  codeExecutionToolResult?: CodeExecutionToolResult;
  /** User attaches a file for the Code Execution container. */
  containerUpload?: ContainerUpload;
  /** Citable document. For future citations, resend this block in later requests so it remains in context. */
  document?: DocumentContent;
  /** Server tool result for Web Fetch. */
  webFetchToolResult?: WebFetchToolResult;
}

export interface V1Text {
  /**
   * Text content.
   * @maxLength 1000000000
   */
  text?: string;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: V1CacheControl;
  /**
   * Structured citations for this text block.
   * Populated by the model when citations are enabled.
   * @maxSize 256
   */
  citations?: Citation[];
}

export interface V1CacheControl {
  /** Currently, “ephemeral” is the only supported cache type */
  type?: CacheControlTypeWithLiterals;
  /**
   * The time-to-live for the cache control breakpoint. This may be one the following values:
   * 5m: 5 minutes (default)
   * 1h: 1 hour
   * @maxLength 50
   */
  ttl?: string | null;
}

export enum CacheControlType {
  UNKNOWN = 'UNKNOWN',
  EPHEMERAL = 'EPHEMERAL',
}

/** @enumType */
export type CacheControlTypeWithLiterals =
  | CacheControlType
  | 'UNKNOWN'
  | 'EPHEMERAL';

/** Unified wrapper for all citation kinds (attach to Text.citations). */
export interface Citation extends CitationTypeOneOf {
  /** Char location */
  charLocation?: CharLocationCitation;
  /** Page location */
  pageLocation?: PageLocationCitation;
  /** Content block location */
  contentBlockLocation?: ContentBlockLocationCitation;
  /** Web search result location */
  webSearchResultLocation?: WebSearchResultLocationCitation;
  /** Search result location */
  searchResultLocation?: SearchResultLocationCitation;
}

/** @oneof */
export interface CitationTypeOneOf {
  /** Char location */
  charLocation?: CharLocationCitation;
  /** Page location */
  pageLocation?: PageLocationCitation;
  /** Content block location */
  contentBlockLocation?: ContentBlockLocationCitation;
  /** Web search result location */
  webSearchResultLocation?: WebSearchResultLocationCitation;
  /** Search result location */
  searchResultLocation?: SearchResultLocationCitation;
}

export interface CharLocationCitation {
  /**
   * Should be "char_location"
   * @maxLength 500
   */
  type?: string;
  /** 0-based index into all document blocks in this request turn. */
  documentIndex?: number | null;
  /**
   * Optional copy of the source document’s title; informational only.
   * @maxLength 10000
   */
  documentTitle?: string | null;
  /** 0-based start character index (inclusive) within the document text. */
  startCharIndex?: number | null;
  /** 0-based end character index (exclusive) within the document text. */
  endCharIndex?: number | null;
  /**
   * Optional quoted snippet; not counted toward tokens.
   * @maxLength 1000000000
   */
  citedText?: string | null;
}

export interface PageLocationCitation {
  /**
   * Should be "page_location"
   * @maxLength 500
   */
  type?: string;
  /** 0-based index into all document blocks in this request turn. */
  documentIndex?: number | null;
  /**
   * Optional copy of the source document’s title; informational only.
   * @maxLength 10000
   */
  documentTitle?: string | null;
  /** 1-based start page number (inclusive). */
  startPageNumber?: number | null;
  /** 1-based end page number (exclusive). */
  endPageNumber?: number | null;
  /**
   * Optional quoted snippet; not counted toward tokens.
   * @maxLength 1000000000
   */
  citedText?: string | null;
}

export interface ContentBlockLocationCitation {
  /**
   * Should be "content_block_location"
   * @maxLength 500
   */
  type?: string;
  /** 0-based index into all document blocks in this request turn. */
  documentIndex?: number | null;
  /**
   * Optional copy of the source document’s title; informational only.
   * @maxLength 10000
   */
  documentTitle?: string | null;
  /** 0-based start content-block index (inclusive) within the custom document. */
  startBlockIndex?: number | null;
  /** 0-based end content-block index (exclusive) within the custom document. */
  endBlockIndex?: number | null;
  /**
   * Optional quoted snippet; not counted toward tokens.
   * @maxLength 1000000000
   */
  citedText?: string | null;
}

export interface WebSearchResultLocationCitation {
  /**
   * Should be "web_search_result_location"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * The URL of the cited source
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * The title of the cited source
   * @maxLength 10000
   */
  title?: string | null;
  /**
   * A reference that must be passed back for multi-turn conversations.
   * @maxLength 1000000000
   */
  encryptedIndex?: string | null;
  /**
   * Up to 150 characters of the cited content
   * @maxLength 1000000000
   */
  citedText?: string | null;
}

export interface SearchResultLocationCitation {
  /**
   * Should be "search_result_location".
   * @maxLength 500
   */
  type?: string;
  /** Index of the search_result within the current turn (0-based). */
  searchResultIndex?: number | null;
  /** 0-based start block indices within that search_result's content. */
  startBlockIndex?: number | null;
  /** 0-based end block indices within that search_result's content. */
  endBlockIndex?: number | null;
  /**
   * Source string
   * @maxLength 100000
   */
  source?: string | null;
  /**
   * Optional title (same as search_result.title).
   * @maxLength 100000
   */
  title?: string | null;
  /**
   * Optional quoted snippet
   * @maxLength 1000000000
   */
  citedText?: string | null;
}

export interface AnthropicV1ImageUrl {
  /**
   * The URL must be a valid wix mp or wix static URL.
   * @maxLength 100000
   */
  url?: string | null;
  /** Media type of the image. */
  mediaType?: ImageMediaTypeMediaTypeWithLiterals;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: V1CacheControl;
}

export enum ImageMediaTypeMediaType {
  UNKNOWN = 'UNKNOWN',
  /** image/jpeg */
  IMAGE_JPEG = 'IMAGE_JPEG',
  /** image/png */
  IMAGE_PNG = 'IMAGE_PNG',
  /** image/webp */
  IMAGE_WEBP = 'IMAGE_WEBP',
  /** image/gif */
  IMAGE_GIF = 'IMAGE_GIF',
}

/** @enumType */
export type ImageMediaTypeMediaTypeWithLiterals =
  | ImageMediaTypeMediaType
  | 'UNKNOWN'
  | 'IMAGE_JPEG'
  | 'IMAGE_PNG'
  | 'IMAGE_WEBP'
  | 'IMAGE_GIF';

export interface V1ToolUse {
  /**
   * Tool use id
   * @maxLength 512
   */
  id?: string | null;
  /**
   * Tool use name
   * @maxLength 1000
   */
  name?: string | null;
  /** Tool use input */
  input?: Record<string, any> | null;
  /** Optional: enable tool use caching */
  cacheControl?: V1CacheControl;
}

export interface V1ToolResult {
  /**
   * Tool use id
   * @maxLength 512
   */
  toolUseId?: string | null;
  /** Tool result is error. */
  isError?: boolean | null;
  /**
   * Tool result content.
   * @maxSize 4096
   */
  content?: ToolResultContentBlock[];
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: V1CacheControl;
}

export interface ToolResultContentBlock
  extends ToolResultContentBlockTypeOneOf {
  /** Text content. */
  text?: V1Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  image?: AnthropicV1ImageUrl;
  /** Document content block. */
  document?: DocumentContent;
  /** Search result block with snippets/citations. */
  searchResult?: ToolResultSearchResult;
}

/** @oneof */
export interface ToolResultContentBlockTypeOneOf {
  /** Text content. */
  text?: V1Text;
  /** Image content, represented as URL. Will be downloaded and passed on as base64. */
  image?: AnthropicV1ImageUrl;
  /** Document content block. */
  document?: DocumentContent;
  /** Search result block with snippets/citations. */
  searchResult?: ToolResultSearchResult;
}

export interface DocumentContent {
  /**
   * Should be "document"
   * @maxLength 500
   */
  type?: string;
  /** Citable payload or reference. */
  source?: DocumentSource;
  /**
   * Optional: Document title
   * Can be passed to the model but not used towards cited content.
   * @maxLength 100000
   */
  title?: string | null;
  /**
   * Optional: Any document metadata as text or stringified json.
   * Can be passed to the model but not used towards cited content.
   * @maxLength 1000000000
   */
  context?: string | null;
  /** Enable citations for this doc */
  citations?: CitationsEnabled;
  /** Optional: Cache the document content */
  cacheControl?: V1CacheControl;
}

export interface DocumentSource {
  /**
   * One of: "text" | "base64" | "content" | "file" | "url".
   * @maxLength 500
   */
  type?: string | null;
  /**
   * Required types "text"/"base64" (e.g., "text/plain", "application/pdf").
   * @maxLength 500
   */
  mediaType?: string | null;
  /**
   * For type "text": raw text. For "base64": bytes as base64.
   * @maxLength 1000000000
   */
  data?: string | null;
  /**
   * For type "file": Files API id (e.g., "file_01...")
   * @maxLength 5000
   */
  fileId?: string | null;
  /**
   * For type "url": absolute URL to the document
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * For type "content": custom content block;  Only text blocks are citable
   * @maxSize 1000
   */
  content?: V1ContentBlock[];
}

export interface CitationsEnabled {
  /** Whether to enable citations */
  enabled?: boolean | null;
}

export interface ToolResultSearchResult {
  /**
   * Should be "search_result".
   * @maxLength 500
   */
  type?: string;
  /**
   * Where this result came from (URL or source label).
   * @maxLength 100000
   */
  source?: string | null;
  /**
   * Human-readable title for the result.
   * @maxLength 100000
   */
  title?: string | null;
  /**
   * Inline text snippets that summarize/support the result.
   * @maxSize 1000
   */
  content?: V1Text[];
  /**
   * Enable/disable citations for this result's content.
   * Matches Anthropic "citations" on search_result blocks.
   */
  citations?: CitationsEnabled;
}

export interface V1Thinking {
  /**
   * Cryptographic token which verifies that the thinking block was generated by Claude, and is verified when thinking blocks are passed back to the API.
   * @maxLength 1000000000
   */
  signature?: string;
  /**
   * Text content of a Thinking block.
   * @maxLength 1000000000
   */
  thinking?: string;
}

export interface V1RedactedThinking {
  /**
   * Occasionally Claude’s internal reasoning will be flagged by our safety systems. When this occurs, we encrypt some or all of the thinking block and return it to you as a redacted_thinking block.
   * These redacted thinking blocks are decrypted when passed back to the API, allowing Claude to continue its response without losing context.
   * @maxLength 1000000000
   */
  data?: string;
}

/**
 * Assistant requests a Model Context Protocol (MCP) tool call.
 * Pair with ToolResult using the same `id`.
 */
export interface McpToolUse {
  /**
   * Unique id for this tool call; must match McpToolResult.tool_use_id.
   * @maxLength 512
   */
  id?: string | null;
  /**
   * Tool name as exposed by the MCP server.
   * @maxLength 1000
   */
  name?: string | null;
  /**
   * Which MCP server to call (must match a server in the request).
   * @maxLength 1000
   */
  serverName?: string | null;
  /** JSON arguments for the tool (object per the tool's schema). */
  input?: Record<string, any> | null;
}

/**
 * Server-tool invocation announced by the ASSISTANT for Anthropic-run tools
 * (e.g., "web_search", "code_execution").
 */
export interface ServerToolUse {
  /**
   * Should be "server_tool_use"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * Tool use id
   * @maxLength 500
   */
  id?: string | null;
  /**
   * The tool name. Available options: "web_search" | "web_fetch" | "code_execution" | "bash_code_execution" | "text_editor_code_execution"
   * @maxLength 500
   */
  name?: string | null;
  /**
   * Tool-specific parameters object:
   * web_search    → { "query": "<string>" }
   * web_fetch     → { "url": "<string>" }
   * code_execution→ { "code":  "<python source>" }
   */
  input?: Record<string, any> | null;
}

/** Server tool result (web search). Either results[] OR error. */
export interface WebSearchToolResult extends WebSearchToolResultContentOneOf {
  /** maps to JSON: content: [ ... ] */
  contentResults?: WebSearchResultList;
  /** maps to JSON: content: { ... } */
  contentError?: WebSearchToolResultError;
  /**
   * Should be "web_search_tool_result"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * Tool use id
   * @maxLength 500
   */
  toolUseId?: string | null;
}

/** @oneof */
export interface WebSearchToolResultContentOneOf {
  /** maps to JSON: content: [ ... ] */
  contentResults?: WebSearchResultList;
  /** maps to JSON: content: { ... } */
  contentError?: WebSearchToolResultError;
}

/** Success payload: the JSON `content` ARRAY of result items. */
export interface WebSearchResultList {
  /**
   * Results items
   * @maxSize 1000
   */
  items?: WebSearchResult[];
}

/**
 * One search result item.
 * Docs (“Search results include”): url, title, page_age, encrypted_content.
 * Each item also has the literal type.
 */
export interface WebSearchResult {
  /**
   * Should be "web_search_result"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * The URL of the source page.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * The title of the source page.
   * @maxLength 100000
   */
  title?: string | null;
  /**
   * When the site was last updated (e.g., "April 30, 2025").
   * @maxLength 100
   */
  pageAge?: string | null;
  /**
   * Encrypted content that must be passed back in multi-turn conversations for citations.
   * @maxLength 1000000000
   */
  encryptedContent?: string | null;
}

/**
 * Error payload
 * Possible error codes: too_many_requests | invalid_input | max_uses_exceeded | query_too_long | unavailable
 */
export interface WebSearchToolResultError {
  /**
   * Should be "web_search_tool_result_error"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * The error code value
   * @maxLength 500
   */
  errorCode?: string | null;
}

export interface CodeExecutionToolResult
  extends CodeExecutionToolResultContentOneOf {
  /** Success */
  contentResult?: CodeExecutionResult;
  /** Error */
  contentError?: CodeExecutionToolResultError;
  /**
   * Should be "code_execution_tool_result"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * Tool use id
   * @maxLength 500
   */
  toolUseId?: string | null;
}

/** @oneof */
export interface CodeExecutionToolResultContentOneOf {
  /** Success */
  contentResult?: CodeExecutionResult;
  /** Error */
  contentError?: CodeExecutionToolResultError;
}

/** Success payload for code execution. */
export interface CodeExecutionResult {
  /**
   * Should be "code_execution_result"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * Output from successful execution (print, etc.).
   * @maxLength 1000000000
   */
  stdout?: string | null;
  /**
   * Error messages emitted by the program.
   * @maxLength 1000000000
   */
  stderr?: string | null;
  /** 0 = success, non-zero = failure. */
  returnCode?: number | null;
  /**
   * Optional: Array of produced artifacts.
   * Example item (typical): { "file_id": "file_abc123", ... }
   * @maxSize 4096
   */
  content?: Record<string, any>[] | null;
}

/**
 * Error payload (HTTP 200; error lives in the result body).
 * Docs list: unavailable | code_execution_exceeded | container_expired
 */
export interface CodeExecutionToolResultError {
  /**
   * Should be "code_execution_tool_result_error"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * The error code value, e.g. "unavailable", "code_execution_exceeded", "container_expired".
   * @maxLength 500
   */
  errorCode?: string | null;
}

export interface ContainerUpload {
  /**
   * Should be "container_upload"
   * @maxLength 500
   */
  type?: string | null;
  /**
   * File identifier returned by the Files API (e.g., "file_01abc...").
   * @maxLength 5000
   */
  fileId?: string | null;
}

/** Web fetch tool result */
export interface WebFetchToolResult extends WebFetchToolResultContentOneOf {
  /** Content success */
  contentSuccess?: WebFetchToolResultContentSuccess;
  /** Content error */
  contentError?: WebFetchToolResultContentError;
  /**
   * Should be "web_fetch_tool_result"
   * @maxLength 500
   */
  type?: string;
  /**
   * Tool use id
   * @maxLength 500
   */
  toolUseId?: string | null;
}

/** @oneof */
export interface WebFetchToolResultContentOneOf {
  /** Content success */
  contentSuccess?: WebFetchToolResultContentSuccess;
  /** Content error */
  contentError?: WebFetchToolResultContentError;
}

export interface WebFetchToolResultContentSuccess {
  /**
   * Should be "web_fetch_result"
   * @maxLength 500
   */
  type?: string;
  /**
   * The URL that was fetched
   * @maxLength 100000
   */
  url?: string | null;
  /** A document block containing the fetched content */
  content?: DocumentContent;
  /**
   * Timestamp when the content was retrieved
   * @maxLength 256
   */
  retrievedAt?: string | null;
}

export interface WebFetchToolResultContentError {
  /**
   * Should be "web_fetch_tool_result_error"
   * @maxLength 500
   */
  type?: string;
  /**
   * These are the possible error codes:
   * - invalid_tool_input: Invalid URL format
   * - url_too_long: URL exceeds maximum length (250 characters)
   * - url_not_allowed: URL blocked by domain filtering rules and model restrictions
   * - url_not_accessible: Failed to fetch content (HTTP error)
   * - too_many_requests: Rate limit exceeded
   * - unsupported_content_type: Content type not supported (only text and PDF)
   * - max_uses_exceeded: Maximum web fetch tool uses exceeded
   * - unavailable: An internal error occurred
   * @maxLength 500
   */
  errorCode?: string | null;
}

export interface InvokeLlamaModelResponse {
  /**
   * The generated text.
   * @maxLength 1000000
   */
  generation?: string | null;
  /** The number of tokens in the prompt. */
  promptTokenCount?: number | null;
  /** The number of tokens in the generated text. */
  generationTokenCount?: number | null;
  /**
   * The reason why the response stopped generating text. Possible values are:
   * stop – The model has finished generating text for the input prompt.
   * length – The length of the tokens for the generated text exceeds the value of max_gen_len in the call to InvokeModel
   * (InvokeModelWithResponseStream, if you are streaming output). The response is truncated to max_gen_len tokens.
   * Consider increasing the value of max_gen_len and trying again.
   * @maxLength 1000
   */
  stopReason?: string | null;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export interface InvokeConverseResponse {
  /** The model's generated output. */
  output?: Output;
  /**
   * Why the model stopped: 'end_turn', 'max_tokens', 'stop_sequence', 'guardrail_intervened', or 'content_filtered'.
   * @maxLength 500
   */
  stopReason?: string | null;
  /** Token usage statistics including cache metrics. */
  usage?: InvokeConverseResponseTokenUsage;
  /** Performance metrics including latency. */
  metrics?: Metrics;
  /** Model-specific response fields as a JSON object. */
  additionalModelResponseFields?: Record<string, any> | null;
  /** The performance configuration applied to this request. */
  performanceConfig?: ConversePerformanceConfig;
  /** Total cost in microcents for this request */
  microcentsSpent?: string | null;
}

export interface ConverseMessage {
  /** The role that generated this message (user or assistant). */
  role?: RoleWithLiterals;
  /**
   * Content blocks that can include text, tool use, and tool results.
   * @maxSize 4096
   */
  content?: ConverseContentBlock[];
}

/** Converse-specific content block (simplified structure for AWS Bedrock Converse API) */
export interface ConverseContentBlock extends ConverseContentBlockContentOneOf {
  /**
   * Plain text content.
   * @maxLength 100000000
   */
  text?: string;
  /** Reasoning refers to a Chain of Thought (CoT) that the model generates to enhance the accuracy of its final response. */
  reasoningContent?: ConverseReasoningContent;
  /** Tool use block representing a function call request. */
  toolUse?: ConverseToolUse;
  /** Tool result block containing the output of a tool execution. */
  toolResult?: ConverseToolResult;
}

/** @oneof */
export interface ConverseContentBlockContentOneOf {
  /**
   * Plain text content.
   * @maxLength 100000000
   */
  text?: string;
  /** Reasoning refers to a Chain of Thought (CoT) that the model generates to enhance the accuracy of its final response. */
  reasoningContent?: ConverseReasoningContent;
  /** Tool use block representing a function call request. */
  toolUse?: ConverseToolUse;
  /** Tool result block containing the output of a tool execution. */
  toolResult?: ConverseToolResult;
}

export interface ConverseReasoningContent {
  /** Contains the reasoning that the model used to return the output. */
  reasoningText?: ReasoningText;
}

export interface ReasoningText {
  /**
   * The reasoning that the model used to return the output.
   * @maxLength 100000000
   */
  text?: string;
}

/** Tool use request from the model */
export interface ConverseToolUse {
  /**
   * Unique identifier for this tool use.
   * @maxLength 1000
   */
  toolUseId?: string;
  /**
   * Name of the tool being invoked.
   * @maxLength 1000
   */
  name?: string;
  /** Input parameters for the tool as a JSON object. */
  input?: Record<string, any> | null;
}

/** Tool execution result */
export interface ConverseToolResult {
  /**
   * Identifier matching the tool_use_id from the ToolUse request.
   * @maxLength 1000
   */
  toolUseId?: string;
  /**
   * Result content (text only for now).
   * @maxSize 1000
   */
  content?: ConverseToolResultContent[];
  /**
   * Execution status: 'success' or 'error'.
   * @maxLength 100
   */
  status?: string | null;
}

/** Tool result content (text only for now) */
export interface ConverseToolResultContent
  extends ConverseToolResultContentContentOneOf {
  /**
   * A tool result that is text.
   * @maxLength 100000000
   */
  text?: string;
  /** A tool result that is JSON format data. */
  json?: Record<string, any> | null;
}

/** @oneof */
export interface ConverseToolResultContentContentOneOf {
  /**
   * A tool result that is text.
   * @maxLength 100000000
   */
  text?: string;
  /** A tool result that is JSON format data. */
  json?: Record<string, any> | null;
}

/** Container for the model's generated output. */
export interface Output {
  /** The generated message with role and content blocks. */
  message?: ConverseMessage;
}

/** todo: expose serverToolUsage */
export interface InvokeConverseResponseTokenUsage {
  /** Tokens in the input (prompt, history, system prompts). */
  inputTokens?: number;
  /** Tokens generated in the response. */
  outputTokens?: number;
  /** Total tokens processed (input + output). */
  totalTokens?: number;
  /** Tokens retrieved from cache. Only present when prompt caching is enabled. */
  cacheReadInputTokens?: number | null;
  /** Tokens written to cache for future requests. Only present when prompt caching is enabled. */
  cacheWriteInputTokens?: number | null;
}

export interface Metrics {
  /** End-to-end latency in milliseconds. */
  latencyMs?: number;
}

export interface ConversePerformanceConfig {
  /**
   * The desired latency profile. Valid values: 'standard' (default) or 'optimized'.
   * @maxLength 100
   */
  latency?: string | null;
}

export interface InvokeMlPlatformLlamaModelResponse {
  /**
   * The generated text.
   * @maxLength 1000000
   */
  generation?: string | null;
  /** The number of tokens in the prompt. */
  promptTokenCount?: number | null;
  /** The number of tokens in the generated text. */
  generationTokenCount?: number | null;
  /**
   * The reason why the response stopped generating text. Possible values are:
   * stop – The model has finished generating text for the input prompt.
   * length – The length of the tokens for the generated text exceeds the value of max_gen_len in the call to InvokeModel
   * (InvokeModelWithResponseStream, if you are streaming output). The response is truncated to max_gen_len tokens.
   * Consider increasing the value of max_gen_len and trying again.
   * @maxLength 1000
   */
  stopReason?: string | null;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export interface InvokeChatCompletionResponse {
  /**
   * Unique response ID
   * @maxLength 100
   */
  id?: string | null;
  /** The model used to generate the response */
  model?: PerplexityModelWithLiterals;
  /**
   * The object type, which always equals chat.completion
   * @maxLength 100
   */
  object?: string | null;
  /** The Unix timestamp (in seconds) of when the completion was created */
  created?: number | null;
  /**
   * Citations for the generated answer
   * @maxLength 10000
   * @maxSize 1000
   */
  citations?: string[];
  /** The list of completion choices the model generated for the input prompt */
  choices?: InvokeChatCompletionResponseChoice[];
  /** URLs and size metadata for returned images */
  images?: PerplexityImageDescriptor[];
  /**
   * Further questions related to the search
   * @maxLength 10000
   * @maxSize 1000
   */
  relatedQuestions?: string[];
  /** Usage statistics for the completion request. */
  usage?: InvokeChatCompletionResponseUsage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export interface PerplexityMessage {
  /**
   * The content of the message
   * @maxLength 200000
   */
  content?: string;
  /**
   * The role of the speaker in this turn of conversation. After the (optional) system message,
   * user and assistant roles should alternate with `user` then `assistant`, ending in `user`.
   */
  role?: PerplexityMessageMessageRoleWithLiterals;
}

export enum PerplexityMessageMessageRole {
  UNKNOWN = 'UNKNOWN',
  SYSTEM = 'SYSTEM',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
}

/** @enumType */
export type PerplexityMessageMessageRoleWithLiterals =
  | PerplexityMessageMessageRole
  | 'UNKNOWN'
  | 'SYSTEM'
  | 'USER'
  | 'ASSISTANT';

export enum PerplexityModel {
  UNKNOWN_PERPLEXITY_MODEL = 'UNKNOWN_PERPLEXITY_MODEL',
  SONAR = 'SONAR',
  SONAR_PRO = 'SONAR_PRO',
  SONAR_REASONING = 'SONAR_REASONING',
  SONAR_REASONING_PRO = 'SONAR_REASONING_PRO',
  SONAR_DEEP_RESEARCH = 'SONAR_DEEP_RESEARCH',
}

/** @enumType */
export type PerplexityModelWithLiterals =
  | PerplexityModel
  | 'UNKNOWN_PERPLEXITY_MODEL'
  | 'SONAR'
  | 'SONAR_PRO'
  | 'SONAR_REASONING'
  | 'SONAR_REASONING_PRO'
  | 'SONAR_DEEP_RESEARCH';

/** Structures the completion choice */
export interface InvokeChatCompletionResponseChoice {
  /** Choice index */
  index?: number | null;
  /**
   * Stop reason, can be `STOP` or `LENGTH`
   * @maxLength 10
   */
  finishReason?: string | null;
  /** Choice message, containing content and role */
  message?: PerplexityMessage;
}

export interface PerplexityImageDescriptor {
  /**
   * Full image url
   * @maxLength 5000
   */
  imageUrl?: string | null;
  /**
   * Image origin website
   * @maxLength 5000
   */
  originUrl?: string | null;
  /** Height */
  height?: number | null;
  /** Width */
  width?: number | null;
}

/** Usage statistics for the completion request. */
export interface InvokeChatCompletionResponseUsage {
  /** The number of tokens provided in the request prompt. */
  promptTokens?: number | null;
  /** The number of tokens generated in the response output. */
  completionTokens?: number | null;
  /** The total number of tokens used in the chat completion (prompt + completion). */
  totalTokens?: number | null;
  /** Tokens passed into the input from citations found during search. Priced like `prompt_tokens` */
  citationTokens?: number | null;
  /** Reasoning tokens are used to reason through the research material before generating the final output via the CoTs */
  reasoningTokens?: number | null;
  /** Number of search queries executed. */
  numSearchQueries?: number | null;
}

export interface CreateImageResponse {
  /**
   * The generated images objects.
   * @maxSize 10
   */
  data?: V1ImageObject[];
  /** Image model used to generate the image. */
  model?: V1ImageModelWithLiterals;
  /** Cost of the request in micro cents. */
  microcentsSpent?: string | null;
}

export interface V1ImageObject {
  /**
   * The WixMp URL of the generated image, available for 24 hours.
   * @maxLength 5000
   * @format WEB_URL
   * @readonly
   */
  url?: string | null;
  /**
   * The prompt that was used to generate the image, if there was any revision to the prompt.
   * @maxLength 100000
   */
  revisedPrompt?: string | null;
}

export enum V1ImageModel {
  UNKNOWN_IMAGE_GENERATION_MODEL = 'UNKNOWN_IMAGE_GENERATION_MODEL',
  DALL_E_2 = 'DALL_E_2',
  DALL_E_3 = 'DALL_E_3',
}

/** @enumType */
export type V1ImageModelWithLiterals =
  | V1ImageModel
  | 'UNKNOWN_IMAGE_GENERATION_MODEL'
  | 'DALL_E_2'
  | 'DALL_E_3';

export interface V1TextToImageResponse {
  /**
   * The generated images objects.
   * @maxSize 10
   */
  data?: ImageObject[];
  /** The model used for generating the image. */
  model?: ImageModelWithLiterals;
  /** Cost of the request in micro cents. */
  microcentsSpent?: string | null;
}

export interface ImageObject {
  /**
   * The WixMp URL of the generated image, available for 24 hours.
   * @maxLength 5000
   * @format WEB_URL
   * @readonly
   */
  url?: string | null;
  /** A specific value [0 .. 4294967294] used to guide the 'randomness' of the generation. */
  seed?: string | null;
  /**
   * Finish reason by the model provider.
   * @maxLength 100
   */
  finishReason?: string | null;
}

export enum ImageModel {
  STABILITY_IMAGE_MODEL_UNSPECIFIED = 'STABILITY_IMAGE_MODEL_UNSPECIFIED',
  /** stable-diffusion-xl-1024-v1-0 - Stable Diffusion XL v1.0 */
  SDXL_1_0 = 'SDXL_1_0',
}

/** @enumType */
export type ImageModelWithLiterals =
  | ImageModel
  | 'STABILITY_IMAGE_MODEL_UNSPECIFIED'
  | 'SDXL_1_0';

export interface GenerateCoreResponse {
  /**
   * The generated images objects.
   * @maxSize 10
   */
  data?: ImageObject[];
  /** The model used for generating the image. */
  model?: ImageCoreModelWithLiterals;
  /** Cost of the request in micro cents. */
  microcentsSpent?: string | null;
}

export enum ImageCoreModel {
  STABILITY_CORE_IMAGE_MODEL_UNSPECIFIED = 'STABILITY_CORE_IMAGE_MODEL_UNSPECIFIED',
  STABLE_IMAGE_CORE = 'STABLE_IMAGE_CORE',
}

/** @enumType */
export type ImageCoreModelWithLiterals =
  | ImageCoreModel
  | 'STABILITY_CORE_IMAGE_MODEL_UNSPECIFIED'
  | 'STABLE_IMAGE_CORE';

export interface GenerateStableDiffusionResponse {
  /**
   * The generated image objects.
   * @maxSize 10
   */
  data?: ImageObject[];
  /** The model used for generating the image. */
  model?: ImageStableDiffusionModelWithLiterals;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export enum ImageStableDiffusionModel {
  STABLE_DIFFUSION_MODEL_UNSPECIFIED = 'STABLE_DIFFUSION_MODEL_UNSPECIFIED',
  /** sd3-large */
  SD3_LARGE = 'SD3_LARGE',
  /** sd3-large-turbo */
  SD3_LARGE_TURBO = 'SD3_LARGE_TURBO',
  /** sd3-medium */
  SD3_MEDIUM = 'SD3_MEDIUM',
  /** sd3.5-large */
  SD3_5_LARGE = 'SD3_5_LARGE',
  /** sd3.5-large-turbo */
  SD3_5_LARGE_TURBO = 'SD3_5_LARGE_TURBO',
  /** sd3.5-medium */
  SD3_5_MEDIUM = 'SD3_5_MEDIUM',
}

/** @enumType */
export type ImageStableDiffusionModelWithLiterals =
  | ImageStableDiffusionModel
  | 'STABLE_DIFFUSION_MODEL_UNSPECIFIED'
  | 'SD3_LARGE'
  | 'SD3_LARGE_TURBO'
  | 'SD3_MEDIUM'
  | 'SD3_5_LARGE'
  | 'SD3_5_LARGE_TURBO'
  | 'SD3_5_MEDIUM';

export interface GenerateAnImageResponse {
  /**
   * The id of the task.
   * @format GUID
   */
  id?: string | null;
  /**
   * status of the image generation
   * one of Task not found, Pending, Request Moderated, Content Moderated, Ready, Error
   * @maxLength 100
   */
  status?: string | null;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /** Result object for the generated image */
  result?: ResultObject;
}

export interface ResultObject {
  /**
   * The URL of the generated image.
   * @maxLength 5000
   * @format WEB_URL
   * @readonly
   */
  url?: string | null;
  /**
   * prompt used for image generation
   * @maxLength 1000000
   */
  prompt?: string | null;
  /** seed used for image generation */
  seed?: string | null;
}

export interface CreatePredictionResponse {
  /**
   * The prediction ID
   * @maxLength 1000
   */
  id?: string | null;
  /**
   * Model Name
   * @maxLength 100
   */
  model?: string | null;
  /**
   * Model version
   * @maxLength 100
   */
  version?: string | null;
  /**
   * The prediction output URLs
   * @minSize 1
   * @maxSize 10
   * @maxLength 40000
   */
  output?: string[] | null;
  /**
   * Prediction text output
   * @minSize 1
   * @maxSize 10
   * @maxLength 40000
   */
  textOutput?: string[] | null;
  /**
   * The prediction status
   * @maxLength 100
   */
  status?: string | null;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /** Token counts */
  tokenUsage?: CreatePredictionResponseTokenUsage;
}

export interface CreatePredictionResponseTokenUsage {
  /** Number of input tokens used in the request. */
  inputTokens?: number | null;
  /** Number of output tokens generated by the model. */
  outputTokens?: number | null;
}

export interface EditImageWithPromptResponse {
  /**
   * The generated images objects.
   * @maxSize 10
   */
  data?: ImageObject[];
  /** The model used for generating the image. */
  model?: EditImageWithPromptRequestModelWithLiterals;
  /** Cost of the request in micro cents. */
  microcentsSpent?: string | null;
}

export enum EditImageWithPromptRequestModel {
  UNKNOWN_EDIT_IMAGE_WITH__PROMPT_REQUEST_MODEL = 'UNKNOWN_EDIT_IMAGE_WITH__PROMPT_REQUEST_MODEL',
  INPAINT = 'INPAINT',
  OUTPAINT = 'OUTPAINT',
}

/** @enumType */
export type EditImageWithPromptRequestModelWithLiterals =
  | EditImageWithPromptRequestModel
  | 'UNKNOWN_EDIT_IMAGE_WITH__PROMPT_REQUEST_MODEL'
  | 'INPAINT'
  | 'OUTPAINT';

export interface TextToImageResponse {
  /**
   * Generation TextToImageTaskResult
   * @maxSize 1000
   */
  data?: TextToImageTaskResult[];
}

export interface TextToImageTaskResult {
  /**
   * The API will return the taskUUID you sent in the request.
   * @format GUID
   */
  taskUuid?: string;
  /**
   * The unique identifier of the image.
   * @format GUID
   */
  imageUuid?: string;
  /**
   * If outputType is set to URL, this parameter contains the URL of the image to be downloaded.
   * @maxLength 2048
   */
  imageUrl?: string | null;
  /** If checkNSFW parameter is used, NSFWContent is included informing if the image has been flagged as potentially sensitive content. */
  nsfwContent?: boolean;
  /** A cost of generated image. */
  microcentsSpent?: string | null;
  /**
   * A seed is a value used to randomize the image generation.
   * @min 1
   * @max 9223372036854776000
   */
  seed?: string | null;
}

export interface V1GenerateImageResponse {
  /**
   * Array of generated image results, one for each requested sampleCount
   * @maxSize 8
   */
  predictions?: Prediction[];
  /** Cost of the request in micro cents */
  microcentsSpent?: string | null;
}

export interface Prediction {
  /**
   * The URL of the generated image.
   * @maxLength 5000
   * @format WEB_URL
   * @readonly
   */
  url?: string | null;
  /**
   * Enhanced prompt used for generation (only returned for models that support prompt enhancement)
   * @maxLength 1000
   */
  prompt?: string | null;
  /**
   * The responsible AI filter reason
   * Only returned if includeRaiReason is enabled and this image was filtered out
   * @maxLength 1000
   */
  raiFilteredReason?: string | null;
  /** Safety attributes information */
  safetyAttributes?: SafetyAttributes;
}

export interface SafetyAttributes {
  /**
   * The safety attribute categories
   * @maxSize 100
   * @maxLength 100
   */
  categories?: string[] | null;
  /**
   * The safety attribute scores
   * @maxSize 100
   */
  scores?: number[] | null;
}

export interface GenerateVideoResponse {
  /**
   * Generated videos
   * @maxSize 4
   */
  videos?: GeneratedVideo[];
  /** Cost of the request in micro-cents. */
  microcentsSpent?: string | null;
}

export interface GeneratedVideo {
  /**
   * The URL of the generated video.
   * @format WEB_URL
   */
  videoUrl?: string | null;
  /**
   * The video MIME type (currently only "video/mp4")
   * @maxLength 50
   */
  mimeType?: string | null;
}

export interface GenerateImageMlPlatformResponse {
  /**
   * The prediction ID
   * @maxLength 1000
   */
  id?: string | null;
  /**
   * Model Name
   * @maxLength 100
   */
  model?: string | null;
  /**
   * Model version
   * @maxLength 100
   */
  version?: string | null;
  /**
   * The prediction status
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The prediction output URLs
   * @minSize 1
   * @maxSize 10
   * @maxLength 40000
   */
  output?: string[] | null;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export interface CreateImageOpenAiResponse {
  /**
   * The generated images objects.
   * @maxSize 10
   */
  data?: V1ImageObject[];
  /** Image model used to generate the image. */
  model?: OpenAiImageModelWithLiterals;
  /** Cost of the request in micro cents. */
  microcentsSpent?: string | null;
  /** Usage information from the API response */
  usage?: V1ImageUsage;
}

export enum OpenAiImageModel {
  UNKNOWN_IMAGE_CREATION_MODEL = 'UNKNOWN_IMAGE_CREATION_MODEL',
  GPT_4O_IMAGE = 'GPT_4O_IMAGE',
  GPT_IMAGE_1 = 'GPT_IMAGE_1',
  GPT_IMAGE_EXP = 'GPT_IMAGE_EXP',
  GPT_IMAGE_EXP_2 = 'GPT_IMAGE_EXP_2',
  GPT_IMAGE_EXP_3 = 'GPT_IMAGE_EXP_3',
  GPT_IMAGE_1_5 = 'GPT_IMAGE_1_5',
}

/** @enumType */
export type OpenAiImageModelWithLiterals =
  | OpenAiImageModel
  | 'UNKNOWN_IMAGE_CREATION_MODEL'
  | 'GPT_4O_IMAGE'
  | 'GPT_IMAGE_1'
  | 'GPT_IMAGE_EXP'
  | 'GPT_IMAGE_EXP_2'
  | 'GPT_IMAGE_EXP_3'
  | 'GPT_IMAGE_1_5';

export interface V1ImageUsage {
  /** Number of tokens in the input */
  inputTokens?: number | null;
  /** Details about input tokens */
  inputTokensDetails?: OpenAiImageTokenDetails;
  /** Number of tokens in the output */
  outputTokens?: number | null;
  /** Output tokens details */
  outputTokensDetails?: OpenAiImageTokenDetails;
  /** Total number of tokens used */
  totalTokens?: number | null;
}

export interface OpenAiImageTokenDetails {
  /** Number of tokens used for image processing */
  imageTokens?: number | null;
  /** Number of tokens used for text processing */
  textTokens?: number | null;
}

export interface EditImageOpenAiResponse {
  /**
   * The generated images objects.
   * @maxSize 10
   */
  data?: V1ImageObject[];
  /** Image model used to generate the image. */
  model?: OpenAiImageModelWithLiterals;
  /** Cost of the request in micro cents. */
  microcentsSpent?: string | null;
  /** Usage information from the API response */
  usage?: V1ImageUsage;
}

export interface V1CreateChatCompletionResponse {
  /**
   * A unique identifier for the chat completion.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * Description of the response object. Will be equal to "chat.completion" for chat completion.
   * @maxLength 100
   */
  object?: string | null;
  /** Timestamp for when the response was created. */
  created?: number | null;
  /** Model that produced the completion. */
  model?: ChatCompletionModelWithLiterals;
  /** A list of chat completion choices. Can be more than one if n is greater than 1. */
  choices?: V1CreateChatCompletionResponseChoice[];
  /** TokenUsage object describing the tokens usage per request. */
  usage?: V1CreateChatCompletionResponseTokenUsage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /**
   * This fingerprint represents the backend configuration that the model runs with.
   * Can be used in conjunction with the seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
}

export interface GoogleproxyV1ChatCompletionMessage {
  /** The role of the message author. */
  role?: V1ChatCompletionMessageMessageRoleWithLiterals;
  /**
   * The content of the message, which can be text or an image URL.
   * @maxSize 5
   */
  contentParts?: V1ChatCompletionMessageContentPart[];
}

export interface V1ChatCompletionMessageImageUrlContent {
  /**
   * The URL of the image.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * Similar to media resolution, this determines the maximum tokens per image for the request.
   * Note that while OpenAI's field is per-image,
   * Google enforces the same detail across the request,
   * and passing multiple detail types in one request will throw an error.
   * @maxLength 100
   */
  detail?: string | null;
}

export enum V1ChatCompletionMessageMessageRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
}

/** @enumType */
export type V1ChatCompletionMessageMessageRoleWithLiterals =
  | V1ChatCompletionMessageMessageRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT'
  | 'SYSTEM';

export interface V1ChatCompletionMessageContentPart
  extends V1ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: V1ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
  /**
   * The type of the content part. Can be text or image_url.
   * @maxLength 100
   */
  type?: string | null;
}

/** @oneof */
export interface V1ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: V1ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
}

export enum ChatCompletionModel {
  UNKNOWN_CHAT_COMPLETION_MODEL = 'UNKNOWN_CHAT_COMPLETION_MODEL',
  /**
   * https://cloud.google.com/vertex-ai/generative-ai/docs/partner-models/llama/llama4-scout
   * llama-4-scout-17b-16e-instruct-maas
   */
  LLAMA_4_SCOUT_17B_16E_INSTRUCT_MAAS = 'LLAMA_4_SCOUT_17B_16E_INSTRUCT_MAAS',
  /**
   * https://cloud.google.com/vertex-ai/generative-ai/docs/partner-models/llama/llama4-maverick
   * llama-4-maverick-17b-128e-instruct-maas
   */
  LLAMA_4_MAVERICK_17B_128E_INSTRUCT_MAAS = 'LLAMA_4_MAVERICK_17B_128E_INSTRUCT_MAAS',
}

/** @enumType */
export type ChatCompletionModelWithLiterals =
  | ChatCompletionModel
  | 'UNKNOWN_CHAT_COMPLETION_MODEL'
  | 'LLAMA_4_SCOUT_17B_16E_INSTRUCT_MAAS'
  | 'LLAMA_4_MAVERICK_17B_128E_INSTRUCT_MAAS';

export interface V1CreateChatCompletionResponseChoice {
  /** Index of this Choice in choices array. */
  index?: number | null;
  /** ChatCompletionMessage object that defines the message. */
  message?: GoogleproxyV1ChatCompletionMessage;
  /**
   * Reason why the message generation was stopped.
   * @maxLength 100
   */
  finishReason?: string | null;
}

export interface V1CreateChatCompletionResponseTokenUsage {
  /** Number of LLM tokens required to encode input. */
  inputTokens?: number | null;
  /** Number of LLM tokens required to encode output. */
  outputTokens?: number | null;
  /** Total number of LLM tokens used for the request. */
  totalTokens?: number | null;
}

export interface InvokeMlPlatformOpenAIChatCompletionRawResponse {
  /**
   * A unique identifier for the chat completion.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * Description of the response object. Will be equal to "chat.completion" for chat completion.
   * @maxLength 100
   */
  object?: string | null;
  /** Timestamp for when the response was created. */
  created?: number | null;
  /**
   * Model that produced the completion.
   * @maxLength 10000
   */
  modelId?: string;
  /**
   * A list of chat completion choices. Can be more than one if n is greater than 1.
   * @maxSize 10000
   */
  choices?: Choice[];
  /** TokenUsage object describing the tokens usage per request. */
  usage?: TokenUsage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /**
   * This fingerprint represents the backend configuration that the model runs with.
   * Can be used in conjunction with the seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
}

export interface ChatCompletionMessage {
  /** The role of the message author. */
  role?: MessageRoleWithLiterals;
  /**
   * The content of the message, which can be text or an image URL.
   * @maxSize 5
   */
  contentParts?: ContentPart[];
}

export interface ImageUrlContent {
  /**
   * The URL of the image.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * Similar to media resolution, this determines the maximum tokens per image for the request.
   * Note that while OpenAI's field is per-image,
   * Google enforces the same detail across the request,
   * and passing multiple detail types in one request will throw an error.
   * @maxLength 100
   */
  detail?: string | null;
}

export enum MessageRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
}

/** @enumType */
export type MessageRoleWithLiterals =
  | MessageRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT'
  | 'SYSTEM';

export interface ContentPart extends ContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: ImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
  /**
   * The type of the content part. Can be text or image_url.
   * @maxLength 100
   */
  type?: string | null;
}

/** @oneof */
export interface ContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: ImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
}

export interface Choice {
  /** Index of this Choice in choices array. */
  index?: number | null;
  /** ChatCompletionMessage object that defines the message. */
  message?: ChatCompletionMessage;
  /**
   * Reason why the message generation was stopped.
   * @maxLength 100
   */
  finishReason?: string | null;
}

export interface TokenUsage {
  /** Number of LLM tokens required to encode input. */
  inputTokens?: number | null;
  /** Number of LLM tokens required to encode output. */
  outputTokens?: number | null;
  /** Total number of LLM tokens used for the request. */
  totalTokens?: number | null;
}

export interface VideoInferenceResponse {
  /**
   * Generation VideoInferenceTaskResult
   * @maxSize 1000
   */
  data?: VideoInferenceTaskResult[];
}

export interface VideoInferenceTaskResult {
  /**
   * The API will return the taskType you sent in the request.
   * @maxLength 100
   */
  taskType?: string;
  /**
   * The API will return the taskUUID you sent in the request.
   * @format GUID
   */
  taskUuid?: string;
  /**
   * A unique identifier for the generated video.
   * @format GUID
   */
  videoUuid?: string | null;
  /**
   * If outputType is set to URL, this parameter contains the URL of the video to be downloaded.
   * @maxLength 10000
   */
  videoUrl?: string | null;
  /**
   * The seed value that was used to generate this video.
   * @min 1
   * @max 9223372036854776000
   */
  seed?: string | null;
  /** A cost of generated video. */
  microcentsSpent?: string | null;
  /**
   * The current processing status (for polling operations).
   * @maxLength 50
   */
  status?: string | null;
}

export interface V1OpenAiResponsesResponse {
  /**
   * Unique identifier for this Response.
   * @maxLength 100
   */
  id?: string | null;
  /** Unix timestamp (in seconds) of when this Response was created. */
  createdAt?: string | null;
  /** Whether to run the model response in the background. */
  background?: boolean | null;
  /** Details about why the response is incomplete. */
  incompleteDetails?: OpenAiResponsesResponseIncompleteDetails;
  /** An upper bound for the number of tokens that can be generated for a response, including visible output tokens and reasoning tokens. */
  maxOutputTokens?: number | null;
  /**
   * The maximum number of total calls to built-in tools that can be processed in a response.
   * This maximum number applies across all built-in tool calls, not per individual tool. Any further attempts to call a tool by the model will be ignored.
   */
  maxToolCalls?: number | null;
  /**
   * Model ID used to generate the response, like gpt-4o or o3. OpenAI offers a wide range of models with different capabilities,
   * performance characteristics, and price points. Refer to the model guide to browse and compare available models.
   */
  model?: V1ResponsesModelWithLiterals;
  /**
   * The object type of this resource - always set to response.
   * @maxLength 100
   */
  object?: string | null;
  /**
   * An array of content items generated by the model.
   * The length and order of items in the output array is dependent on the model's response.
   * Rather than accessing the first item in the output array and assuming it's an assistant message with the content generated by the model,
   * you might consider using the output_text property where supported in SDKs.
   * @maxSize 1000
   */
  output?: V1ResponsesOutput[];
  /** Whether to allow the model to run tool calls in parallel. */
  parallelToolCalls?: boolean | null;
  /**
   * The unique ID of the previous response to the model. Use this to create multi-turn conversations. Learn more about conversation state.
   * @maxLength 100
   */
  previousResponseId?: string | null;
  /** The reasoning effort used by the model to generate the response. */
  reasoning?: V1ResponsesReasoning;
  /**
   * The status of the response generation. One of completed, failed, in_progress, cancelled, queued, or incomplete.
   * @maxLength 100
   */
  status?: string | null;
  /** What sampling temperature to use, between 0 and 2. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic. We generally recommend altering this or top_p but not both. */
  temperature?: number | null;
  /**
   * An integer between 0 and 20 specifying the number of most likely tokens to return at each token position, each with an associated log probability.
   * @max 20
   */
  topLogprobs?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling,
   * where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both.
   * @max 20
   */
  topP?: number | null;
  /**
   * The truncation strategy to use for the model response.
   * auto: If the context of this response and previous ones exceeds the model's context window size, the model will truncate the response to fit the context window by dropping input items in the middle of the conversation.
   * disabled (default): If a model response will exceed the context window size for a model, the request will fail with a 400 error.
   * @maxLength 100
   */
  truncation?: string | null;
  /** TokenUsage object describing the tokens usage per request. */
  usage?: V1ResponsesTokenUsage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export interface OpenAiResponsesResponseIncompleteDetails {
  /**
   * The reason why the response is incomplete.
   * @maxLength 100
   */
  reason?: string | null;
}

export enum V1ResponsesModel {
  MODEL_UNSPECIFIED = 'MODEL_UNSPECIFIED',
  GPT_5_2025_08_07_RESPONSES = 'GPT_5_2025_08_07_RESPONSES',
  GPT_5_MINI_2025_08_07_RESPONSES = 'GPT_5_MINI_2025_08_07_RESPONSES',
  GPT_5_NANO_2025_08_07_RESPONSES = 'GPT_5_NANO_2025_08_07_RESPONSES',
  O3_PRO_2025_06_10 = 'O3_PRO_2025_06_10',
  O3_DEEP_RESEARCH_2025_06_26 = 'O3_DEEP_RESEARCH_2025_06_26',
  GPT_5_CODEX = 'GPT_5_CODEX',
  GPT_5_1_2025_11_13 = 'GPT_5_1_2025_11_13',
  GPT_5_1_CODEX = 'GPT_5_1_CODEX',
  GPT_5_1_CODEX_MINI = 'GPT_5_1_CODEX_MINI',
  GPT_EXP_RESPONSES = 'GPT_EXP_RESPONSES',
  GPT_EXP_RESPONSES_2 = 'GPT_EXP_RESPONSES_2',
  GPT_EXP_RESPONSES_3 = 'GPT_EXP_RESPONSES_3',
  GPT_5_1_CODEX_MAX = 'GPT_5_1_CODEX_MAX',
  GPT_5_2_2025_12_11 = 'GPT_5_2_2025_12_11',
  GPT_5_2_CODEX = 'GPT_5_2_CODEX',
  GPT_5_3_CODEX = 'GPT_5_3_CODEX',
  GPT_5_4_2026_03_05 = 'GPT_5_4_2026_03_05',
  GPT_5_4_PRO_2026_03_05 = 'GPT_5_4_PRO_2026_03_05',
  GPT_5_4_MINI_2026_03_17 = 'GPT_5_4_MINI_2026_03_17',
  GPT_5_4_NANO_2026_03_17 = 'GPT_5_4_NANO_2026_03_17',
}

/** @enumType */
export type V1ResponsesModelWithLiterals =
  | V1ResponsesModel
  | 'MODEL_UNSPECIFIED'
  | 'GPT_5_2025_08_07_RESPONSES'
  | 'GPT_5_MINI_2025_08_07_RESPONSES'
  | 'GPT_5_NANO_2025_08_07_RESPONSES'
  | 'O3_PRO_2025_06_10'
  | 'O3_DEEP_RESEARCH_2025_06_26'
  | 'GPT_5_CODEX'
  | 'GPT_5_1_2025_11_13'
  | 'GPT_5_1_CODEX'
  | 'GPT_5_1_CODEX_MINI'
  | 'GPT_EXP_RESPONSES'
  | 'GPT_EXP_RESPONSES_2'
  | 'GPT_EXP_RESPONSES_3'
  | 'GPT_5_1_CODEX_MAX'
  | 'GPT_5_2_2025_12_11'
  | 'GPT_5_2_CODEX'
  | 'GPT_5_3_CODEX'
  | 'GPT_5_4_2026_03_05'
  | 'GPT_5_4_PRO_2026_03_05'
  | 'GPT_5_4_MINI_2026_03_17'
  | 'GPT_5_4_NANO_2026_03_17';

export interface V1ResponsesOutput extends V1ResponsesOutputOutputOneOf {
  /** An output message from the model. */
  outputMessage?: V1ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: V1ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: V1ResponsesFunctionToolCall;
  /** A reasoning item output from the model. */
  reasoning?: V1ResponsesReasoningOutput;
  /** A code interpreter item output from the model. */
  codeInterpreterToolCall?: V1ResponsesCodeInterpreterToolCall;
}

/** @oneof */
export interface V1ResponsesOutputOutputOneOf {
  /** An output message from the model. */
  outputMessage?: V1ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: V1ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: V1ResponsesFunctionToolCall;
  /** A reasoning item output from the model. */
  reasoning?: V1ResponsesReasoningOutput;
  /** A code interpreter item output from the model. */
  codeInterpreterToolCall?: V1ResponsesCodeInterpreterToolCall;
}

export interface V1ResponsesOutputMessage {
  /**
   * The unique ID of the output message.
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The type of the output message. Always message.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the message input. One of in_progress, completed, or incomplete. Populated when input items are returned via API.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The content of the output message.
   * @maxSize 1000
   */
  content?: ResponsesOutputMessageOutputContent[];
  /**
   * The role of the output message. Always assistant.
   * @maxLength 100
   */
  role?: string | null;
}

/**
 * Annotation types
 * The annotations of the text output.
 */
export interface V1OutputAnnotation
  extends V1OutputAnnotationAnnotationTypeOneOf {
  /** A citation for a web resource used to generate a model response. */
  urlCitation?: V1UrlCitation;
}

/** @oneof */
export interface V1OutputAnnotationAnnotationTypeOneOf {
  /** A citation for a web resource used to generate a model response. */
  urlCitation?: V1UrlCitation;
}

export interface V1UrlCitation {
  /**
   * The type of the URL citation. Always url_citation.
   * @maxLength 100
   */
  type?: string | null;
  /** The index of the first character of the URL citation in the message. */
  startIndex?: number | null;
  /** The index of the last character of the URL citation in the message. */
  endIndex?: number | null;
  /**
   * The title of the web resource.
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * The URL of the web resource.
   * @maxLength 10000
   */
  url?: string | null;
}

export interface ResponsesOutputMessageOutputContent {
  /**
   * The type of the output text output_text/refusal.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The text content of the message.
   * @maxLength 1000000000
   */
  text?: string | null;
  /**
   * The text content of the message.
   * @maxLength 1000000000
   */
  refusal?: string | null;
  /**
   * Annotations for the output content (citations, etc.)
   * @maxSize 1000
   */
  annotations?: V1OutputAnnotation[];
}

export interface V1ResponsesWebSearchToolCall {
  /** The action performed by the model in the web search tool call. */
  action?: ResponsesWebSearchToolCallAction;
  /**
   * The unique ID of the web search tool call.
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The status of the web search tool call.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The type of the web search tool call. Always web_search_call.
   * @maxLength 100
   */
  type?: string | null;
}

export interface ResponsesWebSearchToolCallAction {
  /**
   * The action type.
   * Action type "find": Searches for a pattern within a loaded page.
   * Action type "search" - Performs a web search query.
   * Action type "open_page" - Opens a specific URL from search results.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The search query.
   * @maxLength 100000
   */
  query?: string | null;
  /**
   * The URL opened by the model.
   * @maxLength 1000
   */
  url?: string | null;
  /**
   * The pattern or text to search for within the page.
   * @maxLength 100000
   */
  pattern?: string | null;
}

export interface V1ResponsesFunctionToolCall {
  /**
   * The unique ID of the function call.
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The type of the call. Always "function_call".
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the function call.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The arguments passed to the function as a JSON string.
   * @maxLength 100000
   */
  arguments?: string | null;
  /**
   * The call ID that links this call to its output.
   * @maxLength 100
   */
  callId?: string | null;
  /**
   * The name of the function that was called.
   * @maxLength 100
   */
  name?: string | null;
}

export interface V1ResponsesReasoningOutput {
  /** @maxLength 100 */
  id?: string | null;
  /** @maxLength 100 */
  type?: string | null;
  /** @maxSize 1000 */
  summary?: V1ResponsesReasoningSummaryContent[];
  /** @maxSize 1000 */
  content?: V1ResponsesReasoningContent[];
  /** @maxLength 10000000 */
  encryptedContent?: string | null;
  /** @maxLength 100 */
  status?: string | null;
}

export interface V1ResponsesReasoningSummaryContent {
  /** @maxLength 100 */
  type?: string | null;
  /** @maxLength 1000000 */
  text?: string | null;
}

export interface V1ResponsesReasoningContent {
  /** @maxLength 100 */
  type?: string | null;
  /** @maxLength 1000000 */
  text?: string | null;
}

/** Output types for code interpreter calls and outputs */
export interface V1ResponsesCodeInterpreterToolCall {
  /**
   * The unique ID of the code interpreter tool call
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The type of the tool call. Always "code_interpreter_call"
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the tool call
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The code to run
   * @maxLength 1000000
   */
  code?: string | null;
  /**
   * The container ID used to run the code
   * @maxLength 100
   */
  containerId?: string | null;
  /**
   * The outputs generated by the code interpreter
   * @maxSize 100
   */
  outputs?: V1ResponsesCodeInterpreterOutput[];
}

export interface V1ResponsesCodeInterpreterOutput
  extends V1ResponsesCodeInterpreterOutputOutputTypeOneOf {
  /** Log output */
  logsOutput?: V1ResponsesCodeInterpreterLogsOutput;
  /** Image output */
  imageOutput?: V1ResponsesCodeInterpreterImageOutput;
}

/** @oneof */
export interface V1ResponsesCodeInterpreterOutputOutputTypeOneOf {
  /** Log output */
  logsOutput?: V1ResponsesCodeInterpreterLogsOutput;
  /** Image output */
  imageOutput?: V1ResponsesCodeInterpreterImageOutput;
}

export interface V1ResponsesCodeInterpreterLogsOutput {
  /**
   * The type of output. Always "logs"
   * @maxLength 10
   */
  type?: string | null;
  /**
   * The logs output from the code interpreter
   * @maxLength 1000000
   */
  logs?: string | null;
}

export interface V1ResponsesCodeInterpreterImageOutput {
  /**
   * The type of output. Always "image"
   * @maxLength 10
   */
  type?: string | null;
  /**
   * The image URL
   * @maxLength 1000
   */
  imageUrl?: string | null;
}

export interface V1ResponsesReasoning {
  /**
   * Constrains effort on reasoning for reasoning models. Currently supported values are low, medium, and high.
   * Reducing reasoning effort can result in faster responses and fewer tokens used on reasoning in a response.
   * @maxLength 100
   */
  effort?: string | null;
  /**
   * A summary of the reasoning performed by the model.
   * This can be useful for debugging and understanding the model's reasoning process. One of auto, concise, or detailed.
   * @maxLength 100
   */
  summary?: string | null;
}

export interface V1ResponsesTokenUsage {
  /** Number of LLM tokens required to encode input. */
  inputTokens?: number | null;
  /** A detailed breakdown of the input tokens. */
  inputTokensDetails?: V1ResponsesInputTokensDetails;
  /** Number of LLM tokens required to encode output. */
  outputTokens?: number | null;
  /** A detailed breakdown of the output tokens. */
  outputTokensDetails?: V1ResponsesOutputTokensDetails;
  /** Total number of LLM tokens used for the request. */
  totalTokens?: number | null;
}

export interface V1ResponsesInputTokensDetails {
  /** Cached tokens present in the prompt. */
  cachedTokens?: number | null;
}

export interface V1ResponsesOutputTokensDetails {
  /** Cached tokens present in the prompt. */
  reasoningTokens?: number | null;
}

export interface OpenAiResponsesResponse {
  /**
   * Unique identifier for this Response.
   * @maxLength 100
   */
  id?: string | null;
  /** Unix timestamp (in seconds) of when this Response was created. */
  createdAt?: string | null;
  /** Whether to run the model response in the background. */
  background?: boolean | null;
  /** Details about why the response is incomplete. */
  incompleteDetails?: IncompleteDetails;
  /** An upper bound for the number of tokens that can be generated for a response, including visible output tokens and reasoning tokens. */
  maxOutputTokens?: number | null;
  /**
   * The maximum number of total calls to built-in tools that can be processed in a response.
   * This maximum number applies across all built-in tool calls, not per individual tool. Any further attempts to call a tool by the model will be ignored.
   */
  maxToolCalls?: number | null;
  /**
   * Model ID used to generate the response, like gpt-4o or o3. OpenAI offers a wide range of models with different capabilities,
   * performance characteristics, and price points. Refer to the model guide to browse and compare available models.
   */
  model?: ResponsesModelWithLiterals;
  /**
   * The object type of this resource - always set to response.
   * @maxLength 100
   */
  object?: string | null;
  /**
   * An array of content items generated by the model.
   * The length and order of items in the output array is dependent on the model's response.
   * Rather than accessing the first item in the output array and assuming it's an assistant message with the content generated by the model,
   * you might consider using the output_text property where supported in SDKs.
   * @maxSize 1000
   */
  output?: ResponsesOutput[];
  /** Whether to allow the model to run tool calls in parallel. */
  parallelToolCalls?: boolean | null;
  /**
   * The unique ID of the previous response to the model. Use this to create multi-turn conversations. Learn more about conversation state.
   * @maxLength 100
   */
  previousResponseId?: string | null;
  /** The reasoning effort used by the model to generate the response. */
  reasoning?: ResponsesReasoning;
  /**
   * The status of the response generation. One of completed, failed, in_progress, cancelled, queued, or incomplete.
   * @maxLength 100
   */
  status?: string | null;
  /** What sampling temperature to use, between 0 and 2. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic. We generally recommend altering this or top_p but not both. */
  temperature?: number | null;
  /**
   * An integer between 0 and 20 specifying the number of most likely tokens to return at each token position, each with an associated log probability.
   * @max 20
   */
  topLogprobs?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling,
   * where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both.
   * @max 20
   */
  topP?: number | null;
  /**
   * The truncation strategy to use for the model response.
   * auto: If the context of this response and previous ones exceeds the model's context window size, the model will truncate the response to fit the context window by dropping input items in the middle of the conversation.
   * disabled (default): If a model response will exceed the context window size for a model, the request will fail with a 400 error.
   * @maxLength 100
   */
  truncation?: string | null;
  /** TokenUsage object describing the tokens usage per request. */
  usage?: ResponsesTokenUsage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export interface IncompleteDetails {
  /**
   * The reason why the response is incomplete.
   * @maxLength 100
   */
  reason?: string | null;
}

export enum ResponsesModel {
  MODEL_UNSPECIFIED = 'MODEL_UNSPECIFIED',
  GPT_5_2025_08_07_RESPONSES = 'GPT_5_2025_08_07_RESPONSES',
  GPT_5_MINI_2025_08_07_RESPONSES = 'GPT_5_MINI_2025_08_07_RESPONSES',
  GPT_5_NANO_2025_08_07_RESPONSES = 'GPT_5_NANO_2025_08_07_RESPONSES',
  GPT_5_2_2025_12_11 = 'GPT_5_2_2025_12_11',
}

/** @enumType */
export type ResponsesModelWithLiterals =
  | ResponsesModel
  | 'MODEL_UNSPECIFIED'
  | 'GPT_5_2025_08_07_RESPONSES'
  | 'GPT_5_MINI_2025_08_07_RESPONSES'
  | 'GPT_5_NANO_2025_08_07_RESPONSES'
  | 'GPT_5_2_2025_12_11';

export interface ResponsesOutput extends ResponsesOutputOutputOneOf {
  /** An output message from the model. */
  outputMessage?: ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: ResponsesFunctionToolCall;
  /** A reasoning item output from the model. */
  reasoning?: ResponsesReasoningOutput;
  /** A code interpreter item output from the model. */
  codeInterpreterToolCall?: ResponsesCodeInterpreterToolCall;
}

/** @oneof */
export interface ResponsesOutputOutputOneOf {
  /** An output message from the model. */
  outputMessage?: ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: ResponsesFunctionToolCall;
  /** A reasoning item output from the model. */
  reasoning?: ResponsesReasoningOutput;
  /** A code interpreter item output from the model. */
  codeInterpreterToolCall?: ResponsesCodeInterpreterToolCall;
}

export interface ResponsesOutputMessage {
  /**
   * The unique ID of the output message.
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The type of the output message. Always message.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the message input. One of in_progress, completed, or incomplete. Populated when input items are returned via API.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The content of the output message.
   * @maxSize 1000
   */
  content?: OutputContent[];
  /**
   * The role of the output message. Always assistant.
   * @maxLength 100
   */
  role?: string | null;
}

/**
 * Annotation types
 * The annotations of the text output.
 */
export interface OutputAnnotation extends OutputAnnotationAnnotationTypeOneOf {
  /** A citation for a web resource used to generate a model response. */
  urlCitation?: UrlCitation;
}

/** @oneof */
export interface OutputAnnotationAnnotationTypeOneOf {
  /** A citation for a web resource used to generate a model response. */
  urlCitation?: UrlCitation;
}

export interface UrlCitation {
  /**
   * The type of the URL citation. Always url_citation.
   * @maxLength 100
   */
  type?: string | null;
  /** The index of the first character of the URL citation in the message. */
  startIndex?: number | null;
  /** The index of the last character of the URL citation in the message. */
  endIndex?: number | null;
  /**
   * The title of the web resource.
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * The URL of the web resource.
   * @maxLength 10000
   */
  url?: string | null;
}

export interface OutputContent {
  /**
   * The type of the output text output_text/refusal.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The text content of the message.
   * @maxLength 1000000000
   */
  text?: string | null;
  /**
   * The text content of the message.
   * @maxLength 1000000000
   */
  refusal?: string | null;
  /**
   * Annotations for the output content (citations, etc.)
   * @maxSize 1000
   */
  annotations?: OutputAnnotation[];
}

export interface ResponsesWebSearchToolCall {
  /** The action performed by the model in the web search tool call. */
  action?: Action;
  /**
   * The unique ID of the web search tool call.
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The status of the web search tool call.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The type of the web search tool call. Always web_search_call.
   * @maxLength 100
   */
  type?: string | null;
}

export interface Action {
  /**
   * The action type.
   * Action type "find": Searches for a pattern within a loaded page.
   * Action type "search" - Performs a web search query.
   * Action type "open_page" - Opens a specific URL from search results.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The search query.
   * @maxLength 100000
   */
  query?: string | null;
  /**
   * The URL opened by the model.
   * @maxLength 1000
   */
  url?: string | null;
  /**
   * The pattern or text to search for within the page.
   * @maxLength 100000
   */
  pattern?: string | null;
}

export interface ResponsesFunctionToolCall {
  /**
   * The unique ID of the function call.
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The type of the call. Always "function_call".
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the function call.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The arguments passed to the function as a JSON string.
   * @maxLength 100000
   */
  arguments?: string | null;
  /**
   * The call ID that links this call to its output.
   * @maxLength 100
   */
  callId?: string | null;
  /**
   * The name of the function that was called.
   * @maxLength 100
   */
  name?: string | null;
}

export interface ResponsesReasoningOutput {
  /** @maxLength 100 */
  id?: string | null;
  /** @maxLength 100 */
  type?: string | null;
  /** @maxSize 1000 */
  summary?: ResponsesReasoningSummaryContent[];
  /** @maxSize 1000 */
  content?: ResponsesReasoningContent[];
  /** @maxLength 10000000 */
  encryptedContent?: string | null;
  /** @maxLength 100 */
  status?: string | null;
}

export interface ResponsesReasoningSummaryContent {
  /** @maxLength 100 */
  type?: string | null;
  /** @maxLength 1000000 */
  text?: string | null;
}

export interface ResponsesReasoningContent {
  /** @maxLength 100 */
  type?: string | null;
  /** @maxLength 1000000 */
  text?: string | null;
}

/** Output types for code interpreter calls and outputs */
export interface ResponsesCodeInterpreterToolCall {
  /**
   * The unique ID of the code interpreter tool call
   * @maxLength 100
   */
  id?: string | null;
  /**
   * The type of the tool call. Always "code_interpreter_call"
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the tool call
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The code to run
   * @maxLength 1000000
   */
  code?: string | null;
  /**
   * The container ID used to run the code
   * @maxLength 100
   */
  containerId?: string | null;
  /**
   * The outputs generated by the code interpreter
   * @maxSize 100
   */
  outputs?: ResponsesCodeInterpreterOutput[];
}

export interface ResponsesCodeInterpreterOutput
  extends ResponsesCodeInterpreterOutputOutputTypeOneOf {
  /** Log output */
  logsOutput?: ResponsesCodeInterpreterLogsOutput;
  /** Image output */
  imageOutput?: ResponsesCodeInterpreterImageOutput;
}

/** @oneof */
export interface ResponsesCodeInterpreterOutputOutputTypeOneOf {
  /** Log output */
  logsOutput?: ResponsesCodeInterpreterLogsOutput;
  /** Image output */
  imageOutput?: ResponsesCodeInterpreterImageOutput;
}

export interface ResponsesCodeInterpreterLogsOutput {
  /**
   * The type of output. Always "logs"
   * @maxLength 10
   */
  type?: string | null;
  /**
   * The logs output from the code interpreter
   * @maxLength 1000000
   */
  logs?: string | null;
}

export interface ResponsesCodeInterpreterImageOutput {
  /**
   * The type of output. Always "image"
   * @maxLength 10
   */
  type?: string | null;
  /**
   * The image URL
   * @maxLength 1000
   */
  imageUrl?: string | null;
}

export interface ResponsesReasoning {
  /**
   * Constrains effort on reasoning for reasoning models. Currently supported values are low, medium, and high.
   * Reducing reasoning effort can result in faster responses and fewer tokens used on reasoning in a response.
   * @maxLength 100
   */
  effort?: string | null;
  /**
   * A summary of the reasoning performed by the model.
   * This can be useful for debugging and understanding the model's reasoning process. One of auto, concise, or detailed.
   * @maxLength 100
   */
  summary?: string | null;
}

export interface ResponsesTokenUsage {
  /** Number of LLM tokens required to encode input. */
  inputTokens?: number | null;
  /** A detailed breakdown of the input tokens. */
  inputTokensDetails?: ResponsesInputTokensDetails;
  /** Number of LLM tokens required to encode output. */
  outputTokens?: number | null;
  /** A detailed breakdown of the output tokens. */
  outputTokensDetails?: ResponsesOutputTokensDetails;
  /** Total number of LLM tokens used for the request. */
  totalTokens?: number | null;
}

export interface ResponsesInputTokensDetails {
  /** Cached tokens present in the prompt. */
  cachedTokens?: number | null;
}

export interface ResponsesOutputTokensDetails {
  /** Cached tokens present in the prompt. */
  reasoningTokens?: number | null;
}

export interface CreateVideoResponse {
  videoJob?: VideoJob;
}

export interface VideoJob {
  /**
   * The unique identifier for the video generation job.
   * @maxLength 200
   */
  id?: string | null;
  /**
   * The status of the response generation.
   * @maxLength 50
   */
  status?: string | null;
  /**
   * The generated video result url. Only present when status is "completed".
   * @maxLength 5000
   * @format WEB_URL
   */
  url?: string | null;
  /** Error payload that explains why generation failed, if applicable. */
  error?: ErrorInfo;
  /** The progress of the video generation as a percentage (0-100) */
  progress?: number | null;
}

export interface ErrorInfo {
  /**
   * code
   * @maxLength 50
   */
  code?: string | null;
  /**
   * message
   * @maxLength 1000
   */
  message?: string | null;
}

export interface InvokeCustomOpenAiModelResponse {
  /**
   * A unique identifier for the chat completion.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * Description of the response object. Will be equal to "chat.completion" for chat completion.
   * @maxLength 100
   */
  object?: string | null;
  /** Timestamp for when the response was created. */
  created?: number | null;
  /**
   * Model that produced the completion.
   * @maxLength 10000
   */
  modelId?: string;
  /** A list of chat completion choices. Can be more than one if n is greater than 1. */
  choices?: InvokeCustomOpenAiModelResponseChoice[];
  /** TokenUsage object describing the tokens usage per request. */
  usage?: InvokeCustomOpenAiModelResponseTokenUsage;
  /** Cost of the request in microcents. */
  microcentsSpent?: string | null;
  /**
   * This fingerprint represents the backend configuration that the model runs with.
   * Can be used in conjunction with the seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
}

export interface Fireworks_proxyV1ChatCompletionMessage {
  /** The role of the message author. */
  role?: Fireworks_proxyV1ChatCompletionMessageMessageRoleWithLiterals;
  /**
   * The contents of the message. content is required for all messages, and may be null for assistant messages with function calls.
   * @maxLength 1000000000
   */
  content?: string | null;
  /**
   * The name of the author of this message. name is required if role is function, and it should be the name of
   * the function whose response is in the content. May contain a-z, A-Z, 0-9, and underscores, with a maximum length of 64 characters.
   * @minLength 1
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The name and arguments of a function that should be called, as generated by the model.
   * The name and arguments of a function that should be called, as generated by the model.
   * @deprecated
   * @replacedBy tool_calls
   */
  functionCall?: ChatCompletionMessageFunctionWithArgs;
  /**
   * The tool calls generated by the model, such as function calls.
   * @maxSize 1000
   */
  toolCalls?: ChatCompletionMessageToolCall[];
  /**
   * Tool call that this message is responding to.
   * @maxLength 100
   */
  toolCallId?: string | null;
  /**
   * An array of content parts with a defined type,each can be of type text or image_url when passing in images.
   * If defined, content field will be ignored.
   * You can pass multiple images by adding multiple image_url content parts.
   * Image input is only supported when using the gpt-4-visual-preview model.
   * @maxSize 5
   */
  contentParts?: Fireworks_proxyV1ChatCompletionMessageContentPart[];
}

export interface ChatCompletionMessageFunctionWithArgs {
  /**
   * The name of the function to call.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The arguments to call the function with, as generated by the model in JSON format.
   * Note that the model does not always generate valid JSON, and may hallucinate parameters not defined by
   * your function schema. Validate the arguments in your code before calling your function.
   * @maxLength 1000000
   */
  arguments?: string | null;
}

export interface Fireworks_proxyV1ChatCompletionMessageImageUrlContent {
  /**
   * The URL of the image, must be a valid wix-mp URL.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * By controlling the detail parameter, which has three options, low, high, or auto,
   * you have control over how the model processes the image and generates its textual understanding.
   * more info and cost calculation : https://platform.openai.com/docs/guides/vision/low-or-high-fidelity-image-understanding
   * @maxLength 100
   */
  detail?: string | null;
}

export enum Fireworks_proxyV1ChatCompletionMessageMessageRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
  FUNCTION = 'FUNCTION',
  TOOL = 'TOOL',
  /**
   * Developer-provided instructions that the model should follow, regardless of messages sent by the user.
   * With o1 models and newer, developer messages replace the previous system messages.
   */
  DEVELOPER = 'DEVELOPER',
}

/** @enumType */
export type Fireworks_proxyV1ChatCompletionMessageMessageRoleWithLiterals =
  | Fireworks_proxyV1ChatCompletionMessageMessageRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT'
  | 'SYSTEM'
  | 'FUNCTION'
  | 'TOOL'
  | 'DEVELOPER';

export interface ChatCompletionMessageToolCall {
  /**
   * The ID of the tool call.
   * @maxLength 100
   */
  id?: string;
  /**
   * The type of the tool. Currently, only function is supported.
   * @maxLength 100
   */
  type?: string;
  /** The function that the model called. */
  function?: ChatCompletionMessageFunctionWithArgs;
}

export interface Fireworks_proxyV1ChatCompletionMessageContentPart
  extends Fireworks_proxyV1ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: Fireworks_proxyV1ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
  /**
   * The type of the content part. Can be text or image_url.
   * @maxLength 100
   */
  type?: string | null;
}

/** @oneof */
export interface Fireworks_proxyV1ChatCompletionMessageContentPartContentValueOneOf {
  /** Image_url content */
  imageUrl?: Fireworks_proxyV1ChatCompletionMessageImageUrlContent;
  /**
   * Text content
   * @maxLength 1000000000
   */
  text?: string | null;
}

export interface InvokeCustomOpenAiModelResponsePromptTokenDetails {
  /** Audio input tokens present in the prompt. */
  audioTokens?: number | null;
  /** Cached tokens present in the prompt. */
  cachedTokens?: number | null;
}

export interface InvokeCustomOpenAiModelResponseCompletionTokenDetails {
  /** Reasoning tokens present in the completion. */
  reasoningTokens?: number | null;
  /** Audio tokens present in the completion. */
  audioTokens?: number | null;
  /** Accepted prediction tokens. */
  acceptedPredictionTokens?: number | null;
  /** Rejected prediction tokens. */
  rejectedPredictionTokens?: number | null;
}

export interface InvokeCustomOpenAiModelResponseChoice {
  /** Index of this Choice in choices array. */
  index?: number | null;
  /** ChatCompletionMessage object that defines the message. */
  message?: Fireworks_proxyV1ChatCompletionMessage;
  /**
   * Reason why the message generation was stopped.
   * @maxLength 100
   */
  finishReason?: string | null;
}

export interface InvokeCustomOpenAiModelResponseTokenUsage {
  /** Number of LLM tokens required to encode input. */
  inputTokens?: number | null;
  /** Number of LLM tokens required to encode output. */
  outputTokens?: number | null;
  /** Total number of LLM tokens used for the request. */
  totalTokens?: number | null;
  /** Breakdown of tokens used in the prompt. */
  promptTokenDetails?: InvokeCustomOpenAiModelResponsePromptTokenDetails;
  /** Breakdown of tokens used in the completion. */
  completionTokenDetails?: InvokeCustomOpenAiModelResponseCompletionTokenDetails;
}

export interface CreateContentTaskResponse {
  task?: CommonContentTaskResponse;
}

export interface CommonContentTaskResponse {
  /**
   * The ID of the video generation task.
   * @format GUID
   */
  id?: string | null;
  /**
   * The name and version of the model used by the task.
   * @maxLength 255
   */
  model?: string | null;
  /**
   * The status of the task.
   * Valid values: queued, running, cancelled, succeeded, failed, expired.
   * @maxLength 255
   */
  status?: string | null;
  /** Error information. Null if the task succeeds. */
  error?: TaskError;
  /** The time when the task was created (Unix timestamp in seconds). */
  createdAt?: string | null;
  /** The time when the task was last updated (Unix timestamp in seconds). */
  updatedAt?: string | null;
  /** The output after the video generation task is completed. */
  content?: TaskContent;
  /**
   * The seed value used for this request.
   * @min -1
   * @max 4294967295
   */
  seed?: number | null;
  /**
   * The resolution of the generated video.
   * @maxLength 255
   */
  resolution?: string | null;
  /**
   * The width-to-height ratio of the generated video.
   * @maxLength 255
   */
  ratio?: string | null;
  /**
   * The length of the generated video in seconds.
   * Only one of duration and frames is returned.
   * @min -1
   * @max 120
   */
  duration?: number | null;
  /**
   * Number of frames for the generated video.
   * Only one of duration and frames is returned.
   * @min 29
   * @max 2890
   */
  frames?: number | null;
  /** The frame rate of the generated video. */
  framespersecond?: number | null;
  /**
   * Whether the generated video includes audio synchronized with the visuals.
   * Only supported by Seedance 1.5 pro.
   */
  generateAudio?: boolean | null;
  /**
   * Whether the generated video is a Draft video.
   * Only returned by Seedance 1.5 Pro.
   */
  draft?: boolean | null;
  /**
   * Draft video task ID. Returned when generating official video from a draft.
   * @maxLength 1000
   */
  draftTaskId?: string | null;
  /**
   * The service tier actually used to process the task.
   * @maxLength 255
   */
  serviceTier?: string | null;
  /**
   * The expiration threshold for the task, in seconds.
   * @min 3600
   * @max 259200
   */
  executionExpiresAfter?: number | null;
  /** The token usage for the request. */
  usage?: TaskUsage;
  /** The cost of the request in microcents. */
  microcentsSpent?: string | null;
}

export interface TaskError {
  /**
   * The error code.
   * @maxLength 255
   */
  code?: string | null;
  /**
   * The error message.
   * @maxLength 255
   */
  message?: string | null;
}

export interface TaskContent {
  /**
   * The URL of the output video. Valid for 24 hours.
   * @maxLength 255
   */
  videoUrl?: string | null;
  /**
   * URL of the last frame of the generated video. Valid for 24 hours.
   * Returned only if return_last_frame was set to true in the creation request.
   * @maxLength 255
   */
  lastFrameUrl?: string | null;
}

export interface TaskUsage {
  /** The number of tokens consumed for the video output. */
  completionTokens?: number | null;
  /** Total tokens for this request (input tokens are always 0). */
  totalTokens?: number | null;
}

export interface GenerateImageResponse {
  /**
   * The model ID used for generation (model name-version).
   * @maxLength 255
   */
  model?: string | null;
  /** Unix timestamp (seconds) of the creation time of the request. */
  created?: string | null;
  /**
   * Generated image data. May contain multiple entries for batch generation.
   * When batch-generating, if an image fails due to content moderation the remaining
   * images continue; if it fails due to an internal error (500), subsequent images are skipped.
   */
  data?: ImageData[];
  /** Token usage information for this request. */
  usage?: ImageUsage;
  /** Request-level error information, if any. */
  error?: ImageError;
  /** The cost of the request in microcents (set by proxy, not vendor). */
  microcentsSpent?: string | null;
}

export interface ImageData {
  /**
   * URL of the generated image. Returned when response_format = "url".
   * The link expires 24 hours after generation.
   * @maxLength 10000
   */
  url?: string | null;
  /** Base64-encoded image data. Returned when response_format = "b64_json". */
  b64Json?: string | null;
  /**
   * The width and height of the generated image in pixels, format "<width>x<height>" (e.g. "2048x2048").
   * @maxLength 255
   */
  size?: string | null;
  /** Per-image error information for a failed generation within a batch. */
  error?: ImageError;
}

export interface ImageError {
  /**
   * Error code. See ByteDance error codes documentation.
   * @maxLength 255
   */
  code?: string | null;
  /**
   * Human-readable error message.
   * @maxLength 1000
   */
  message?: string | null;
}

export interface ImageUsage {
  /** Number of images successfully generated, excluding failures. Billing is based on this count. */
  generatedImages?: number | null;
  /** Number of output tokens consumed. Calculated as sum(image_width * image_height) / 256, rounded. */
  outputTokens?: number | null;
  /** Total tokens consumed by this request. Same as output_tokens (input tokens are not counted). */
  totalTokens?: number | null;
}

export interface GenerateContentByPromptObjectRequest {
  /** Prompt object that describes the content generation request. */
  prompt?: Prompt;
  /** Key-value pairs that will be used to substitute templated parameters in the prompt. */
  params?: Record<string, string>;
  /** Contains additional information for the request, such as interaction ID and additional tags (comma-separated string). */
  userRequestInfo?: UserRequestInfo;
  /** Fallback properties for the request. */
  fallbackProperties?: FallbackProperties;
  /** Key-value pairs that will be used to overwrite dynamic properties in the prompt. */
  dynamicProperties?: Record<string, string>;
  /** Optional asynchronous configuration. When set, results are delivered via pub/sub events or explicit polling. */
  asyncGenerationConfig?: AsyncGenerationConfig;
  /** Dynamic request configuration containing tools and other dynamic properties. */
  dynamicRequestConfig?: DynamicRequestConfig;
}

export interface Prompt extends PromptModelRequestOneOf {
  /** OpenAI chat completion request. */
  openAiChatCompletionRequest?: OpenaiproxyV1CreateChatCompletionRequest;
  /** Azure OpenAI chat completion request. */
  azureChatCompletionRequest?: CreateChatCompletionRequest;
  /** Google Gemini generate content request. */
  googleGeminiGenerateContentRequest?: GenerateContentRequest;
  /** Anthropic Claude via Amazon Bedrock generate content request. */
  anthropicClaudeRequest?: InvokeAnthropicClaudeModelRequest;
  /** Anthropic Claude via Google Vertex request. */
  googleAnthropicClaudeRequest?: V1InvokeAnthropicClaudeModelRequest;
  /** Native Anthropic API proxy generate content request. */
  invokeAnthropicModelRequest?: InvokeAnthropicModelRequest;
  /** Llama via Amazon Bedrock text completion request. */
  llamaModelRequest?: InvokeLlamaModelRequest;
  /** Invoke Amazon Converse API request. */
  amazonConverseRequest?: InvokeConverseRequest;
  /** OpenAI generate image request (Image Generation). */
  openAiCreateImageRequest?: CreateImageRequest;
  /** Stability AI text to image request (Image Generation). */
  stabilityAiTextToImageRequest?: V1TextToImageRequest;
  /** Stability AI generate core request (Image Generation). */
  stabilityAiGenerateCoreRequest?: GenerateCoreRequest;
  /** Stability AI - Stable Diffusion 3.0 & 3.5 request. */
  stabilityAiStableDiffusionRequest?: GenerateStableDiffusionRequest;
  /** Black Forest Labs - Flux Generate an Image request. */
  blackForestLabsGenerateImageRequest?: GenerateAnImageRequest;
  /** Replicate AI - Create Prediction request. */
  replicateCreatePredictionRequest?: CreatePredictionRequest;
  /** Stability AI - Edit with Prompt request. */
  stabilityAiEditWithPromptRequest?: EditImageWithPromptRequest;
  /** Runware AI - Flux TextToImage request */
  runwareTextToImageRequest?: TextToImageRequest;
  /** ML Platform Llama model prediction request */
  mlPlatformLlamaModelRequest?: InvokeMlPlatformLlamaModelRequest;
  /** Perplexity chat completion request */
  perplexityChatCompletionRequest?: InvokeChatCompletionRequest;
  /** Google AI - generate image request */
  googleGenerateImageRequest?: V1GenerateImageRequest;
  /** ML platform - generate image request */
  mlPlatformGenerateImageRequest?: GenerateImageMlPlatformRequest;
  /** OpenAI image creation response. */
  openAiCreateOpenAiImageRequest?: CreateImageOpenAiRequest;
  /** OpenAI image edit response. */
  openAiEditOpenAiImageRequest?: EditImageOpenAiRequest;
  /** Google AI - generate video request */
  googleGenerateVideoRequest?: GenerateVideoRequest;
  /** Google AI - create chat completion request */
  googleCreateChatCompletionRequest?: V1CreateChatCompletionRequest;
  /** ML Platform - invoke an OpenAI-type interface with a JSON string */
  mlPlatformOpenAiRawRequest?: InvokeMlPlatformOpenAIChatCompletionRawRequest;
  /** Runware Video inference request */
  runwareVideoInferenceRequest?: VideoInferenceRequest;
  /** Open AI Responses API request */
  openAiResponsesRequest?: V1OpenAiResponsesRequest;
  /** Open AI Responses API request via Azure */
  azureOpenAiResponsesRequest?: OpenAiResponsesRequest;
  /** OpenAI video generation request */
  openAiCreateVideoRequest?: CreateVideoRequest;
  /** Fireworks - OpenAI payload */
  fireworksInvokeCustomOpenAiRequest?: InvokeCustomOpenAiModelRequest;
  /** Bytedance - Create content task request (video generation) */
  bytedanceCreateContentTaskRequest?: CreateContentTaskRequest;
  /** Bytedance - Generate image request (Seedream models) */
  bytedanceGenerateImageRequest?: GenerateImageRequest;
  /**
   * Prompt id.
   * @format GUID
   */
  id?: string | null;
  /**
   * Names of template parameters, that will be checked and substituted during GenerateText requests.
   * @maxLength 1000
   * @maxSize 100
   */
  templatedParameterNames?: string[];
  /** FallbackPromptConfig object that describes optional second Prompt that can be invoked in case main invocation fails. */
  fallbackPromptConfig?: FallbackPromptConfig;
  /**
   * Names of dynamic properties, that will be checked and substituted during requests.
   * @maxLength 1000
   * @maxSize 100
   */
  templatedDynamicPropertiesNames?: string[];
}

/** @oneof */
export interface PromptModelRequestOneOf {
  /** OpenAI chat completion request. */
  openAiChatCompletionRequest?: OpenaiproxyV1CreateChatCompletionRequest;
  /** Azure OpenAI chat completion request. */
  azureChatCompletionRequest?: CreateChatCompletionRequest;
  /** Google Gemini generate content request. */
  googleGeminiGenerateContentRequest?: GenerateContentRequest;
  /** Anthropic Claude via Amazon Bedrock generate content request. */
  anthropicClaudeRequest?: InvokeAnthropicClaudeModelRequest;
  /** Anthropic Claude via Google Vertex request. */
  googleAnthropicClaudeRequest?: V1InvokeAnthropicClaudeModelRequest;
  /** Native Anthropic API proxy generate content request. */
  invokeAnthropicModelRequest?: InvokeAnthropicModelRequest;
  /** Llama via Amazon Bedrock text completion request. */
  llamaModelRequest?: InvokeLlamaModelRequest;
  /** Invoke Amazon Converse API request. */
  amazonConverseRequest?: InvokeConverseRequest;
  /** OpenAI generate image request (Image Generation). */
  openAiCreateImageRequest?: CreateImageRequest;
  /** Stability AI text to image request (Image Generation). */
  stabilityAiTextToImageRequest?: V1TextToImageRequest;
  /** Stability AI generate core request (Image Generation). */
  stabilityAiGenerateCoreRequest?: GenerateCoreRequest;
  /** Stability AI - Stable Diffusion 3.0 & 3.5 request. */
  stabilityAiStableDiffusionRequest?: GenerateStableDiffusionRequest;
  /** Black Forest Labs - Flux Generate an Image request. */
  blackForestLabsGenerateImageRequest?: GenerateAnImageRequest;
  /** Replicate AI - Create Prediction request. */
  replicateCreatePredictionRequest?: CreatePredictionRequest;
  /** Stability AI - Edit with Prompt request. */
  stabilityAiEditWithPromptRequest?: EditImageWithPromptRequest;
  /** Runware AI - Flux TextToImage request */
  runwareTextToImageRequest?: TextToImageRequest;
  /** ML Platform Llama model prediction request */
  mlPlatformLlamaModelRequest?: InvokeMlPlatformLlamaModelRequest;
  /** Perplexity chat completion request */
  perplexityChatCompletionRequest?: InvokeChatCompletionRequest;
  /** Google AI - generate image request */
  googleGenerateImageRequest?: V1GenerateImageRequest;
  /** ML platform - generate image request */
  mlPlatformGenerateImageRequest?: GenerateImageMlPlatformRequest;
  /** OpenAI image creation response. */
  openAiCreateOpenAiImageRequest?: CreateImageOpenAiRequest;
  /** OpenAI image edit response. */
  openAiEditOpenAiImageRequest?: EditImageOpenAiRequest;
  /** Google AI - generate video request */
  googleGenerateVideoRequest?: GenerateVideoRequest;
  /** Google AI - create chat completion request */
  googleCreateChatCompletionRequest?: V1CreateChatCompletionRequest;
  /** ML Platform - invoke an OpenAI-type interface with a JSON string */
  mlPlatformOpenAiRawRequest?: InvokeMlPlatformOpenAIChatCompletionRawRequest;
  /** Runware Video inference request */
  runwareVideoInferenceRequest?: VideoInferenceRequest;
  /** Open AI Responses API request */
  openAiResponsesRequest?: V1OpenAiResponsesRequest;
  /** Open AI Responses API request via Azure */
  azureOpenAiResponsesRequest?: OpenAiResponsesRequest;
  /** OpenAI video generation request */
  openAiCreateVideoRequest?: CreateVideoRequest;
  /** Fireworks - OpenAI payload */
  fireworksInvokeCustomOpenAiRequest?: InvokeCustomOpenAiModelRequest;
  /** Bytedance - Create content task request (video generation) */
  bytedanceCreateContentTaskRequest?: CreateContentTaskRequest;
  /** Bytedance - Generate image request (Seedream models) */
  bytedanceGenerateImageRequest?: GenerateImageRequest;
}

export interface FallbackPromptConfig {
  /**
   * Id of the fallback Prompt. This Prompt will be used for text generation in case the invocation of original Prompt fails.
   * @format GUID
   */
  fallbackPromptId?: string | null;
}

export interface OpenaiproxyV1CreateChatCompletionRequest
  extends OpenaiproxyV1CreateChatCompletionRequestFunctionCallOneOf {
  /** Specifying a particular function via {"name":\ "my_function"} forces the model to call that function. */
  forceCallFunctionCallConfig?: Record<string, any> | null;
  /**
   * "none" means the model does not call a function, and responds to the end-user.
   * "auto" means the model can pick between an end-user or calling a function.
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10
   */
  defaultFunctionCallConfig?: string | null;
  /** ID of the model to use. */
  model?: OpenaiproxyV1ModelWithLiterals;
  /**
   * A list of messages comprising the conversation so far.
   * @minSize 1
   * @maxSize 1000
   */
  messages?: OpenaiproxyV1ChatCompletionMessage[];
  /**
   * A list of functions the model may generate JSON inputs for.
   * @maxSize 100
   * @deprecated
   * @replacedBy tools
   */
  functions?: CreateChatCompletionRequestFunctionSignature[];
  /**
   * What sampling temperature to use, between 0 and 2. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic.
   * We generally recommend altering this or top_p but not both.
   * @max 2
   */
  temperature?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both. Defaults to 1.
   */
  topP?: number | null;
  /** How many chat completion choices to generate for each input message. Defaults to 1. */
  n?: number | null;
  /**
   * Stream: Up to 4 sequences where the API will stop generating further tokens.
   * @maxSize 4
   * @maxLength 100
   */
  stop?: string[];
  /**
   * The maximum number of tokens allowed for the generated answer.
   * By default, the number of tokens the model can return will be (4096 - prompt tokens).
   */
  maxTokens?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on whether they appear in the text so far,
   * increasing the model's likelihood to talk about new topics.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  presencePenalty?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on their existing frequency in the text so far,
   * decreasing the model's likelihood to repeat the same line verbatim.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  frequencyPenalty?: number | null;
  /**
   * Modify the likelihood of specified tokens appearing in the completion.
   * Accepts a json object that maps tokens (specified by their token ID in the tokenizer) to an associated bias value from -100 to 100.
   * Mathematically, the bias is added to the logits generated by the model prior to sampling.
   * The exact effect will vary per model, but values between -1 and 1 should decrease or increase likelihood of selection;
   * values like -100 or 100 should result in a ban or exclusive selection of the relevant token.
   */
  logitBias?: Record<string, number>;
  /**
   * A unique identifier representing your end-user, which can help OpenAI to monitor and detect abuse.
   * @maxLength 100
   */
  user?: string | null;
  /**
   * This feature is in Beta. If specified, our system will make a best effort to sample deterministically, such that
   * repeated requests with the same "seed" and parameters should return the same result. Determinism is not guaranteed,
   * and you should refer to the "system_fingerprint" response parameter to monitor changes in the backend.
   */
  seed?: string | null;
  /**
   * Controls which (if any) function is called by the model.
   * "none" means the model will not call a function and instead generates a message.
   * "auto" means the model can pick between generating a message or calling a function.
   * Specifying a particular function via {"type: "function", "function": {"name": "my_function"}} forces the model to call that function.
   *
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10000
   */
  toolChoice?: string | null;
  /**
   * A list of tools the model may call. Currently, only functions are supported as a tool. Use this to provide a list of functions the model may generate JSON inputs for.
   * @maxSize 1000
   */
  tools?: V1CreateChatCompletionRequestTool[];
  /** If present, describes the fine-tuning model that will be called instead of generic one. */
  fineTuningSpec?: V1FineTuningSpec;
  /**
   * An object specifying the format that the model must output. Compatible with gpt-4-1106-preview and gpt-3.5-turbo-1106.
   * Setting to type to "json_object" enables JSON mode, which guarantees the message the model generates is valid JSON.
   * Important: when using JSON mode, you must also instruct the model to produce JSON yourself via a system or user message.
   * Without this, the model may generate an unending stream of whitespace until the generation reaches the token limit,
   * resulting in a long-running and seemingly "stuck" request. Also note that the message content may be partially cut off if finish_reason="length",
   * which indicates the generation exceeded max_tokens or the conversation exceeded the max context length.
   */
  responseFormat?: OpenaiproxyV1CreateChatCompletionRequestResponseFormat;
  /**
   * An upper bound for the number of tokens that can be generated for a completion,
   * including visible output tokens and reasoning tokens.
   */
  maxCompletionTokens?: number | null;
  /**
   * Constrains effort on reasoning for reasoning models. Currently supported values are low, medium, and high.
   * Reducing reasoning effort can result in faster responses and fewer tokens used on reasoning in a response.
   * o1 models only
   * @maxLength 100
   */
  reasoningEffort?: string | null;
  /** Whether to enable parallel function calling during tool use. */
  parallelToolCalls?: boolean | null;
  /**
   * Constrains the verbosity of the model's response. Lower values will result in more concise responses,
   * while higher values will result in more verbose responses.
   * Currently supported values are low, medium, and high.
   * @maxLength 100
   */
  verbosity?: string | null;
}

/** @oneof */
export interface OpenaiproxyV1CreateChatCompletionRequestFunctionCallOneOf {
  /** Specifying a particular function via {"name":\ "my_function"} forces the model to call that function. */
  forceCallFunctionCallConfig?: Record<string, any> | null;
  /**
   * "none" means the model does not call a function, and responds to the end-user.
   * "auto" means the model can pick between an end-user or calling a function.
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10
   */
  defaultFunctionCallConfig?: string | null;
}

export interface CreateChatCompletionRequestFunctionSignature {
  /**
   * The name of the function to be called.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The description of what the function does.
   * @maxLength 100000
   */
  description?: string | null;
  /** The parameters the functions accepts, described as a JSON Schema object. */
  parameters?: Record<string, any> | null;
  /** If true, the model will strictly follow the function parameters schema (a.k.a. open-ai structured outputs). */
  strict?: boolean | null;
}

export interface V1CreateChatCompletionRequestTool {
  /**
   * The type of the tool. Currently, only "function" is supported.
   * @maxLength 100
   */
  type?: string;
  /** Function definition object. */
  function?: CreateChatCompletionRequestFunctionSignature;
}

export interface V1FineTuningSpec {
  /**
   * Organization field from in the returned fine-tuned model name
   * Example: ft:gpt-3.5-turbo:<my-org>:custom_suffix:id
   * @maxLength 100
   */
  org?: string | null;
  /**
   * Suffix field from in the returned fine-tuned model name
   * Example: ft:gpt-3.5-turbo:my-org:<custom_suffix>:id
   * @maxLength 100
   */
  suffix?: string | null;
  /**
   * Id field from in the returned fine-tuned model name
   * Example: ft:gpt-3.5-turbo:my-org:custom_suffix:<id>
   * @maxLength 100
   */
  id?: string | null;
}

export interface OpenaiproxyV1CreateChatCompletionRequestResponseFormat {
  /**
   * Must be one of text, json_object or json_schema.
   * @maxLength 100
   */
  type?: string | null;
  /** The schema object describes the output object for the model. Currently, only the JSON Schema Object is supported. */
  schema?: Record<string, any> | null;
}

export interface CreateChatCompletionRequest
  extends CreateChatCompletionRequestFunctionCallOneOf {
  /** Specifying a particular function via {"name":\ "my_function"} forces the model to call that function. */
  forceCallFunctionCallConfig?: Record<string, any> | null;
  /**
   * "none" means the model does not call a function, and responds to the end-user.
   * "auto" means the model can pick between an end-user or calling a function.
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10
   */
  defaultFunctionCallConfig?: string | null;
  /** ID of the model to use. */
  model?: V1ModelWithLiterals;
  /**
   * A list of messages comprising the conversation so far.
   * @minSize 1
   * @maxSize 1000
   */
  messages?: V1ChatCompletionMessage[];
  /**
   * A list of functions the model may generate JSON inputs for.
   * @maxSize 100
   * @deprecated
   * @replacedBy tools
   */
  functions?: FunctionSignature[];
  /**
   * What sampling temperature to use, between 0 and 2. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic.
   * We generally recommend altering this or top_p but not both.
   * @max 2
   */
  temperature?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both. Defaults to 1.
   */
  topP?: number | null;
  /** How many chat completion choices to generate for each input message. Defaults to 1. */
  n?: number | null;
  /**
   * Stream: Up to 4 sequences where the API will stop generating further tokens.
   * @maxSize 4
   * @maxLength 100
   */
  stop?: string[];
  /**
   * The maximum number of tokens allowed for the generated answer.
   * By default, the number of tokens the model can return will be (4096 - prompt tokens).
   */
  maxTokens?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on whether they appear in the text so far,
   * increasing the model's likelihood to talk about new topics.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  presencePenalty?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on their existing frequency in the text so far,
   * decreasing the model's likelihood to repeat the same line verbatim.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  frequencyPenalty?: number | null;
  /**
   * Modify the likelihood of specified tokens appearing in the completion.
   * Accepts a json object that maps tokens (specified by their token ID in the tokenizer) to an associated bias value from -100 to 100.
   * Mathematically, the bias is added to the logits generated by the model prior to sampling.
   * The exact effect will vary per model, but values between -1 and 1 should decrease or increase likelihood of selection;
   * values like -100 or 100 should result in a ban or exclusive selection of the relevant token.
   */
  logitBias?: Record<string, number>;
  /**
   * A unique identifier representing your end-user, which can help OpenAI to monitor and detect abuse.
   * @maxLength 100
   */
  user?: string | null;
  /**
   * This feature is in Beta. If specified, our system will make a best effort to sample deterministically, such that
   * repeated requests with the same "seed" and parameters should return the same result. Determinism is not guaranteed,
   * and you should refer to the "system_fingerprint" response parameter to monitor changes in the backend.
   */
  seed?: string | null;
  /**
   * Controls which (if any) function is called by the model.
   * "none" means the model will not call a function and instead generates a message.
   * "auto" means the model can pick between generating a message or calling a function.
   * Specifying a particular function via {"type: "function", "function": {"name": "my_function"}} forces the model to call that function.
   *
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10000
   */
  toolChoice?: string | null;
  /**
   * A list of tools the model may call. Currently, only functions are supported as a tool. Use this to provide a list of functions the model may generate JSON inputs for.
   * @maxSize 1000
   */
  tools?: CreateChatCompletionRequestTool[];
  /**
   * An object specifying the format that the model must output. Compatible with gpt-4-1106-preview and gpt-3.5-turbo-1106.
   * Setting to type to "json_object" enables JSON mode, which guarantees the message the model generates is valid JSON.
   * Important: when using JSON mode, you must also instruct the model to produce JSON yourself via a system or user message.
   * Without this, the model may generate an unending stream of whitespace until the generation reaches the token limit,
   * resulting in a long-running and seemingly "stuck" request. Also note that the message content may be partially cut off if finish_reason="length",
   * which indicates the generation exceeded max_tokens or the conversation exceeded the max context length.
   */
  responseFormat?: CreateChatCompletionRequestResponseFormat;
  /**
   * An upper bound for the number of tokens that can be generated for a completion,
   * including visible output tokens and reasoning tokens.
   */
  maxCompletionTokens?: number | null;
  /** Whether to enable parallel function calling during tool use. */
  parallelToolCalls?: boolean | null;
}

/** @oneof */
export interface CreateChatCompletionRequestFunctionCallOneOf {
  /** Specifying a particular function via {"name":\ "my_function"} forces the model to call that function. */
  forceCallFunctionCallConfig?: Record<string, any> | null;
  /**
   * "none" means the model does not call a function, and responds to the end-user.
   * "auto" means the model can pick between an end-user or calling a function.
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10
   */
  defaultFunctionCallConfig?: string | null;
}

export interface FunctionSignature {
  /**
   * The name of the function to be called.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The description of what the function does.
   * @maxLength 100000
   */
  description?: string | null;
  /** The parameters the functions accepts, described as a JSON Schema object. */
  parameters?: Record<string, any> | null;
  /** If true, the model will strictly follow the function parameters schema (a.k.a. open-ai structured outputs). */
  strict?: boolean | null;
}

export interface CreateChatCompletionRequestTool {
  /**
   * The type of the tool. Currently, only "function" is supported.
   * @maxLength 100
   */
  type?: string;
  /** Function definition object. */
  function?: FunctionSignature;
}

export interface CreateChatCompletionRequestResponseFormat {
  /**
   * Must be one of text, json_object or json_schema.
   * @maxLength 100
   */
  type?: string | null;
  /** The schema object describes the output object for the model. Currently, only the JSON Schema Object is supported. */
  schema?: Record<string, any> | null;
}

export interface GenerateContentRequest {
  /** ID of the model to use. */
  model?: GoogleproxyV1ModelWithLiterals;
  /**
   * The content of the current conversation with the model.
   * @maxSize 1000
   */
  contents?: Content[];
  /** The system instruction to the model. */
  systemInstruction?: SystemInstruction;
  /**
   * A list of Tools the model may use to generate the next response.
   * @maxSize 1000
   */
  tools?: GoogleproxyV1Tool[];
  /**
   * Per request settings for blocking unsafe content. Enforced on GenerateContentResponse.candidates.
   * @maxSize 100
   */
  safetySettings?: SafetySetting[];
  /** The generation configuration for the response. */
  generationConfig?: GenerationConfig;
  /** Tool configuration for any Tool specified in the request. */
  toolConfig?: V1ToolConfig;
  /** If present, describes the fine-tuning model that will be called instead of generic one. */
  fineTuningSpec?: FineTuningSpec;
}

export enum GoogleproxyV1Model {
  UNKNOWN_MODEL = 'UNKNOWN_MODEL',
  GEMINI_1_0_PRO = 'GEMINI_1_0_PRO',
  GEMINI_1_0_PRO_VISION = 'GEMINI_1_0_PRO_VISION',
  GEMINI_1_5_PRO = 'GEMINI_1_5_PRO',
  GEMINI_1_5_FLASH = 'GEMINI_1_5_FLASH',
  GEMINI_2_0_FLASH = 'GEMINI_2_0_FLASH',
  GEMINI_2_0_FLASH_LITE = 'GEMINI_2_0_FLASH_LITE',
  GEMINI_2_5_PRO = 'GEMINI_2_5_PRO',
  GEMINI_2_5_FLASH = 'GEMINI_2_5_FLASH',
  GEMINI_2_5_FLASH_LITE = 'GEMINI_2_5_FLASH_LITE',
  GEMINI_2_5_FLASH_IMAGE = 'GEMINI_2_5_FLASH_IMAGE',
  GEMINI_2_5_COMPUTER_USE = 'GEMINI_2_5_COMPUTER_USE',
  GEMINI_3_0_PRO = 'GEMINI_3_0_PRO',
  GEMINI_3_0_PRO_IMAGE = 'GEMINI_3_0_PRO_IMAGE',
  GEMINI_3_0_FLASH = 'GEMINI_3_0_FLASH',
  GEMINI_3_1_PRO = 'GEMINI_3_1_PRO',
}

/** @enumType */
export type GoogleproxyV1ModelWithLiterals =
  | GoogleproxyV1Model
  | 'UNKNOWN_MODEL'
  | 'GEMINI_1_0_PRO'
  | 'GEMINI_1_0_PRO_VISION'
  | 'GEMINI_1_5_PRO'
  | 'GEMINI_1_5_FLASH'
  | 'GEMINI_2_0_FLASH'
  | 'GEMINI_2_0_FLASH_LITE'
  | 'GEMINI_2_5_PRO'
  | 'GEMINI_2_5_FLASH'
  | 'GEMINI_2_5_FLASH_LITE'
  | 'GEMINI_2_5_FLASH_IMAGE'
  | 'GEMINI_2_5_COMPUTER_USE'
  | 'GEMINI_3_0_PRO'
  | 'GEMINI_3_0_PRO_IMAGE'
  | 'GEMINI_3_0_FLASH'
  | 'GEMINI_3_1_PRO';

export interface Content {
  /**
   * The role in a conversation associated with the content.
   * Specifying a role is required even in single turn use cases. Acceptable values include the following:
   * USER: Specifies content that's sent by you. MODEL: Specifies the model's response.
   */
  role?: ContentRoleWithLiterals;
  /**
   * Ordered parts that make up the input. Parts may have different MIME types.
   * For gemini-1.0-pro, only the text field is valid. The token limit is 32k.
   * For gemini-1.0-pro-vision, you may specify either text only, text and up to 16 images, or text and 1 video. The token limit is 16k.
   * @maxSize 1000
   */
  parts?: V1ContentPart[];
}

export enum ContentRole {
  UNKNOWN_CONTENT_ROLE = 'UNKNOWN_CONTENT_ROLE',
  USER = 'USER',
  MODEL = 'MODEL',
}

/** @enumType */
export type ContentRoleWithLiterals =
  | ContentRole
  | 'UNKNOWN_CONTENT_ROLE'
  | 'USER'
  | 'MODEL';

export interface V1ContentPart {
  /**
   * Union field data can be only one of the following:
   * The text instructions or chat dialogue to include in the prompt.
   * @maxLength 1000000000
   */
  text?: string | null;
  /** data field not supported for gemini-1.0-pro */
  contentData?: ContentData;
  /** A predicted FunctionCall returned from the model that contains a string representing the FunctionDeclaration.name with the arguments and their values. */
  functionCall?: FunctionCall;
  /**
   * The result output from a FunctionCall that contains a string representing the FunctionDeclaration.name and a structured JSON object containing any output from the
   * function is used as context to the model. This should contain the result of aFunctionCall made based on model prediction.
   */
  functionResponse?: FunctionResponse;
  /**
   * Code generated by the model that is meant to be executed, and the result returned to the model.
   * Only generated when using the CodeExecution tool, in which the code will be automatically executed, and a corresponding CodeExecutionResult will also be generated.
   */
  executableCode?: ExecutableCode;
  /**
   * Result of executing the ExecutableCode.
   * Only generated when using the CodeExecution, and always follows a part containing the ExecutableCode.
   */
  codeExecutionResult?: V1CodeExecutionResult;
  /** Inline media bytes. */
  inlineData?: Blob;
  /** Optional. Media resolution level for the input media. */
  mediaResolution?: MediaResolution;
  /** Thought flag indicates that the content part is a thought. */
  thought?: boolean | null;
  /**
   * Optional. An opaque signature for the thought so it can be reused in subsequent requests. A base64-encoded string.
   * @maxLength 10000000
   */
  thoughtSignature?: string | null;
}

export interface ContentData {
  /**
   * The URL must be a valid wix mp or wix static URL.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * The MIME type of the content data. supported types are image/jpeg, image/png.
   * @maxLength 100
   */
  mimeType?: string | null;
}

export interface FunctionResponse {
  /**
   * Required. The name of the function to call. Must be a-z, A-Z, 0-9, or contain underscores and dashes, with a maximum length of 63.
   * @maxLength 64
   */
  name?: string;
  /** Required. The function response in JSON object format. */
  response?: Record<string, any> | null;
  /**
   * Optional. Ordered Parts that constitute a function response. Parts may have different IANA MIME types.
   * @maxSize 1000
   */
  parts?: FunctionResponsePart[];
}

export interface FunctionResponsePart extends FunctionResponsePartDataOneOf {
  /** Inline media bytes for function response. */
  inlineData?: FunctionResponseBlob;
}

/** @oneof */
export interface FunctionResponsePartDataOneOf {
  /** Inline media bytes for function response. */
  inlineData?: FunctionResponseBlob;
}

export interface FunctionResponseBlob {
  /**
   * The IANA standard MIME type of the source data.
   * @maxLength 100
   */
  mimeType?: string;
  /**
   * Raw bytes for media formats. Will be fetched from the passed URL in request, and uploaded to WixMP URL in response.
   * @format WEB_URL
   */
  data?: string;
}

export interface MediaResolution {
  /** Media resolution level */
  level?: MediaResolutionLevelWithLiterals;
}

export enum MediaResolutionLevel {
  /** Media resolution has not been set. */
  MEDIA_RESOLUTION_UNSPECIFIED = 'MEDIA_RESOLUTION_UNSPECIFIED',
  /** Media resolution set to low (64 tokens). */
  MEDIA_RESOLUTION_LOW = 'MEDIA_RESOLUTION_LOW',
  /** Media resolution set to medium (256 tokens). */
  MEDIA_RESOLUTION_MEDIUM = 'MEDIA_RESOLUTION_MEDIUM',
  /** Media resolution set to high (zoomed re-framing with 256 tokens). */
  MEDIA_RESOLUTION_HIGH = 'MEDIA_RESOLUTION_HIGH',
  /** Media resolution set to ultra high (2240 tokens for images). */
  MEDIA_RESOLUTION_ULTRA_HIGH = 'MEDIA_RESOLUTION_ULTRA_HIGH',
}

/** @enumType */
export type MediaResolutionLevelWithLiterals =
  | MediaResolutionLevel
  | 'MEDIA_RESOLUTION_UNSPECIFIED'
  | 'MEDIA_RESOLUTION_LOW'
  | 'MEDIA_RESOLUTION_MEDIUM'
  | 'MEDIA_RESOLUTION_HIGH'
  | 'MEDIA_RESOLUTION_ULTRA_HIGH';

export interface SystemInstruction {
  /**
   * The role field of systemInstruction is ignored and doesn't affect the performance of the model.
   * @maxLength 20
   */
  role?: string | null;
  /**
   * Instructions for the model to steer it toward better performance.
   * The text strings count toward the token limit.
   * @maxSize 10
   */
  parts?: V1ContentPart[];
}

export interface GoogleproxyV1Tool {
  /**
   * One or more function declarations
   * More information about the function declarations :
   * https://cloud.google.com/vertex-ai/generative-ai/docs/multimodal/function-calling
   * @maxSize 1000
   */
  functionDeclarations?: FunctionDeclaration[];
  /** Optional. Retrieval tool that is powered by Google search. */
  googleSearchRetrieval?: GoogleSearchRetrieval;
  /** Optional. Enables the model to execute code as part of generation. */
  codeExecution?: CodeExecution;
  /** Optional. GoogleSearch tool type. Tool to support Google Search in Model. Powered by Google. */
  googleSearch?: GoogleSearch;
  /**
   * Optional. Tool to support the model interacting directly with the computer.
   * If enabled, it automatically populates computer-use specific Function Declarations.
   */
  computerUse?: ComputerUse;
}

export enum DynamicRetrievalConfigMode {
  /** Always trigger retrieval. */
  MODE_UNSPECIFIED = 'MODE_UNSPECIFIED',
  /** Run retrieval only when system decides it is necessary. */
  MODE_DYNAMIC = 'MODE_DYNAMIC',
}

/** @enumType */
export type DynamicRetrievalConfigModeWithLiterals =
  | DynamicRetrievalConfigMode
  | 'MODE_UNSPECIFIED'
  | 'MODE_DYNAMIC';

export interface DynamicRetrievalConfig {
  /** The mode of the predictor to be used in dynamic retrieval. */
  mode?: DynamicRetrievalConfigModeWithLiterals;
  /** The threshold to be used in dynamic retrieval. If not set, a system default value is used. */
  dynamicThreshold?: string | null;
}

export enum Environment {
  /** Defaults to browser. */
  ENVIRONMENT_UNSPECIFIED = 'ENVIRONMENT_UNSPECIFIED',
  /** Operates in a web browser. */
  ENVIRONMENT_BROWSER = 'ENVIRONMENT_BROWSER',
}

/** @enumType */
export type EnvironmentWithLiterals =
  | Environment
  | 'ENVIRONMENT_UNSPECIFIED'
  | 'ENVIRONMENT_BROWSER';

export interface FunctionDeclaration {
  /**
   * The name of the function to call. Must start with a letter or an underscore.
   * Must be a-z, A-Z, 0-9, or contain underscores and dashes, with a maximum length of 64.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The description and purpose of the function. The model uses this to decide how and whether to call the function.
   * For the best results, we recommend that you include a description.
   * @maxLength 100000
   */
  description?: string | null;
  /**
   * The parameters of this function in a format that's compatible with the OpenAPI
   * https://spec.opensoapis.org/oas/v3.0.3#schema
   */
  parameters?: Record<string, any> | null;
}

export interface GoogleSearchRetrieval {
  /** Specifies the dynamic retrieval configuration for the given source. */
  dynamicRetrievalConfig?: DynamicRetrievalConfig;
}

export interface CodeExecution {}

export interface GoogleSearch {}

export interface ComputerUse {
  /** Required. The environment being operated. */
  environment?: EnvironmentWithLiterals;
  /**
   * Optional. By default, predefined functions are included in the final model call.
   * Some of them can be explicitly excluded from being automatically included.
   * This can serve two purposes:
   * 1. Using a more restricted / different action space.
   * 2. Improving the definitions / instructions of predefined functions.
   * @maxSize 100
   * @maxLength 1000
   */
  excludedPredefinedFunctions?: string[];
}

export interface SafetySetting {
  /** The safety category to configure a threshold for. */
  category?: HarmCategoryWithLiterals;
  /** The threshold for blocking responses that could belong to the specified safety category based on probability. */
  threshold?: ThresholdWithLiterals;
}

export enum Threshold {
  UNKNOWN_THRESHOLD = 'UNKNOWN_THRESHOLD',
  BLOCK_NONE = 'BLOCK_NONE',
  BLOCK_LOW_AND_ABOVE = 'BLOCK_LOW_AND_ABOVE',
  BLOCK_MED_AND_ABOVE = 'BLOCK_MED_AND_ABOVE',
  BLOCK_ONLY_HIGH = 'BLOCK_ONLY_HIGH',
}

/** @enumType */
export type ThresholdWithLiterals =
  | Threshold
  | 'UNKNOWN_THRESHOLD'
  | 'BLOCK_NONE'
  | 'BLOCK_LOW_AND_ABOVE'
  | 'BLOCK_MED_AND_ABOVE'
  | 'BLOCK_ONLY_HIGH';

export interface GenerationConfig {
  /**
   * The temperature is used for sampling during the response generation, which occurs when topP and topK are applied.
   * Temperature controls the degree of randomness in token selection.
   * Lower temperatures are good for prompts that require a more deterministic and less open-ended or creative response,
   * while higher temperatures can lead to more diverse or creative results. A temperature of 0 is deterministic:
   * the highest probability response is always selected.
   * Range: 0.0 - 1.0, Default for gemini-1.0-pro: 0.9, Default for gemini-1.0-pro-vision: 0.4
   * @max 1
   */
  temperature?: number | null;
  /**
   * Maximum number of tokens that can be generated in the response. A token is approximately four characters.
   * 100 tokens correspond to roughly 60-80 words.
   * Specify a lower value for shorter responses and a higher value for potentially longer responses.
   * Range for gemini-1.0-pro: 1-8192 (default: 8192),
   * Range for gemini-1.0-pro-vision: 1-2048 (default: 2048)
   * Range for gemini-2.5-pro: 1-65536
   * @min 1
   * @max 65536
   */
  maxOutputTokens?: string | null;
  /**
   * Top-K changes how the model selects tokens for output.
   * A top-K of 1 means the next selected token is the most probable among all tokens in the model's vocabulary (also called greedy decoding),
   * while a top-K of 3 means that the next token is selected from among the three most probable tokens by using temperature.
   * For each token selection step, the top-K tokens with the highest probabilities are sampled.
   * Then tokens are further filtered based on top-P with the final token selected using temperature sampling.
   * Specify a lower value for less random responses and a higher value for more random responses.
   * Default for gemini-1.0-pro-vision: 32, Default for gemini-1.0-pro: none
   * @min 1
   * @max 40
   */
  topK?: number | null;
  /**
   * Top-P changes how the model selects tokens for output.
   * Tokens are selected from the most (see top-K) to least probable until the sum of their probabilities equals the top-P value.
   * For example, if tokens A, B, and C have a probability of 0.3, 0.2, and 0.1 and the top-P value is 0.5,
   * then the model will select either A or B as the next token by using temperature and excludes C as a candidate.
   * Specify a lower value for less random responses and a higher value for more random responses.
   * Default: 1.0
   * @max 1
   */
  topP?: number | null;
  /**
   * The number of response variations to return.This value must be 1.
   * @min 1
   * @max 1
   */
  candidateCount?: number | null;
  /**
   * Specifies a list of strings that tells the model to stop generating text if one of the strings is encountered in the response.
   * If a string appears multiple times in the response, then the response truncates where it's first encountered. The strings are case-sensitive.
   * For example, if the following is the returned response when stopSequences isn't specified:
   * public static string reverse(string myString)
   * Then the returned response with stopSequences set to ["Str","reverse"] is:
   * public static string
   * Maximum 5 items in the list.
   * @maxSize 5
   * @maxLength 1000
   */
  stopSequences?: string[] | null;
  /**
   * Available for gemini-1.5-pro
   * The output format of the generated candidate text.
   * Supported MIME types: text/plain: (default) Text output. application/json: JSON response in the candidates.
   * text/x.enum: For classification tasks, output an enum value as defined in the response schema.
   * How to control the output format: https://cloud.google.com/vertex-ai/generative-ai/docs/multimodal/control-generated-output
   * @maxLength 50
   */
  responseMimeType?: string | null;
  /**
   * Available for gemini-1.5-pro.
   * The schema that generated candidate text must follow. For more information, see Control generated output.
   * You must specify the responseType or responseMimeType field to use this parameter.
   * Link for examples : https://cloud.google.com/vertex-ai/docs/reference/rest/v1/Schema
   */
  responseSchema?: Record<string, any> | null;
  /**
   * Optional. Output schema of the generated response. This is an alternative to responseSchema that accepts JSON Schema.
   * If set, responseSchema must be omitted, but responseMimeType is required.
   * While the full JSON Schema may be sent, not all features are supported.
   * more information about supported features and examples can be found here:
   * https://ai.google.dev/api/generate-content#FIELDS.response_json_schema
   */
  responseJsonSchema?: Record<string, any> | null;
  /** Optional. Config for thinking features. An error will be returned if this field is set for models that don't support thinking */
  thinkingConfig?: GenerationThinkingConfig;
  /**
   * Optional. The requested modalities of the response.
   * Represents the set of modalities that the model can return, and should be expected in the response.
   * This is an exact match to the modalities of the response.
   * A model may have multiple combinations of supported modalities.
   * If the requested modalities do not match any of the supported combinations, an error will be returned.
   * An empty list is equivalent to requesting only TEXT.
   * Currently supported as experimental feature for gemini-2.0-flash only.
   * @maxSize 5
   */
  responseModalities?: ModalityWithLiterals[];
  /**
   * Optional. Configuration for image generation.
   * This message allows you to control various aspects of image generation, such as the output format, aspect ratio, and whether the model can generate images of people.
   */
  imageConfig?: ImageConfig;
  /**
   * The media_resolution parameter controls how the Gemini API processes media inputs like images, videos,
   * and PDF documents by determining the maximum number of tokens allocated for media inputs,
   * allowing you to balance response quality against latency and cost.
   */
  mediaResolution?: MediaResolutionLevelWithLiterals;
}

export interface GenerationThinkingConfig {
  /** Indicates whether to include thoughts in the response. If true, thoughts are returned only when available. */
  includeThoughts?: boolean | null;
  /** The number of thoughts tokens that the model should generate. */
  thinkingBudget?: string | null;
  /**
   * Thinking level parameter offering 2 states:
   * Low: Minimizes latency and cost. Best for simple instruction following or chat.
   * High: Maximizes reasoning depth. Default. Dynamic thinking.
   * The model may take significantly longer to reach a first token,
   * but the output will be more thoroughly vetted.
   * Note: You cannot use both thinking_level and the legacy thinking_budget parameter in the same request. Doing so will return a 400 error
   * @maxLength 20
   */
  thinkingLevel?: string | null;
}

export interface ImageConfig {
  /** Optional. The image output format for generated images. */
  imageOutputOptions?: ImageOutputOptions;
  /**
   * Optional. The desired aspect ratio for the generated images. The following aspect ratios are supported:
   * "1:1" "2:3", "3:2" "3:4", "4:3" "4:5", "5:4" "9:16", "16:9" "21:9"
   * @maxLength 10
   */
  aspectRatio?: string | null;
  /** Optional. Controls whether the model can generate people. */
  personGeneration?: PersonGenerationWithLiterals;
}

export interface ImageOutputOptions {
  /**
   * Optional. The image format that the output should be saved as.
   * @maxLength 100
   */
  mimeType?: string | null;
  /** Optional. The compression quality of the output image. */
  compressionQuality?: string | null;
}

export enum PersonGeneration {
  /** The default behavior is unspecified. The model will decide whether to generate images of people. */
  PERSON_GENERATION_UNSPECIFIED = 'PERSON_GENERATION_UNSPECIFIED',
  /** Allows the model to generate images of people, including adults and children. */
  ALLOW_ALL = 'ALLOW_ALL',
  /** Allows the model to generate images of adults, but not children. */
  ALLOW_ADULT = 'ALLOW_ADULT',
  /** Prevents the model from generating images of people. */
  ALLOW_NONE = 'ALLOW_NONE',
}

/** @enumType */
export type PersonGenerationWithLiterals =
  | PersonGeneration
  | 'PERSON_GENERATION_UNSPECIFIED'
  | 'ALLOW_ALL'
  | 'ALLOW_ADULT'
  | 'ALLOW_NONE';

export interface V1ToolConfig {
  /** Function calling config. */
  functionCallingConfig?: FunctionCallingConfig;
}

export interface FunctionCallingConfig {
  /** Specifies the mode in which function calling should execute. If unspecified, the default value will be set to AUTO. */
  mode?: ModeWithLiterals;
  /**
   * A set of function names that, when provided, limits the functions the model will call.
   * This should only be set when the Mode is ANY or VALIDATED. Function names should match [FunctionDeclaration.name]. When set, model will predict a function call from only allowed function names.
   * @maxLength 64
   * @maxSize 100
   */
  allowedFunctionNames?: string[];
}

export enum Mode {
  UNKNOWN = 'UNKNOWN',
  /** Default model behavior, model decides to predict either a function call or a natural language response. */
  AUTO = 'AUTO',
  /**
   * Model is constrained to always predicting a function call only. If "allowedFunctionNames" are set, the predicted function call will be limited to any one of "allowedFunctionNames",
   * else the predicted function call will be any one of the provided "functionDeclarations".
   */
  ANY = 'ANY',
  /** Model will not predict any function call. Model behavior is same as when not passing any function declarations. */
  NONE = 'NONE',
  /**
   * Model decides to predict either a function call or a natural language response, but will validate function calls with constrained decoding. If "allowedFunctionNames" are set, the predicted function call will be
   * limited to any one of "allowedFunctionNames", else the predicted function call will be any one of the provided "functionDeclarations".
   */
  VALIDATED = 'VALIDATED',
}

/** @enumType */
export type ModeWithLiterals =
  | Mode
  | 'UNKNOWN'
  | 'AUTO'
  | 'ANY'
  | 'NONE'
  | 'VALIDATED';

export interface FineTuningSpec {
  /**
   * Endpoint ID of the fine-tuning model to use.
   * @maxLength 100
   */
  id?: string | null;
}

export interface InvokeAnthropicClaudeModelRequest {
  /** The unique identifier of the model to invoke to run inference. */
  model?: ModelWithLiterals;
  /**
   * Each input message content may be either a single string or an array of content blocks.
   * @maxSize 4096
   */
  messages?: AnthropicClaudeMessage[];
  /**
   * System prompt.
   * @maxLength 1000000000
   * @deprecated System prompt.
   * @replacedBy system_prompt
   * @targetRemovalDate 2025-10-01
   */
  system?: string | null;
  /**
   * System prompt.
   * @maxSize 4096
   */
  systemPrompt?: Text[];
  /**
   * The maximum number of tokens to generate before stopping.
   * Defaults to 1024.
   * @min 1
   */
  maxTokens?: number | null;
  /**
   * Custom text sequences that will cause the model to stop generating.
   * @maxLength 512
   * @maxSize 8191
   */
  stopSequences?: string[];
  /**
   * Amount of randomness injected into the response.
   * Defaults to 1.0.
   * Use temperature closer to 0.0 for analytical / multiple choice, and closer to 1.0 for creative and generative tasks.
   * @max 1
   */
  temperature?: number | null;
  /**
   * Only sample from the top K options for each subsequent token.
   * Use top_k to remove long tail low probability responses.
   * @max 500
   */
  topK?: number | null;
  /**
   * In nucleus sampling, Anthropic Claude computes the cumulative distribution over all the options for each subsequent token in decreasing probability order and cuts it off once it reaches a particular probability specified by top_p.
   * You should alter either temperature or top_p, but not both.
   * @max 1
   */
  topP?: number | null;
  /**
   * Definitions of tools that the model may use.
   * If you include tools in your API request, the model may return tool_use content blocks that represent the model's
   * use of those tools. You can then run those tools using the tool input generated by the model and then optionally
   * return results back to the model using tool_result content blocks.
   * @maxSize 1000
   */
  tools?: Tool[];
  /**
   * How the model should use the provided tools. The model can use a specific tool, any available tool, or decide by itself.
   * More info: https://docs.anthropic.com/en/docs/build-with-claude/tool-use#forcing-tool-use
   */
  toolChoice?: ToolChoice;
  /**
   * Configuration for enabling Claude's extended thinking.
   * When enabled, responses include thinking content blocks showing Claude's thinking process before the final answer.
   * Requires a minimum budget of 1,024 tokens and counts towards your max_tokens limit.
   */
  thinking?: ThinkingConfig;
  /**
   * MCP servers to be utilized in this request
   * @maxSize 100
   */
  mcpServers?: McpServer[];
  /** Desired output format. */
  outputFormat?: Record<string, any> | null;
}

export interface InputSchema {
  /**
   * Available options: object
   * @maxLength 100
   */
  type?: string | null;
  /** Object that defines JSON schema itself. */
  properties?: Record<string, any> | null;
  /**
   * List of required parameters from JSON schema.
   * @maxSize 4096
   * @maxLength 1000
   */
  required?: string[];
}

export enum Model {
  UNKNOWN = 'UNKNOWN',
  /** anthropic.claude-3-sonnet-20240229-v1:0 */
  CLAUDE_3_SONNET_1_0 = 'CLAUDE_3_SONNET_1_0',
  /** anthropic.claude-3-haiku-20240307-v1:0 */
  CLAUDE_3_HAIKU_1_0 = 'CLAUDE_3_HAIKU_1_0',
  /** anthropic.claude-3-5-sonnet-20240620-v1:0 */
  CLAUDE_3_5_SONNET_1_0 = 'CLAUDE_3_5_SONNET_1_0',
  /** anthropic.claude-3-5-sonnet-20241022-v2:0 */
  CLAUDE_3_5_SONNET_2_0 = 'CLAUDE_3_5_SONNET_2_0',
  /** us.anthropic.claude-3-5-haiku-20241022-v1:0 */
  CLAUDE_3_5_HAIKU_1_0 = 'CLAUDE_3_5_HAIKU_1_0',
  /** us.anthropic.claude-3-7-sonnet-20250219-v1:0 */
  CLAUDE_3_7_SONNET_1_0 = 'CLAUDE_3_7_SONNET_1_0',
  CLAUDE_4_SONNET_1_0 = 'CLAUDE_4_SONNET_1_0',
  CLAUDE_4_OPUS_1_0 = 'CLAUDE_4_OPUS_1_0',
  /** us.anthropic.claude-sonnet-4-5-20250929-v1:0 */
  CLAUDE_4_5_SONNET_1_0 = 'CLAUDE_4_5_SONNET_1_0',
  /** us.anthropic.claude-haiku-4-5-20251001-v1:0 */
  CLAUDE_4_5_HAIKU_1_0 = 'CLAUDE_4_5_HAIKU_1_0',
  CLAUDE_4_5_OPUS_1_0 = 'CLAUDE_4_5_OPUS_1_0',
  CLAUDE_4_6_OPUS_1_0 = 'CLAUDE_4_6_OPUS_1_0',
  CLAUDE_4_6_SONNET_1_0 = 'CLAUDE_4_6_SONNET_1_0',
}

/** @enumType */
export type ModelWithLiterals =
  | Model
  | 'UNKNOWN'
  | 'CLAUDE_3_SONNET_1_0'
  | 'CLAUDE_3_HAIKU_1_0'
  | 'CLAUDE_3_5_SONNET_1_0'
  | 'CLAUDE_3_5_SONNET_2_0'
  | 'CLAUDE_3_5_HAIKU_1_0'
  | 'CLAUDE_3_7_SONNET_1_0'
  | 'CLAUDE_4_SONNET_1_0'
  | 'CLAUDE_4_OPUS_1_0'
  | 'CLAUDE_4_5_SONNET_1_0'
  | 'CLAUDE_4_5_HAIKU_1_0'
  | 'CLAUDE_4_5_OPUS_1_0'
  | 'CLAUDE_4_6_OPUS_1_0'
  | 'CLAUDE_4_6_SONNET_1_0';

export interface AnthropicClaudeMessage {
  /** The role of the message author. */
  role?: RoleWithLiterals;
  /**
   * The content of the message.
   * @maxSize 4096
   */
  content?: ContentBlock[];
}

export interface Tool {
  /**
   * Description of what this tool does.
   * Tool descriptions should be as detailed as possible. The more information that the model has about what the tool
   * is and how to use it, the better it will perform. You can use natural language descriptions to reinforce
   * important aspects of the tool input JSON schema.
   * @maxLength 1000000000
   */
  description?: string | null;
  /**
   * Tool's name
   * @maxLength 1000
   */
  name?: string | null;
  /**
   * JSON schema for this tool's input.
   * This defines the shape of the input that your tool accepts and that the model will produce.
   */
  inputSchema?: InputSchema;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: CacheControl;
  /**
   * Tool type for Claude built-in tools
   * @maxLength 100000
   */
  type?: string | null;
  /** Maximum uses of a tool allowed to the model. Currently used only by `web_search` */
  maxUses?: number | null;
}

export interface ToolChoice {
  /**
   * AUTO allows Claude to decide whether to call any provided tools or not. This is the default value.
   * ANY  tells Claude that it must use one of the provided tools, but doesn’t force a particular tool.
   * TOOL allows us to force Claude to always use a particular tool.
   */
  type?: ToolChoiceTypeWithLiterals;
  /**
   * The name of the tool to use in case Type is TOOL.
   * @maxLength 1000
   */
  name?: string | null;
}

export enum ToolChoiceType {
  UNKNOWN = 'UNKNOWN',
  AUTO = 'AUTO',
  ANY = 'ANY',
  TOOL = 'TOOL',
}

/** @enumType */
export type ToolChoiceTypeWithLiterals =
  | ToolChoiceType
  | 'UNKNOWN'
  | 'AUTO'
  | 'ANY'
  | 'TOOL';

export interface ThinkingConfig {
  /**
   * Determines how many tokens Claude can use for its internal reasoning process. Larger budgets can enable more thorough
   * analysis for complex problems, improving response quality.
   * Must be ≥1024 and less than max_tokens.
   */
  budgetTokens?: number;
  /**
   * When enabled, responses include thinking content blocks showing Claude's thinking process before the final answer.
   * Requires a minimum budget of 1,024 tokens and counts towards your max_tokens limit.
   * Defaults to "enabled" in the mapper if unset to preserve legacy behavior.
   */
  enabled?: boolean | null;
}

export interface McpServer {
  /**
   * McpServer name
   * @maxLength 1000
   */
  name?: string;
  /** Available options: url */
  type?: McpServerTypeWithLiterals;
  /**
   * McpServer url
   * @maxLength 10000
   */
  url?: string | null;
  /** Tool configuration */
  toolConfiguration?: ToolConfiguration;
}

export enum McpServerType {
  UNKNOWN = 'UNKNOWN',
  URL = 'URL',
}

/** @enumType */
export type McpServerTypeWithLiterals = McpServerType | 'UNKNOWN' | 'URL';

export interface ToolConfiguration {
  /**
   * Allowed tools
   * @maxLength 1000
   * @maxSize 100
   */
  allowedTools?: string[];
  /** Enabled */
  enabled?: boolean | null;
}

export interface V1InvokeAnthropicClaudeModelRequest {
  /** The unique identifier of the model to invoke to run inference. */
  model?: ClaudeModelWithLiterals;
  /**
   * Each input message content may be either a single string or an array of content blocks.
   * @maxSize 4096
   */
  messages?: V1AnthropicClaudeMessage[];
  /**
   * System prompt.
   * @maxLength 1000000000
   * @deprecated System prompt.
   * @replacedBy system_prompt
   * @targetRemovalDate 2025-10-01
   */
  system?: string | null;
  /**
   * System prompt.
   * @maxSize 4096
   */
  systemPrompt?: GoogleproxyV1Text[];
  /**
   * The maximum number of tokens to generate before stopping.
   * Defaults to 1024.
   * @min 1
   */
  maxTokens?: number | null;
  /**
   * Custom text sequences that will cause the model to stop generating.
   * @maxLength 512
   * @maxSize 8191
   */
  stopSequences?: string[];
  /**
   * Amount of randomness injected into the response.
   * Defaults to 1.0.
   * Use temperature closer to 0.0 for analytical / multiple choice, and closer to 1.0 for creative and generative tasks.
   * @max 1
   */
  temperature?: number | null;
  /**
   * Only sample from the top K options for each subsequent token.
   * Use top_k to remove long tail low probability responses.
   * @max 500
   */
  topK?: number | null;
  /**
   * In nucleus sampling, Anthropic Claude computes the cumulative distribution over all the options for each subsequent token in decreasing probability order and cuts it off once it reaches a particular probability specified by top_p.
   * You should alter either temperature or top_p, but not both.
   * @max 1
   */
  topP?: number | null;
  /**
   * Definitions of tools that the model may use.
   * If you include tools in your API request, the model may return tool_use content blocks that represent the model's
   * use of those tools. You can then run those tools using the tool input generated by the model and then optionally
   * return results back to the model using tool_result content blocks.
   * @maxSize 1000
   */
  tools?: InvokeAnthropicClaudeModelRequestTool[];
  /**
   * How the model should use the provided tools. The model can use a specific tool, any available tool, or decide by itself.
   * More info: https://docs.anthropic.com/en/docs/build-with-claude/tool-use#forcing-tool-use
   */
  toolChoice?: GoogleproxyV1ToolChoice;
  /**
   * Configuration for enabling Claude's extended thinking.
   * When enabled, responses include thinking content blocks showing Claude's thinking process before the final answer.
   * Requires a minimum budget of 1,024 tokens and counts towards your max_tokens limit.
   */
  thinking?: GoogleproxyV1ThinkingConfig;
  /**
   * MCP servers to be utilized in this request
   * @maxSize 100
   */
  mcpServers?: GoogleproxyV1McpServer[];
  /** Desired output format. */
  outputFormat?: Record<string, any> | null;
}

export interface GoogleproxyV1InputSchema {
  /**
   * Available options: object
   * @maxLength 100
   */
  type?: string | null;
  /** Object that defines JSON schema itself. */
  properties?: Record<string, any> | null;
  /**
   * List of required parameters from JSON schema.
   * @maxSize 4096
   * @maxLength 1000
   */
  required?: string[];
}

export enum ClaudeModel {
  UNKNOWN_CLAUDE_MODEL = 'UNKNOWN_CLAUDE_MODEL',
  CLAUDE_3_SONNET_1_0 = 'CLAUDE_3_SONNET_1_0',
  CLAUDE_3_HAIKU_1_0 = 'CLAUDE_3_HAIKU_1_0',
  CLAUDE_3_OPUS_1_0 = 'CLAUDE_3_OPUS_1_0',
  CLAUDE_3_5_SONNET_1_0 = 'CLAUDE_3_5_SONNET_1_0',
  CLAUDE_3_5_SONNET_2_0 = 'CLAUDE_3_5_SONNET_2_0',
  CLAUDE_3_7_SONNET_1_0 = 'CLAUDE_3_7_SONNET_1_0',
  CLAUDE_4_SONNET_1_0 = 'CLAUDE_4_SONNET_1_0',
  CLAUDE_4_OPUS_1_0 = 'CLAUDE_4_OPUS_1_0',
  CLAUDE_4_5_SONNET_1_0 = 'CLAUDE_4_5_SONNET_1_0',
  CLAUDE_4_5_HAIKU_1_0 = 'CLAUDE_4_5_HAIKU_1_0',
  CLAUDE_4_5_OPUS_1_0 = 'CLAUDE_4_5_OPUS_1_0',
  CLAUDE_4_6_OPUS_1_0 = 'CLAUDE_4_6_OPUS_1_0',
  CLAUDE_4_6_SONNET_1_0 = 'CLAUDE_4_6_SONNET_1_0',
}

/** @enumType */
export type ClaudeModelWithLiterals =
  | ClaudeModel
  | 'UNKNOWN_CLAUDE_MODEL'
  | 'CLAUDE_3_SONNET_1_0'
  | 'CLAUDE_3_HAIKU_1_0'
  | 'CLAUDE_3_OPUS_1_0'
  | 'CLAUDE_3_5_SONNET_1_0'
  | 'CLAUDE_3_5_SONNET_2_0'
  | 'CLAUDE_3_7_SONNET_1_0'
  | 'CLAUDE_4_SONNET_1_0'
  | 'CLAUDE_4_OPUS_1_0'
  | 'CLAUDE_4_5_SONNET_1_0'
  | 'CLAUDE_4_5_HAIKU_1_0'
  | 'CLAUDE_4_5_OPUS_1_0'
  | 'CLAUDE_4_6_OPUS_1_0'
  | 'CLAUDE_4_6_SONNET_1_0';

export interface V1AnthropicClaudeMessage {
  /** The role of the message author. */
  role?: V1MessageRoleRoleWithLiterals;
  /**
   * The content of the message.
   * @maxSize 4096
   */
  content?: GoogleproxyV1ContentBlock[];
}

export interface InvokeAnthropicClaudeModelRequestTool {
  /**
   * Description of what this tool does.
   * Tool descriptions should be as detailed as possible. The more information that the model has about what the tool
   * is and how to use it, the better it will perform. You can use natural language descriptions to reinforce
   * important aspects of the tool input JSON schema.
   * @maxLength 1000000000
   */
  description?: string | null;
  /**
   * Tool's name
   * @maxLength 1000
   */
  name?: string | null;
  /**
   * JSON schema for this tool's input.
   * This defines the shape of the input that your tool accepts and that the model will produce.
   */
  inputSchema?: GoogleproxyV1InputSchema;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: GoogleproxyV1CacheControl;
  /**
   * Tool type for Claude built-in tools
   * @maxLength 100000
   */
  type?: string | null;
  /** Maximum uses of a tool allowed to the model. Currently used only by `web_search` */
  maxUses?: number | null;
}

export interface GoogleproxyV1ToolChoice {
  /**
   * AUTO allows Claude to decide whether to call any provided tools or not. This is the default value.
   * ANY  tells Claude that it must use one of the provided tools, but doesn’t force a particular tool.
   * TOOL allows us to force Claude to always use a particular tool.
   */
  type?: GoogleproxyV1ToolChoiceTypeWithLiterals;
  /**
   * The name of the tool to use in case Type is TOOL.
   * @maxLength 1000
   */
  name?: string | null;
  /**
   * Whether to disable parallel tool use.
   * Defaults to false.
   * If set to true, the model will output at most one tool use (if Type is AUTO) or exactly one tool use (if Type is ANY or TOOL)
   */
  disableParallelToolUse?: boolean | null;
}

export enum GoogleproxyV1ToolChoiceType {
  UNKNOWN = 'UNKNOWN',
  AUTO = 'AUTO',
  ANY = 'ANY',
  TOOL = 'TOOL',
}

/** @enumType */
export type GoogleproxyV1ToolChoiceTypeWithLiterals =
  | GoogleproxyV1ToolChoiceType
  | 'UNKNOWN'
  | 'AUTO'
  | 'ANY'
  | 'TOOL';

export interface GoogleproxyV1ThinkingConfig {
  /**
   * Determines how many tokens Claude can use for its internal reasoning process. Larger budgets can enable more thorough
   * analysis for complex problems, improving response quality.
   * Must be ≥1024 and less than max_tokens.
   */
  budgetTokens?: number;
  /**
   * When enabled, responses include thinking content blocks showing Claude's thinking process before the final answer.
   * Requires a minimum budget of 1,024 tokens and counts towards your max_tokens limit.
   * Defaults to "enabled" in the mapper if unset to preserve legacy behavior.
   */
  enabled?: boolean | null;
}

export interface GoogleproxyV1McpServer {
  /**
   * McpServer name
   * @maxLength 1000
   */
  name?: string;
  /** Available options: url */
  type?: GoogleproxyV1McpServerTypeWithLiterals;
  /**
   * McpServer url
   * @maxLength 10000
   */
  url?: string | null;
  /** Tool configuration */
  toolConfiguration?: V1McpServerToolConfiguration;
}

export enum GoogleproxyV1McpServerType {
  UNKNOWN = 'UNKNOWN',
  URL = 'URL',
}

/** @enumType */
export type GoogleproxyV1McpServerTypeWithLiterals =
  | GoogleproxyV1McpServerType
  | 'UNKNOWN'
  | 'URL';

export interface V1McpServerToolConfiguration {
  /**
   * Allowed tools
   * @maxLength 1000
   * @maxSize 100
   */
  allowedTools?: string[];
  /** Enabled */
  enabled?: boolean | null;
}

export interface InvokeAnthropicModelRequest {
  /** The unique identifier of the model to invoke to run inference. */
  model?: AnthropicModelWithLiterals;
  /**
   * Each input message content may be either a single string or an array of content blocks.
   * @maxSize 4096
   */
  messages?: AnthropicMessage[];
  /**
   * System prompt.
   * @maxSize 4096
   */
  systemPrompt?: V1Text[];
  /**
   * The maximum number of tokens to generate before stopping.
   * Defaults to 1024.
   * @min 1
   */
  maxTokens?: number | null;
  /**
   * Custom text sequences that will cause the model to stop generating.
   * @maxLength 512
   * @maxSize 8191
   */
  stopSequences?: string[];
  /**
   * Amount of randomness injected into the response.
   * Defaults to 1.0.
   * Use temperature closer to 0.0 for analytical / multiple choice, and closer to 1.0 for creative and generative tasks.
   * @max 1
   */
  temperature?: number | null;
  /**
   * Only sample from the top K options for each subsequent token.
   * Use top_k to remove long tail low probability responses.
   * @max 500
   */
  topK?: number | null;
  /**
   * In nucleus sampling, Anthropic Claude computes the cumulative distribution over all the options for each subsequent token in decreasing probability order and cuts it off once it reaches a particular probability specified by top_p.
   * You should alter either temperature or top_p, but not both.
   * @max 1
   */
  topP?: number | null;
  /**
   * Definitions of tools that the model may use.
   * If you include tools in your API request, the model may return tool_use content blocks that represent the model's
   * use of those tools. You can then run those tools using the tool input generated by the model and then optionally
   * return results back to the model using tool_result content blocks.
   * @maxSize 1000
   */
  tools?: V1Tool[];
  /**
   * How the model should use the provided tools. The model can use a specific tool, any available tool, or decide by itself.
   * More info: https://docs.anthropic.com/en/docs/build-with-claude/tool-use#forcing-tool-use
   */
  toolChoice?: V1ToolChoice;
  /**
   * Configuration for enabling Claude's extended thinking.
   * When enabled, responses include thinking content blocks showing Claude's thinking process before the final answer.
   * Requires a minimum budget of 1,024 tokens and counts towards your max_tokens limit.
   */
  thinking?: V1ThinkingConfig;
  /**
   * MCP servers to be utilized in this request
   * @maxSize 100
   */
  mcpServers?: V1McpServer[];
  /**
   * Container identifier for reuse across requests.
   * @maxLength 512
   */
  container?: string | null;
  /** An object describing metadata about the request. */
  metadata?: RequestMetadata;
  /** Desired output format. */
  outputFormat?: Record<string, any> | null;
}

export enum AnthropicModel {
  UNKNOWN_ANTHROPIC_MODEL = 'UNKNOWN_ANTHROPIC_MODEL',
  CLAUDE_3_HAIKU_1_0 = 'CLAUDE_3_HAIKU_1_0',
  CLAUDE_3_5_SONNET_1_0 = 'CLAUDE_3_5_SONNET_1_0',
  CLAUDE_3_5_SONNET_2_0 = 'CLAUDE_3_5_SONNET_2_0',
  CLAUDE_3_7_SONNET_1_0 = 'CLAUDE_3_7_SONNET_1_0',
  CLAUDE_4_SONNET_1_0 = 'CLAUDE_4_SONNET_1_0',
  CLAUDE_4_OPUS_1_0 = 'CLAUDE_4_OPUS_1_0',
  CLAUDE_4_1_OPUS_1_0 = 'CLAUDE_4_1_OPUS_1_0',
  CLAUDE_4_5_SONNET_1_0 = 'CLAUDE_4_5_SONNET_1_0',
  CLAUDE_4_5_HAIKU_1_0 = 'CLAUDE_4_5_HAIKU_1_0',
  CLAUDE_4_5_OPUS_1_0 = 'CLAUDE_4_5_OPUS_1_0',
  CLAUDE_4_6_OPUS_1_0 = 'CLAUDE_4_6_OPUS_1_0',
  CLAUDE_4_6_SONNET_1_0 = 'CLAUDE_4_6_SONNET_1_0',
}

/** @enumType */
export type AnthropicModelWithLiterals =
  | AnthropicModel
  | 'UNKNOWN_ANTHROPIC_MODEL'
  | 'CLAUDE_3_HAIKU_1_0'
  | 'CLAUDE_3_5_SONNET_1_0'
  | 'CLAUDE_3_5_SONNET_2_0'
  | 'CLAUDE_3_7_SONNET_1_0'
  | 'CLAUDE_4_SONNET_1_0'
  | 'CLAUDE_4_OPUS_1_0'
  | 'CLAUDE_4_1_OPUS_1_0'
  | 'CLAUDE_4_5_SONNET_1_0'
  | 'CLAUDE_4_5_HAIKU_1_0'
  | 'CLAUDE_4_5_OPUS_1_0'
  | 'CLAUDE_4_6_OPUS_1_0'
  | 'CLAUDE_4_6_SONNET_1_0';

export interface AnthropicMessage {
  /** The role of the message author. */
  role?: MessageRoleRoleWithLiterals;
  /**
   * The content of the message.
   * @maxSize 4096
   */
  content?: V1ContentBlock[];
}

/** Top-level tool wrapper. Exactly one branch is set. */
export interface V1Tool extends V1ToolKindOneOf {
  /**
   * Client tool.
   * User-defined custom tools that you create and implement
   */
  custom?: CustomTool;
  /**
   * Client tool (Anthropic-defined).
   * Claude can interact with computer environments through the computer use tool,
   * which provides screenshot capabilities and mouse/keyboard control for autonomous desktop interaction.
   */
  computerUse?: ComputerUseTool;
  /**
   * Client tool (Anthropic-defined).
   * Claude can use an Anthropic-defined text editor tool to view and modify text files, helping you debug, fix, and improve your code or other text documents.
   * This allows Claude to directly interact with your files, providing hands-on assistance rather than just suggesting changes.
   */
  textEditor?: TextEditorTool;
  /**
   * Client tool (Anthropic-defined).
   * The bash tool enables Claude to execute shell commands in a persistent bash session,
   * allowing system operations, script execution, and command-line automation.
   */
  bash?: BashTool;
  /**
   * Server tool (Anthropic-defined).
   * The web search tool gives Claude direct access to real-time web content,
   * allowing it to answer questions with up-to-date information beyond its knowledge cutoff.
   * Claude automatically cites sources from search results as part of its answer.
   */
  webSearch?: WebSearchTool;
  /**
   * Server tool (Anthropic-defined).
   * The code execution tool allows Claude to execute Python code in a secure, sandboxed environment.
   * Claude can analyze data, create visualizations, perform complex calculations, and process uploaded files directly within the API conversation.
   */
  codeExecution?: CodeExecutionTool;
  /**
   * Server tool (Anthropic-defined).
   * The web fetch tool allows Claude to retrieve full content from specified web pages and PDF documents.
   */
  webFetch?: WebFetchTool;
}

/** @oneof */
export interface V1ToolKindOneOf {
  /**
   * Client tool.
   * User-defined custom tools that you create and implement
   */
  custom?: CustomTool;
  /**
   * Client tool (Anthropic-defined).
   * Claude can interact with computer environments through the computer use tool,
   * which provides screenshot capabilities and mouse/keyboard control for autonomous desktop interaction.
   */
  computerUse?: ComputerUseTool;
  /**
   * Client tool (Anthropic-defined).
   * Claude can use an Anthropic-defined text editor tool to view and modify text files, helping you debug, fix, and improve your code or other text documents.
   * This allows Claude to directly interact with your files, providing hands-on assistance rather than just suggesting changes.
   */
  textEditor?: TextEditorTool;
  /**
   * Client tool (Anthropic-defined).
   * The bash tool enables Claude to execute shell commands in a persistent bash session,
   * allowing system operations, script execution, and command-line automation.
   */
  bash?: BashTool;
  /**
   * Server tool (Anthropic-defined).
   * The web search tool gives Claude direct access to real-time web content,
   * allowing it to answer questions with up-to-date information beyond its knowledge cutoff.
   * Claude automatically cites sources from search results as part of its answer.
   */
  webSearch?: WebSearchTool;
  /**
   * Server tool (Anthropic-defined).
   * The code execution tool allows Claude to execute Python code in a secure, sandboxed environment.
   * Claude can analyze data, create visualizations, perform complex calculations, and process uploaded files directly within the API conversation.
   */
  codeExecution?: CodeExecutionTool;
  /**
   * Server tool (Anthropic-defined).
   * The web fetch tool allows Claude to retrieve full content from specified web pages and PDF documents.
   */
  webFetch?: WebFetchTool;
}

export interface CustomTool {
  /**
   * The name of the tool. Must match the regex ^[a-zA-Z0-9_-]{1,64}$.
   * @maxLength 1000
   */
  name?: string;
  /**
   * Description of what this tool does.
   * Tool descriptions should be as detailed as possible. The more information that the model has about what the tool
   * is and how to use it, the better it will perform. You can use natural language descriptions to reinforce
   * important aspects of the tool input JSON schema.
   * @maxLength 100000
   */
  description?: string | null;
  /**
   * JSON schema for this tool's input.
   * This defines the shape of the input that your tool accepts and that the model will produce.
   */
  inputSchema?: V1InputSchema;
  /** Enables prompt caching: https://docs.anthropic.com/en/docs/build-with-claude/prompt-caching. */
  cacheControl?: V1CacheControl;
}

export interface V1InputSchema {
  /**
   * Available options: object
   * @maxLength 100
   */
  type?: string | null;
  /** Object that defines JSON schema itself. */
  properties?: Record<string, any> | null;
  /**
   * List of required parameters from JSON schema.
   * @maxSize 4096
   * @maxLength 1000
   */
  required?: string[];
}

export interface ComputerUseTool {
  /** Display width in pixels, recommend ≤1280 */
  displayWidthPx?: number;
  /** Display height in pixels, recommend ≤800 */
  displayHeightPx?: number;
  /** Display number for X11 environments */
  displayNumber?: number | null;
}

export interface TextEditorTool {
  /** Parameter to control truncation when viewing large files. Available only for text_editor_20250728 and later. */
  maxCharacters?: number | null;
}

export interface BashTool {
  /**
   * Name must be "bash".
   * @maxLength 500
   */
  name?: string | null;
}

export interface WebSearchTool {
  /** Optional: Limit the number of searches per request; exceeding -> error "max_uses_exceeded". */
  maxUses?: number | null;
  /**
   * Note: You can use either allowed_domains or blocked_domains, but not both in the same request.
   * Optional: Only include results from these domains, e.g. "trusteddomain.org"
   * @maxSize 100
   * @maxLength 500
   */
  allowedDomains?: string[];
  /**
   * Optional: Never include results from these domains, e.g. "untrustedsource.com"
   * @maxSize 100
   * @maxLength 500
   */
  blockedDomains?: string[];
  /** Optional: Localize search results */
  userLocation?: WebSearchUserLocation;
  /** Optional: caches the tool definition only (it will not cache the results) */
  cacheControl?: V1CacheControl;
}

export interface WebSearchUserLocation {
  /**
   * The type of location (must be "approximate")
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The city name
   * @maxLength 500
   */
  city?: string | null;
  /**
   * The region or state
   * @maxLength 500
   */
  region?: string | null;
  /**
   * The country
   * @maxLength 500
   */
  country?: string | null;
  /**
   * The IANA timezone ID, e.g. "America/Los_Angeles"
   * @maxLength 500
   */
  timezone?: string | null;
}

export interface CodeExecutionTool {
  /**
   * Name must be "code_execution".
   * @maxLength 500
   */
  name?: string | null;
}

export interface WebFetchTool {
  /** Optional: Limit the number of fetches per request */
  maxUses?: number | null;
  /**
   * Note: You can use either allowed_domains or blocked_domains, but not both in the same request.
   * Optional: Only fetch from these domains, e.g. "trusteddomain.org"
   * @maxSize 100
   * @maxLength 500
   */
  allowedDomains?: string[];
  /**
   * Optional: Never fetch from these domains, e.g. "untrustedsource.com"
   * @maxSize 100
   * @maxLength 500
   */
  blockedDomains?: string[];
  /** Optional: Enable citations for fetched content */
  citations?: CitationsEnabled;
  /** Optional: Maximum content length in tokens */
  maxContentTokens?: number | null;
}

export interface V1ToolChoice {
  /**
   * AUTO allows Claude to decide whether to call any provided tools or not. This is the default value.
   * ANY  tells Claude that it must use one of the provided tools, but doesn’t force a particular tool.
   * TOOL allows us to force Claude to always use a particular tool.
   */
  type?: V1ToolChoiceTypeWithLiterals;
  /**
   * The name of the tool to use in case Type is TOOL.
   * @maxLength 1000
   */
  name?: string | null;
  /**
   * Whether to disable parallel tool use.
   * Defaults to false.
   * If set to true, the model will output at most one tool use (if Type is AUTO) or exactly one tool use (if Type is ANY or TOOL)
   */
  disableParallelToolUse?: boolean | null;
}

export enum V1ToolChoiceType {
  UNKNOWN = 'UNKNOWN',
  AUTO = 'AUTO',
  ANY = 'ANY',
  TOOL = 'TOOL',
  NONE = 'NONE',
}

/** @enumType */
export type V1ToolChoiceTypeWithLiterals =
  | V1ToolChoiceType
  | 'UNKNOWN'
  | 'AUTO'
  | 'ANY'
  | 'TOOL'
  | 'NONE';

export interface V1ThinkingConfig {
  /**
   * Determines how many tokens Claude can use for its internal reasoning process. Larger budgets can enable more thorough
   * analysis for complex problems, improving response quality.
   * Must be ≥1024 and less than max_tokens.
   * @min 1024
   */
  budgetTokens?: number;
  /**
   * When enabled, responses include thinking content blocks showing Claude's thinking process before the final answer.
   * Requires a minimum budget of 1,024 tokens and counts towards your max_tokens limit.
   * Defaults to "enabled" in the mapper if unset to preserve legacy behavior.
   */
  enabled?: boolean | null;
}

export interface V1McpServer {
  /**
   * McpServer name
   * @maxLength 1000
   */
  name?: string;
  /** Available options: url */
  type?: V1McpServerTypeWithLiterals;
  /**
   * McpServer url
   * @maxLength 10000
   */
  url?: string | null;
  /** Tool configuration */
  toolConfiguration?: McpServerToolConfiguration;
}

export enum V1McpServerType {
  UNKNOWN = 'UNKNOWN',
  URL = 'URL',
}

/** @enumType */
export type V1McpServerTypeWithLiterals = V1McpServerType | 'UNKNOWN' | 'URL';

export interface McpServerToolConfiguration {
  /**
   * Allowed tools
   * @maxLength 1000
   * @maxSize 100
   */
  allowedTools?: string[];
  /** Enabled */
  enabled?: boolean | null;
}

export interface RequestMetadata {
  /**
   * An external identifier for the user who is associated with the request.
   * This should be a uuid, hash value, or other opaque identifier. Anthropic may use this id to help detect abuse. Do not include any identifying information such as name, email address, or phone number.
   * Maximum length: 256
   * Examples: "13803d75-b4b5-4c3e-b2a2-6f21399b021b"
   * @maxLength 256
   */
  userId?: string | null;
}

export interface InvokeLlamaModelRequest {
  /** The unique identifier of the model to invoke to run inference. */
  model?: LlamaModelWithLiterals;
  /**
   * The prompt that you want to pass to the model. With Llama 2 Chat, format the conversation with the following template.
   * @maxLength 1000000
   */
  prompt?: string | null;
  /**
   * Specify the maximum number of tokens to use in the generated response.
   * The model truncates the response once the generated text exceeds max_gen_len.
   * @min 1
   */
  maxGenLen?: number | null;
  /**
   * Use a lower value to decrease randomness in the response.
   * @max 1
   */
  temperature?: number | null;
  /**
   * Use a lower value to ignore less probable options. Set to 0 or 1.0 to disable.
   * @max 1
   */
  topP?: number | null;
}

export enum LlamaModel {
  UNKNOWN_LLAMA_MODEL = 'UNKNOWN_LLAMA_MODEL',
  /** meta.llama3-8b-instruct-v1:0 */
  LLAMA_3_8B_INSTRUCT_1_0 = 'LLAMA_3_8B_INSTRUCT_1_0',
  /** meta.llama3-70b-instruct-v1:0 */
  LLAMA_3_70B_INSTRUCT_1_0 = 'LLAMA_3_70B_INSTRUCT_1_0',
  /** meta.llama3-1-8b-instruct-v1:0 */
  LLAMA_3_1_8B_INSTRUCT_1_0 = 'LLAMA_3_1_8B_INSTRUCT_1_0',
  /** meta.llama3-1-70b-instruct-v1:0 */
  LLAMA_3_1_70B_INSTRUCT_1_0 = 'LLAMA_3_1_70B_INSTRUCT_1_0',
  /** meta.llama3-2-1b-instruct-v1:0 */
  LLAMA_3_2_1B_INSTRUCT_1_0 = 'LLAMA_3_2_1B_INSTRUCT_1_0',
  /** meta.llama3-2-3b-instruct-v1:0 */
  LLAMA_3_2_3B_INSTRUCT_1_0 = 'LLAMA_3_2_3B_INSTRUCT_1_0',
}

/** @enumType */
export type LlamaModelWithLiterals =
  | LlamaModel
  | 'UNKNOWN_LLAMA_MODEL'
  | 'LLAMA_3_8B_INSTRUCT_1_0'
  | 'LLAMA_3_70B_INSTRUCT_1_0'
  | 'LLAMA_3_1_8B_INSTRUCT_1_0'
  | 'LLAMA_3_1_70B_INSTRUCT_1_0'
  | 'LLAMA_3_2_1B_INSTRUCT_1_0'
  | 'LLAMA_3_2_3B_INSTRUCT_1_0';

export interface InvokeConverseRequest {
  /** The foundation model to use for this conversation. */
  model?: ConverseModelWithLiterals;
  /**
   * Conversation history and new input. Processed in the order provided.
   * @maxSize 4096
   */
  messages?: ConverseMessage[];
  /** Parameters controlling text generation behavior. */
  inferenceConfig?: ConverseInferenceConfig;
  /** Tool configuration for function calling. */
  toolConfig?: ToolConfig;
  /** Latency optimization settings. */
  performanceConfig?: ConversePerformanceConfig;
  /**
   * System prompts providing high-level instructions. Processed before conversation messages.
   * @maxSize 100
   */
  system?: SystemContentBlock[];
  /** Model-specific parameters as a JSON object. */
  additionalModelRequestFields?: Record<string, any> | null;
  /**
   * JSON paths to extract from the model's raw response.
   * @maxLength 1000
   * @maxSize 100
   */
  additionalModelResponseFieldPaths?: string[];
}

export enum ConverseModel {
  UNKNOWN_CONVERSE_MODEL = 'UNKNOWN_CONVERSE_MODEL',
  /** OPEN AI Models */
  OPEN_AI_GPT_OSS_120B = 'OPEN_AI_GPT_OSS_120B',
  /** MiniMax AI */
  MINIMAX_M2 = 'MINIMAX_M2',
}

/** @enumType */
export type ConverseModelWithLiterals =
  | ConverseModel
  | 'UNKNOWN_CONVERSE_MODEL'
  | 'OPEN_AI_GPT_OSS_120B'
  | 'MINIMAX_M2';

/** Parameters that control the model's text generation behavior. */
export interface ConverseInferenceConfig {
  /**
   * Maximum tokens to generate before stopping.
   * @min 1
   */
  maxTokens?: number | null;
  /**
   * Randomness in output. Higher values (closer to 1.0) increase creativity.
   * @max 1
   */
  temperature?: number | null;
  /**
   * Nucleus sampling threshold. The model considers tokens whose cumulative probability exceeds this value.
   * @max 1
   */
  topP?: number | null;
  /**
   * Text sequences that cause the model to stop generating.
   * @maxLength 512
   * @maxSize 8191
   */
  stopSequences?: string[];
}

/** Converse-specific tool configuration */
export interface ToolConfig {
  /**
   * Available tools for the model.
   * @maxSize 1000
   */
  tools?: ConverseTool[];
  /** How the model should use tools. */
  toolChoice?: ToolChoice;
}

/** Tool wrapper with specification */
export interface ConverseTool {
  /** Tool specification containing name, description, and input schema. */
  toolSpec?: ToolSpecification;
}

/** Detailed tool specification */
export interface ToolSpecification {
  /**
   * Name of the tool.
   * @maxLength 256
   */
  name?: string;
  /**
   * Description of what the tool does.
   * @maxLength 2048
   */
  description?: string | null;
  /** JSON schema for tool input parameters. */
  inputSchema?: ConverseInputSchema;
}

/** Input schema wrapper */
export interface ConverseInputSchema {
  /** JSON schema as a Struct (wraps the schema in "json" field for AWS API). */
  json?: Record<string, any> | null;
}

export interface SystemContentBlock {
  /**
   * Text providing high-level instructions or context for the conversation.
   * @maxLength 100000000
   */
  text?: string | null;
}

export interface CreateImageRequest {
  /**
   * A text description of the desired image(s). The maximum length is 1000 characters for dall-e-2 and 4000 characters for dall-e-3.
   * @maxLength 50000
   */
  prompt?: string | null;
  /** The model to use for image generation. */
  model?: V1ImageModelWithLiterals;
  /** The number of images to generate. Must be between 1 and 10. For dall-e-3, only n=1 is supported. */
  n?: number | null;
  /**
   * The quality of the image that will be generated.
   * hd creates images with finer details and greater consistency across the image. This param is only supported for dall-e-3.
   */
  quality?: ImageQualityWithLiterals;
  /** The size of the generated images. Must be one of 256x256, 512x512, or 1024x1024 for dall-e-2. Must be one of 1024x1024, 1792x1024, or 1024x1792 for dall-e-3 models. */
  size?: ImageSizeWithLiterals;
  /**
   * The style of the generated images. Must be one of vivid or natural. Vivid causes the model to lean towards generating hyper-real and dramatic images.
   * Natural causes the model to produce more natural, less hyper-real looking images. This param is only supported for dall-e-3.
   */
  style?: ImageStyleWithLiterals;
  /**
   * A unique identifier representing your end-user, which can help OpenAI to monitor and detect abuse.
   * @maxLength 100
   */
  user?: string | null;
}

export enum ImageQuality {
  UNKNOWN_IMAGE_QUALITY = 'UNKNOWN_IMAGE_QUALITY',
  STANDARD = 'STANDARD',
  HD = 'HD',
}

/** @enumType */
export type ImageQualityWithLiterals =
  | ImageQuality
  | 'UNKNOWN_IMAGE_QUALITY'
  | 'STANDARD'
  | 'HD';

export enum ImageSize {
  UNKNOWN_IMAGE_SIZE = 'UNKNOWN_IMAGE_SIZE',
  SIZE_256X256 = 'SIZE_256X256',
  SIZE_512X512 = 'SIZE_512X512',
  SIZE_1024X1024 = 'SIZE_1024X1024',
  SIZE_1792X1024 = 'SIZE_1792X1024',
  SIZE_1024X1792 = 'SIZE_1024X1792',
}

/** @enumType */
export type ImageSizeWithLiterals =
  | ImageSize
  | 'UNKNOWN_IMAGE_SIZE'
  | 'SIZE_256X256'
  | 'SIZE_512X512'
  | 'SIZE_1024X1024'
  | 'SIZE_1792X1024'
  | 'SIZE_1024X1792';

export enum ImageStyle {
  UNKNOWN_IMAGE_STYLE = 'UNKNOWN_IMAGE_STYLE',
  VIVID = 'VIVID',
  NATURAL = 'NATURAL',
}

/** @enumType */
export type ImageStyleWithLiterals =
  | ImageStyle
  | 'UNKNOWN_IMAGE_STYLE'
  | 'VIVID'
  | 'NATURAL';

export interface V1TextToImageRequest {
  /** The model to use for generating the image. */
  model?: ImageModelWithLiterals;
  /** Height of the image to generate, in pixels, in an increment divisible by 64. Default: 512 */
  height?: number | null;
  /** Width of the image to generate, in pixels, in an increment divisible by 64. Default: 512 */
  width?: number | null;
  /**
   * An array of text prompts to use for generation.
   * @minSize 1
   * @maxSize 10
   */
  textPrompts?: TextPrompt[];
  /** How strictly the diffusion process adheres to the prompt text (higher values keep your image closer to your prompt. Default: 7 */
  cfgScale?: number | null;
  /**
   * CLIP Guidance is a technique that uses the CLIP neural network to guide the generation of images to be more in-line with your included prompt,
   * which often results in improved coherency.
   */
  clipGuidancePreset?: ClipGuidancePresetWithLiterals;
  /** Which sampler to use for the diffusion process. If this value is omitted we'll automatically select an appropriate sampler for you. */
  sampler?: SamplerWithLiterals;
  /** Number of images to generate. Default: 1 */
  samples?: number | null;
  /** A specific value [ 0 .. 4294967294 ] that is used to guide the 'randomness' of the generation. (Omit this parameter or pass 0 to use a random seed.) */
  seed?: string | null;
  /** Number of diffusion steps to run. Default: 30 */
  steps?: number | null;
  /**
   * style_preset 3d-model analog-film anime cinematic comic-book digital-art enhance fantasy-art isometric line-art low-poly modeling-compound neon-punk origami photographic pixel-art tile-texture
   * Pass in a style preset to guide the image model towards a particular style. This list of style presets is subject to change.
   */
  stylePreset?: TextToImageRequestStylePresetWithLiterals;
}

export interface TextPrompt {
  /**
   * The text to generate the image from.
   * @maxLength 4000
   */
  text?: string | null;
  /** The weight of the text prompt. */
  weight?: number | null;
}

export enum ClipGuidancePreset {
  CLIP_GUIDANCE_PRESET_UNSPECIFIED = 'CLIP_GUIDANCE_PRESET_UNSPECIFIED',
  FAST_BLUE = 'FAST_BLUE',
  FAST_GREEN = 'FAST_GREEN',
  NONE = 'NONE',
  SIMPLE = 'SIMPLE',
  SLOW = 'SLOW',
  SLOWER = 'SLOWER',
  SLOWEST = 'SLOWEST',
}

/** @enumType */
export type ClipGuidancePresetWithLiterals =
  | ClipGuidancePreset
  | 'CLIP_GUIDANCE_PRESET_UNSPECIFIED'
  | 'FAST_BLUE'
  | 'FAST_GREEN'
  | 'NONE'
  | 'SIMPLE'
  | 'SLOW'
  | 'SLOWER'
  | 'SLOWEST';

export enum Sampler {
  SAMPLER_UNSPECIFIED = 'SAMPLER_UNSPECIFIED',
  DDIM = 'DDIM',
  DDPM = 'DDPM',
  K_DPMPP_2M = 'K_DPMPP_2M',
  K_DPMPP_2S_ANCESTRAL = 'K_DPMPP_2S_ANCESTRAL',
  K_DPM_2 = 'K_DPM_2',
  K_DPM_2_ANCESTRAL = 'K_DPM_2_ANCESTRAL',
  K_EULER = 'K_EULER',
  K_EULER_ANCESTRAL = 'K_EULER_ANCESTRAL',
  K_HEUN = 'K_HEUN',
  K_LMS = 'K_LMS',
}

/** @enumType */
export type SamplerWithLiterals =
  | Sampler
  | 'SAMPLER_UNSPECIFIED'
  | 'DDIM'
  | 'DDPM'
  | 'K_DPMPP_2M'
  | 'K_DPMPP_2S_ANCESTRAL'
  | 'K_DPM_2'
  | 'K_DPM_2_ANCESTRAL'
  | 'K_EULER'
  | 'K_EULER_ANCESTRAL'
  | 'K_HEUN'
  | 'K_LMS';

export enum TextToImageRequestStylePreset {
  STYLE_PRESET_UNSPECIFIED = 'STYLE_PRESET_UNSPECIFIED',
  ANALOG_FILM = 'ANALOG_FILM',
  ANIME = 'ANIME',
  CINEMATIC = 'CINEMATIC',
  COMIC_BOOK = 'COMIC_BOOK',
  DIGITAL_ART = 'DIGITAL_ART',
  ENHANCE = 'ENHANCE',
  FANTASY_ART = 'FANTASY_ART',
  ISOMETRIC = 'ISOMETRIC',
  LINE_ART = 'LINE_ART',
  LOW_POLY = 'LOW_POLY',
  MODELING_COMPOUND = 'MODELING_COMPOUND',
  NEON_PUNK = 'NEON_PUNK',
  ORIGAMI = 'ORIGAMI',
  PHOTOGRAPHIC = 'PHOTOGRAPHIC',
  PIXEL_ART = 'PIXEL_ART',
  TILE_TEXTURE = 'TILE_TEXTURE',
  MODEL_3D = 'MODEL_3D',
}

/** @enumType */
export type TextToImageRequestStylePresetWithLiterals =
  | TextToImageRequestStylePreset
  | 'STYLE_PRESET_UNSPECIFIED'
  | 'ANALOG_FILM'
  | 'ANIME'
  | 'CINEMATIC'
  | 'COMIC_BOOK'
  | 'DIGITAL_ART'
  | 'ENHANCE'
  | 'FANTASY_ART'
  | 'ISOMETRIC'
  | 'LINE_ART'
  | 'LOW_POLY'
  | 'MODELING_COMPOUND'
  | 'NEON_PUNK'
  | 'ORIGAMI'
  | 'PHOTOGRAPHIC'
  | 'PIXEL_ART'
  | 'TILE_TEXTURE'
  | 'MODEL_3D';

export interface GenerateCoreRequest {
  /** The model to use for generating the image. will always be STABLE_IMAGE_CORE */
  model?: ImageCoreModelWithLiterals;
  /**
   * What you wish to see in the output image.
   * A strong, descriptive prompt that clearly defines elements, colors, and subjects will lead to better results.
   * To control the weight of a given word use the format (word:weight),
   * where word is the word you'd like to control the weight of and weight is a value between 0 and 1.
   * For example: The sky was a crisp (blue:0.3) and (green:0.8) would convey a sky that was blue and green, but more green than blue.
   * @maxLength 10000
   */
  prompt?: string | null;
  /**
   * Default: 1:1
   * One of : 16:9 1:1 21:9 2:3 3:2 4:5 5:4 9:16 9:21
   * Controls the aspect ratio of the generated image.
   * @maxLength 100
   */
  aspectRatio?: string | null;
  /**
   * A blurb of text describing what you do not wish to see in the output image.
   * This is an advanced feature.
   * @maxLength 10000
   */
  negativePrompt?: string | null;
  /**
   * Default: 0
   * A specific value [ 0 .. 4294967294 ] that is used to guide the 'randomness' of the generation.
   * (Omit this parameter or pass 0 to use a random seed.)
   */
  seed?: string | null;
  /**
   * style_preset 3d-model analog-film anime cinematic comic-book digital-art enhance fantasy-art isometric line-art low-poly modeling-compound neon-punk origami photographic pixel-art tile-texture
   * Pass in a style preset to guide the image model towards a particular style. This list of style presets is subject to change.
   */
  stylePreset?: GenerateCoreRequestStylePresetWithLiterals;
  /**
   * Default: png
   * Enum: jpeg png webp
   * Dictates the content-type of the generated image.
   * @maxLength 100
   */
  outputFormat?: string | null;
}

export enum GenerateCoreRequestStylePreset {
  STYLE_PRESET_UNSPECIFIED = 'STYLE_PRESET_UNSPECIFIED',
  ANALOG_FILM = 'ANALOG_FILM',
  ANIME = 'ANIME',
  CINEMATIC = 'CINEMATIC',
  COMIC_BOOK = 'COMIC_BOOK',
  DIGITAL_ART = 'DIGITAL_ART',
  ENHANCE = 'ENHANCE',
  FANTASY_ART = 'FANTASY_ART',
  ISOMETRIC = 'ISOMETRIC',
  LINE_ART = 'LINE_ART',
  LOW_POLY = 'LOW_POLY',
  MODELING_COMPOUND = 'MODELING_COMPOUND',
  NEON_PUNK = 'NEON_PUNK',
  ORIGAMI = 'ORIGAMI',
  PHOTOGRAPHIC = 'PHOTOGRAPHIC',
  PIXEL_ART = 'PIXEL_ART',
  TILE_TEXTURE = 'TILE_TEXTURE',
  MODEL_3D = 'MODEL_3D',
}

/** @enumType */
export type GenerateCoreRequestStylePresetWithLiterals =
  | GenerateCoreRequestStylePreset
  | 'STYLE_PRESET_UNSPECIFIED'
  | 'ANALOG_FILM'
  | 'ANIME'
  | 'CINEMATIC'
  | 'COMIC_BOOK'
  | 'DIGITAL_ART'
  | 'ENHANCE'
  | 'FANTASY_ART'
  | 'ISOMETRIC'
  | 'LINE_ART'
  | 'LOW_POLY'
  | 'MODELING_COMPOUND'
  | 'NEON_PUNK'
  | 'ORIGAMI'
  | 'PHOTOGRAPHIC'
  | 'PIXEL_ART'
  | 'TILE_TEXTURE'
  | 'MODEL_3D';

export interface GenerateStableDiffusionRequest {
  /**
   * The text prompt to generate the image from.
   * A strong, descriptive prompt that clearly defines elements, colors, and subjects will lead to better results.
   * @maxLength 10000
   */
  prompt?: string | null;
  /**
   * Controls whether this is a text-to-image or image-to-image generation.
   * - TEXT_TO_IMAGE requires only the prompt parameter.
   * - IMAGE_TO_IMAGE requires prompt, image, and strength parameters.
   */
  mode?: GenerationModeWithLiterals;
  /**
   * The image to use as the starting point for the generation.
   * This parameter is only valid for IMAGE_TO_IMAGE mode.
   * The URL must be a valid wix mp or wix static URL.
   * @maxLength 100000
   */
  url?: string | null;
  /**
   * Controls how much influence the image parameter has on the output image.
   * A value of 0 yields an image identical to the input; 1 ignores the input image.
   * This parameter is only valid for IMAGE_TO_IMAGE mode.
   */
  strength?: number | null;
  /**
   * Default: 1:1
   * One of : 16:9 1:1 21:9 2:3 3:2 4:5 5:4 9:16 9:21
   * Controls the aspect ratio of the generated image.
   * This parameter is only valid for TEXT_TO_IMAGE mode.
   * @maxLength 100
   */
  aspectRatio?: string | null;
  /** The model to use for generation. */
  model?: ImageStableDiffusionModelWithLiterals;
  /** A specific value [ 0 .. 4294967294 ] that is used to guide the 'randomness' of the generation. (Omit this parameter or pass 0 to use a random seed.) */
  seed?: string | null;
  /** Dictates the content-type of the generated image. */
  outputFormat?: GenerateStableDiffusionRequestOutputFormatWithLiterals;
  /**
   * Keywords of what you do not wish to see in the output image.
   * @maxLength 10000
   */
  negativePrompt?: string | null;
}

export enum GenerationMode {
  UNKNOWN_GENERATION_MODE = 'UNKNOWN_GENERATION_MODE',
  TEXT_TO_IMAGE = 'TEXT_TO_IMAGE',
  IMAGE_TO_IMAGE = 'IMAGE_TO_IMAGE',
}

/** @enumType */
export type GenerationModeWithLiterals =
  | GenerationMode
  | 'UNKNOWN_GENERATION_MODE'
  | 'TEXT_TO_IMAGE'
  | 'IMAGE_TO_IMAGE';

export enum GenerateStableDiffusionRequestOutputFormat {
  OUTPUT_FORMAT_UNSPECIFIED = 'OUTPUT_FORMAT_UNSPECIFIED',
  JPEG = 'JPEG',
  PNG = 'PNG',
}

/** @enumType */
export type GenerateStableDiffusionRequestOutputFormatWithLiterals =
  | GenerateStableDiffusionRequestOutputFormat
  | 'OUTPUT_FORMAT_UNSPECIFIED'
  | 'JPEG'
  | 'PNG';

/** Request to generate an image */
export interface GenerateAnImageRequest {
  /** The model to use for generating the image. */
  model?: GenerateAnImageModelWithLiterals;
  /**
   * The prompt to use for image generation.
   * Relevant models : ALL
   * @maxLength 1000000
   */
  prompt?: string | null;
  /**
   * Optional seed for reproducibility. If not provided, a random seed will be used.
   * Relevant models : ALL
   */
  seed?: number | null;
  /**
   * Aspect ratio of the image between 21:9 and 9:21
   * default: 16:9
   * Relevant models : FLUX_PRO_1_1_ULTRA
   * @maxLength 100
   */
  aspectRatio?: string | null;
  /**
   * Width of the generated image in pixels. Must be a multiple of 32.
   * Relevant models : FLUX_1_DEV
   * @min 256
   * @max 1440
   */
  width?: number | null;
  /**
   * Height of the generated image in pixels. Must be a multiple of 32.
   * Relevant models : FLUX_1_DEV
   * @min 256
   * @max 1440
   */
  height?: number | null;
  /**
   * Tolerance level for input and output moderation. Between 0 and 6, 0 being most strict, 6 being least strict.
   * Relevant models : ALL
   * @max 6
   */
  safetyTolerance?: number | null;
  /**
   * Output format for the generated image. Can be 'jpeg' or 'png'.
   * Relevant models : ALL
   * @maxLength 100
   */
  outputFormat?: string | null;
  /**
   * Generate less processed, more natural-looking images
   * Relevant models : FLUX_PRO_1_1_ULTRA
   */
  raw?: boolean | null;
  /**
   * Optional image to remix
   * The URL must be a valid wix mp or wix static URL.
   * Relevant models FLUX_PRO_1_1_ULTRA, FLUX_1_DEV, FLUX_PRO_1_FILL
   * @maxLength 100000
   */
  imageUrl?: string | null;
  /**
   * Blend between the prompt and the image prompt
   * Relevant models : FLUX_PRO_1_1_ULTRA
   * @max 1
   */
  imagePromptStrength?: number | null;
  /**
   * Optional image to remix
   * Image to use as control input - relevant models FLUX_PRO_1_DEPTH and FLUX_PRO_1_CANNY
   * @maxLength 100000
   */
  controlImageUrl?: string | null;
  /**
   * Whether to perform up sampling on the prompt
   * Relevant models FLUX_1_DEV, FLUX_PRO_1_DEPTH, FLUX_PRO_1_CANNY, FLUX_PRO_1_FILL
   */
  promptUpsampling?: boolean | null;
  /**
   * Number of steps for the image generation process
   * Relevant models FLUX_1_DEV, FLUX_PRO_1_DEPTH, FLUX_PRO_1_CANNY, FLUX_PRO_1_FILL
   * @min 15
   * @max 50
   */
  steps?: number | null;
  /**
   * Guidance strength for the image generation process
   * Relevant models FLUX_1_DEV, FLUX_PRO_1_DEPTH, FLUX_PRO_1_CANNY, FLUX_PRO_1_FILL
   * @max 100
   */
  guidance?: number | null;
  /**
   * Image Mask
   * A Urk representing a mask for the areas you want to modify in the image.
   * The mask should be the same dimensions as the image and in black and white.
   * Black areas (0%) indicate no modification, while white areas (100%) specify areas for in painting.
   * Optional if you provide an alpha mask in the original image.
   * Validation: The endpoint verifies that the dimensions of the mask match the original image.
   * Relevant models FLUX_PRO_1_FILL
   * @maxLength 100000
   */
  imageMaskUrl?: string | null;
  /**
   * skip polling flag - if set to true, the response will be returned immediately without waiting for the image to be generated.
   * user should call GetResult to get the image.
   */
  skipPolling?: boolean | null;
}

export enum GenerateAnImageModel {
  GEN_IMAGE_MODEL_UNSPECIFIED = 'GEN_IMAGE_MODEL_UNSPECIFIED',
  FLUX_PRO_1_1_ULTRA = 'FLUX_PRO_1_1_ULTRA',
  FLUX_1_DEV = 'FLUX_1_DEV',
  FLUX_PRO_1_CANNY = 'FLUX_PRO_1_CANNY',
  FLUX_PRO_1_DEPTH = 'FLUX_PRO_1_DEPTH',
  FLUX_PRO_1_FILL = 'FLUX_PRO_1_FILL',
}

/** @enumType */
export type GenerateAnImageModelWithLiterals =
  | GenerateAnImageModel
  | 'GEN_IMAGE_MODEL_UNSPECIFIED'
  | 'FLUX_PRO_1_1_ULTRA'
  | 'FLUX_1_DEV'
  | 'FLUX_PRO_1_CANNY'
  | 'FLUX_PRO_1_DEPTH'
  | 'FLUX_PRO_1_FILL';

export interface CreatePredictionRequest
  extends CreatePredictionRequestInputOneOf {
  /** The input parameters for FluxPulid model */
  fluxPulid?: FluxPulid;
  /** Input for FLUX_DEV_CONTROLNET */
  fluxDevControlnet?: FluxDevControlnet;
  /** Input for REVE_EDIT */
  reveEdit?: ReveEdit;
  /** Input for Florence 2 */
  lucatacoFlorence2Large?: LucatacoFlorence2Large;
  /** Input for Isaac-0.1 */
  perceptronIsaac01?: PerceptronIsaac01;
  /** Input for z-image-turbo */
  prunaaiZImageTurbo?: PrunaaiZImageTurbo;
  /** Input for qwen-image-layered */
  qwenImageLayered?: QwenImageLayered;
  /** The model version ID */
  model?: CreatePredictionModelWithLiterals;
  /**
   * skip polling flag - if set to true, the response will be returned immediately without waiting for the image to be generated.
   * user should call GetResult to get the image.
   */
  skipPolling?: boolean | null;
}

/** @oneof */
export interface CreatePredictionRequestInputOneOf {
  /** The input parameters for FluxPulid model */
  fluxPulid?: FluxPulid;
  /** Input for FLUX_DEV_CONTROLNET */
  fluxDevControlnet?: FluxDevControlnet;
  /** Input for REVE_EDIT */
  reveEdit?: ReveEdit;
  /** Input for Florence 2 */
  lucatacoFlorence2Large?: LucatacoFlorence2Large;
  /** Input for Isaac-0.1 */
  perceptronIsaac01?: PerceptronIsaac01;
  /** Input for z-image-turbo */
  prunaaiZImageTurbo?: PrunaaiZImageTurbo;
  /** Input for qwen-image-layered */
  qwenImageLayered?: QwenImageLayered;
}

export enum CreatePredictionModel {
  /** The model version ID */
  UNKNOWN_CREATE_PREDICTION_MODEL = 'UNKNOWN_CREATE_PREDICTION_MODEL',
  /** The model version ID */
  FLUX_PULID = 'FLUX_PULID',
  /** Flux-dev-controlnet */
  FLUX_DEV_CONTROLNET = 'FLUX_DEV_CONTROLNET',
  /** https://replicate.com/reve/edit. Has a `prompt` field, routed through GenerateContent */
  REVE_EDIT = 'REVE_EDIT',
  /** https://replicate.com/lucataco/florence-2-large */
  LUCATACO_FLORENCE_2_LARGE = 'LUCATACO_FLORENCE_2_LARGE',
  /** https://replicate.com/perceptron-ai-inc/isaac-0.1 */
  PERCEPTRON_ISAAC_01 = 'PERCEPTRON_ISAAC_01',
  /** https://replicate.com/prunaai/z-image-turbo */
  PRUNAAI_Z_IMAGE_TURBO = 'PRUNAAI_Z_IMAGE_TURBO',
  /** https://replicate.com/qwen/qwen-image-layered */
  QWEN_IMAGE_LAYERED = 'QWEN_IMAGE_LAYERED',
}

/** @enumType */
export type CreatePredictionModelWithLiterals =
  | CreatePredictionModel
  | 'UNKNOWN_CREATE_PREDICTION_MODEL'
  | 'FLUX_PULID'
  | 'FLUX_DEV_CONTROLNET'
  | 'REVE_EDIT'
  | 'LUCATACO_FLORENCE_2_LARGE'
  | 'PERCEPTRON_ISAAC_01'
  | 'PRUNAAI_Z_IMAGE_TURBO'
  | 'QWEN_IMAGE_LAYERED';

export interface FluxPulid {
  /**
   * The prompt for image generation
   * @maxLength 1000
   */
  prompt?: string | null;
  /** Starting step for the generation process */
  startStep?: number | null;
  /**
   * Number of images to generate
   * @min 1
   * @max 4
   */
  numOutputs?: number | null;
  /**
   * URL of the main face image
   * @maxLength 2000
   */
  mainFaceImage?: string | null;
  /**
   * Negative prompt to specify what to avoid in generation
   * @maxLength 1000
   */
  negativePrompt?: string | null;
  /**
   * Set a random seed for generation (leave blank or -1 for random)
   * @min -1
   */
  seed?: number | null;
  /**
   * Set the width of the generated image (256-1536 pixels)
   * @min 256
   * @max 1536
   */
  width?: number | null;
  /**
   * Set the height of the generated image (256-1536 pixels)
   * @min 256
   * @max 1536
   */
  height?: number | null;
  /**
   * Set the Classifier-Free Guidance (CFG) scale. 1.0 uses standard CFG, while values >1.0 enable
   * True CFG for more precise control over generation. Higher values increase adherence to the prompt at the cost of image quality.
   * @min 1
   * @max 10
   */
  trueCfg?: number | null;
  /**
   * Set the weight of the ID image influence (0.0-3.0)
   * @max 3
   */
  idWeight?: number | null;
  /**
   * Set the number of denoising steps (1-20)
   * @min 1
   * @max 20
   */
  numSteps?: number | null;
  /**
   * Choose the format of the output image
   * Default: "webp"
   * @maxLength 100
   */
  outputFormat?: string | null;
  /**
   * Set the guidance scale for text prompt influence (1.0-10.0)
   * @min 1
   * @max 10
   */
  guidanceScale?: number | null;
  /**
   * Set the quality of the output image for jpg and webp (1-100)
   * @min 1
   * @max 100
   */
  outputQuality?: number | null;
  /**
   * Set the max sequence length for prompt (T5), smaller is faster (128-512)
   * @min 128
   * @max 512
   */
  maxSequenceLength?: number | null;
}

export interface FluxDevControlnet {
  /** Set a seed for reproducibility. Random by default. */
  seed?: number | null;
  /**
   * Number of steps
   * @min 1
   * @max 50
   */
  steps?: number | null;
  /**
   * Prompt
   * @maxLength 10000
   */
  prompt?: string | null;
  /**
   * Optional LoRA model to use.
   * Give a URL to a HuggingFace .safetensors file, a Replicate .tar file or a CivitAI download link.
   * @maxLength 2000
   */
  loraUrl?: string | null;
  /**
   * Type of control net
   * @maxLength 100
   */
  controlType?: string | null;
  /**
   * Image to use with control net
   * @maxLength 2000
   */
  controlImage?: string | null;
  /**
   * Strength of LoRA model
   * @min -1
   * @max 3
   */
  loraStrength?: number | null;
  /**
   * Format of the output images
   * @maxLength 100
   */
  outputFormat?: string | null;
  /**
   * Guidance scale
   * @max 5
   */
  guidanceScale?: number | null;
  /**
   * Quality of the output images, from 0 to 100.
   * @max 100
   */
  outputQuality?: number | null;
  /**
   * Things you do not want to see in your image
   * @maxLength 10000
   */
  negativePrompt?: string | null;
  /**
   * Strength of control net.
   * @max 3
   */
  controlStrength?: number | null;
  /**
   * Preprocessor to use with depth control net
   * @maxLength 100
   */
  depthPreprocessor?: string | null;
  /**
   * Preprocessor to use with soft edge control net
   * @maxLength 100
   */
  softEdgePreprocessor?: string | null;
  /**
   * Strength of image to image control.
   * @max 1
   */
  imageToImageStrength?: number | null;
  /** Return the preprocessed image used to control the generation process. */
  returnPreprocessedImage?: boolean | null;
}

export interface ReveEdit {
  /**
   * Image URI
   * @maxLength 10000
   */
  image?: string | null;
  /**
   * Edit instructions
   * @maxLength 10000
   */
  prompt?: string | null;
  /**
   * Specific version to use. Default: "latest"
   * @maxLength 10000
   */
  version?: string | null;
}

/** https://replicate.com/lucataco/florence-2-large/readme */
export interface LucatacoFlorence2Large {
  /**
   * Image URI
   * @maxLength 10000
   */
  image?: string | null;
  /** Which task to perform */
  taskInput?: TaskInputWithLiterals;
  /**
   * Optional input for some task types
   * @maxLength 10000
   */
  textInput?: string | null;
}

export enum TaskInput {
  UNRECOGNIZED_TASK_INPUT = 'UNRECOGNIZED_TASK_INPUT',
  OBJECT_DETECTION = 'OBJECT_DETECTION',
  CAPTION = 'CAPTION',
  DETAILED_CAPTION = 'DETAILED_CAPTION',
  MORE_DETAILED_CAPTION = 'MORE_DETAILED_CAPTION',
  CAPTION_TO_PHRASE_GROUNDING = 'CAPTION_TO_PHRASE_GROUNDING',
  REGION_PROPOSAL = 'REGION_PROPOSAL',
  DENSE_REGION_CAPTION = 'DENSE_REGION_CAPTION',
  OCR = 'OCR',
  OCR_WITH_REGION = 'OCR_WITH_REGION',
}

/** @enumType */
export type TaskInputWithLiterals =
  | TaskInput
  | 'UNRECOGNIZED_TASK_INPUT'
  | 'OBJECT_DETECTION'
  | 'CAPTION'
  | 'DETAILED_CAPTION'
  | 'MORE_DETAILED_CAPTION'
  | 'CAPTION_TO_PHRASE_GROUNDING'
  | 'REGION_PROPOSAL'
  | 'DENSE_REGION_CAPTION'
  | 'OCR'
  | 'OCR_WITH_REGION';

/** https://replicate.com/perceptron-ai-inc/isaac-0.1 */
export interface PerceptronIsaac01 {
  /**
   * Image URI
   * @maxLength 10000
   */
  image?: string | null;
  /**
   * Prompt
   * @maxLength 10000
   */
  prompt?: string | null;
  /** Which task to perform */
  response?: ResponseTypeWithLiterals;
  /** Max new tokens */
  maxNewTokens?: string | null;
}

export enum ResponseType {
  UNRECOGNIZED_RESPONSE_TYPE = 'UNRECOGNIZED_RESPONSE_TYPE',
  TEXT = 'TEXT',
  BOX = 'BOX',
  POINT = 'POINT',
  POLYGON = 'POLYGON',
}

/** @enumType */
export type ResponseTypeWithLiterals =
  | ResponseType
  | 'UNRECOGNIZED_RESPONSE_TYPE'
  | 'TEXT'
  | 'BOX'
  | 'POINT'
  | 'POLYGON';

/** https://replicate.com/prunaai/z-image-turbo */
export interface PrunaaiZImageTurbo {
  /**
   * Prompt
   * @maxLength 10000
   */
  prompt?: string | null;
  /**
   * Height of the generated image
   * @min 64
   * @max 2048
   */
  width?: number | null;
  /**
   * Width of the generated image
   * @min 64
   * @max 2048
   */
  height?: number | null;
  /**
   * Number of inference steps. This actually results in (num_inference_steps - 1) DiT forwards
   * @min 1
   * @max 50
   */
  numInferenceSteps?: number | null;
  /**
   * Guidance scale. Should be 0 for Turbo models
   * @max 20
   */
  guidanceScale?: number | null;
  /** Random seed. Set for reproducible generation */
  seed?: number | null;
  /**
   * Format of the output images
   * @maxLength 5
   */
  outputFormat?: string | null;
  /**
   * Quality when saving the output images, from 0 to 100. 100 is best quality, 0 is lowest quality. Not relevant for .png outputs
   * @max 100
   */
  outputQuality?: number | null;
}

/** https://replicate.com/qwen/qwen-image-layered */
export interface QwenImageLayered {
  /**
   * Image to be converted into a layered image
   * @minLength 1
   * @maxLength 100000
   */
  image?: string;
  /**
   * Number of layers to generate (2-8)
   * @min 2
   * @max 8
   */
  numLayers?: number | null;
  /**
   * Text description of the input image. Use 'auto' for auto captioning
   * @maxLength 100000
   */
  description?: string | null;
  /** Run faster predictions with additional optimizations */
  goFast?: boolean | null;
  /** Random seed. Set for reproducible generation */
  seed?: number | null;
  /**
   * Format of the output images. Default: "webp"
   * @maxLength 100
   */
  outputFormat?: string | null;
  /**
   * Quality when saving the output images, from 0 to 100. Not relevant for .png outputs
   * @max 100
   */
  outputQuality?: number | null;
  /** Disable safety checker for generated images */
  disableSafetyChecker?: boolean | null;
}

export interface EditImageWithPromptRequest {
  /** The model to use for generating the image. */
  model?: EditImageWithPromptRequestModelWithLiterals;
  /**
   * The image you wish to inpaint.
   * Supported Formats: jpeg, png, webp
   * Validation Rules:
   * - Every side must be at least 64 pixels
   * - Total pixel count must be between 4,096 and 9,437,184 pixels
   * @maxLength 100000
   */
  imageUrl?: string | null;
  /**
   * image format jpeg, png, webp
   * @maxLength 100
   */
  imageFormat?: string | null;
  /**
   * What you wish to see in the output image.
   * A strong, descriptive prompt that clearly defines elements, colors, and subjects will lead to better results.
   * To control the weight of a given word use the format (word:weight),
   * where word is the word you'd like to control the weight of and weight is a value between 0 and 1.
   * For example: The sky was a crisp (blue:0.3) and (green:0.8) would convey a sky that was blue and green, but more green than blue.
   * Optional for OUTPAINT model , and required for INPAINT model
   * @maxLength 10000
   */
  prompt?: string | null;
  /**
   * A blurb of text describing what you do not wish to see in the output image.
   * This is an advanced feature.
   * @maxLength 10000
   */
  negativePrompt?: string | null;
  /**
   * Controls the strength of the inpainting process on a per-pixel basis,
   * either via a second image (passed into this parameter) or via the alpha channel of the image parameter.
   * Passing in a Mask
   * The image passed to this parameter should be a black and white image that represents,
   * at any pixel, the strength of inpainting based on how dark or light the given pixel is.
   * Completely black pixels represent no inpainting strength while completely white pixels represent maximum strength.
   * In the event the mask is a different size than the image parameter, it will be automatically resized.
   * Alpha Channel Support
   * If you don't provide an explicit mask, one will be derived from the alpha channel of the image parameter.
   * Transparent pixels will be inpainted while opaque pixels will be preserved.
   * In the event an image with an alpha channel is provided along with a mask, the mask will take precedence.
   * Relevant only for INPAINT model
   * @maxLength 100000
   */
  imageMask?: string | null;
  /**
   * image mask format jpeg, png, webp
   * Relevant only for INPAINT model
   * @maxLength 100
   */
  imageMaskFormat?: string | null;
  /**
   * Grows the edges of the mask outward in all directions by the specified number of pixels. The expanded area around the mask will be blurred,
   * which can help smooth the transition between inpainted content and the original image.
   * Try this parameter if you notice seams or rough edges around the inpainted content.
   * Default: 5
   * Relevant only for INPAINT model
   * @max 100
   */
  growMask?: number | null;
  /**
   * A specific value [ 0 .. 4294967294 ] that is used to guide the 'randomness' of the generation.
   * (Omit this parameter or pass 0 to use a random seed.)
   */
  seed?: string | null;
  /**
   * Default: png
   * Enum: jpeg png webp
   * Dictates the content-type of the generated image.
   * @maxLength 100
   */
  outputFormat?: string | null;
  /**
   * style_preset 3d-model analog-film anime cinematic comic-book digital-art enhance fantasy-art isometric line-art low-poly modeling-compound neon-punk origami photographic pixel-art tile-texture
   * Pass in a style preset to guide the image model towards a particular style. This list of style presets is subject to change.
   */
  stylePreset?: StylePresetWithLiterals;
  /**
   * The direction to outpaint the image
   * Relevant only for OUTPAINT model
   * At least one of the fields must be set
   */
  outpaintDirection?: OutpaintDirection;
  /**
   * Controls the likelihood of creating additional details not heavily conditioned by the init image [0..1]
   * Relevant only for OUTPAINT model
   * @max 1
   */
  creativity?: number | null;
}

export enum StylePreset {
  STYLE_PRESET_UNSPECIFIED = 'STYLE_PRESET_UNSPECIFIED',
  ANALOG_FILM = 'ANALOG_FILM',
  ANIME = 'ANIME',
  CINEMATIC = 'CINEMATIC',
  COMIC_BOOK = 'COMIC_BOOK',
  DIGITAL_ART = 'DIGITAL_ART',
  ENHANCE = 'ENHANCE',
  FANTASY_ART = 'FANTASY_ART',
  ISOMETRIC = 'ISOMETRIC',
  LINE_ART = 'LINE_ART',
  LOW_POLY = 'LOW_POLY',
  MODELING_COMPOUND = 'MODELING_COMPOUND',
  NEON_PUNK = 'NEON_PUNK',
  ORIGAMI = 'ORIGAMI',
  PHOTOGRAPHIC = 'PHOTOGRAPHIC',
  PIXEL_ART = 'PIXEL_ART',
  TILE_TEXTURE = 'TILE_TEXTURE',
  MODEL_3D = 'MODEL_3D',
}

/** @enumType */
export type StylePresetWithLiterals =
  | StylePreset
  | 'STYLE_PRESET_UNSPECIFIED'
  | 'ANALOG_FILM'
  | 'ANIME'
  | 'CINEMATIC'
  | 'COMIC_BOOK'
  | 'DIGITAL_ART'
  | 'ENHANCE'
  | 'FANTASY_ART'
  | 'ISOMETRIC'
  | 'LINE_ART'
  | 'LOW_POLY'
  | 'MODELING_COMPOUND'
  | 'NEON_PUNK'
  | 'ORIGAMI'
  | 'PHOTOGRAPHIC'
  | 'PIXEL_ART'
  | 'TILE_TEXTURE'
  | 'MODEL_3D';

export interface OutpaintDirection {
  /**
   * The number of pixels to outpaint on the left side of the image [0..2000]
   * Relevant only for OUTPAINT model
   * @max 2000
   */
  left?: number | null;
  /**
   * The number of pixels to outpaint on the right side of the image [0..2000]
   * Relevant only for OUTPAINT model
   * @max 2000
   */
  right?: number | null;
  /**
   * The number of pixels to outpaint on the top of the image [0..2000]
   * Relevant only for OUTPAINT model
   * @max 2000
   */
  up?: number | null;
  /**
   * The number of pixels to outpaint on the bottom of the image [0..2000]
   * Relevant only for OUTPAINT model
   * @max 2000
   */
  down?: number | null;
}

export interface TextToImageRequest {
  /**
   * Specifies the format of the output image. Supported formats are: PNG, JPG and WEBP. Default: JPG.
   * @maxLength 4
   */
  outputFormat?: string | null;
  /**
   * Sets the compression quality of the output image. Higher values preserve more quality but increase file size, lower values reduce file size but decrease quality. Default: 95.
   * @min 20
   * @max 99
   */
  outputQuality?: number | null;
  /** This parameter is used to enable or disable the NSFW check. */
  checkNsfw?: boolean | null;
  /**
   * A positive prompt is a text instruction to guide the model on generating the image. It is usually a sentence or a paragraph that provides positive guidance for the task. This parameter is essential to shape the desired results.
   * For example, if the positive prompt is "dragon drinking coffee", the model will generate an image of a dragon drinking coffee. The more detailed the prompt, the more accurate the results.
   * The length of the prompt must be between 2 and 3000 characters.
   * @maxLength 1000000
   */
  positivePrompt?: string;
  /**
   * Used to define the height dimension of the generated image. Certain models perform better with specific dimensions.
   * The value must be divisible by 64, eg: 128...512, 576, 640...2048.
   */
  height?: number;
  /**
   * Used to define the width dimension of the generated image. Certain models perform better with specific dimensions.
   * The value must be divisible by 64, eg: 128...512, 576, 640...2048.
   */
  width?: number;
  /**
   * A list of reference images URLs to be used for the image generation process.
   * These images serve as visual references for the model.
   * @maxSize 10
   * @maxLength 10000
   */
  referenceImages?: string[] | null;
  /** Model to invoke. */
  model?: TextToImageRequestModelWithLiterals;
  /**
   * Video model as a string
   * @maxLength 1000
   */
  modelId?: string | null;
  /**
   * The number of steps is the number of iterations the model will perform to generate the image. Default: 28.
   * @min 1
   * @max 100
   */
  steps?: number | null;
  /**
   * A seed is a value used to randomize the image generation.
   * @min 1
   * @max 9223372036854776000
   */
  seed?: string | null;
  /**
   * Guidance scale represents how closely the images will resemble the prompt or how much freedom the AI model has. Higher values are closer to the prompt. Low values may reduce the quality of the results. Default: 7.
   * @max 30
   */
  cfgScale?: number | null;
  /** The number of images to generate from the specified prompt. */
  numberResults?: number | null;
  /**
   * When doing inpainting, this parameter is required.
   * Specifies the mask image to be used for the inpainting process. The value must be a URL pointing to the image. The image must be accessible publicly.
   * Supported formats are: PNG, JPG and WEBP.
   * @maxLength 10000
   */
  maskImage?: string | null;
  /**
   * Specifies the seed image to be used for the diffusion process.
   * Must be a URL pointing to the image. The image must be accessible publicly.
   * @maxLength 10000
   */
  seedImage?: string | null;
  /**
   * Used to determine the influence of the seedImage image in the generated output. A lower value results in more influence from the original image, while a higher value allows more creative deviation.
   * @max 1
   */
  strength?: number | null;
  /**
   * An array of LoRA models to be applied during the image generation process.
   * @maxSize 10
   */
  loraModels?: LoraModelSelect[];
  /**
   * Contains provider-specific configuration settings that customize the behavior of different AI models and services.
   * @deprecated Contains provider-specific configuration settings that customize the behavior of different AI models and services.
   * @replacedBy provider_settings_string
   * @targetRemovalDate 2030-01-01
   */
  providerSettings?: Record<string, any> | null;
  /**
   * Contains provider-specific configuration settings that customize the behavior of different AI models and services.
   * Uses a string representation that's parsed directly into JSON to avoid issues with number parsing.
   * https://stackoverflow.com/questions/51818125/how-to-use-ints-in-a-protobuf-struct
   * @maxLength 1000000
   */
  providerSettingsString?: string | null;
  /** Inputs for the image generation process. */
  inputs?: Inputs;
  /**
   * Defines the syntax to be used for prompt weighting.
   * Prompt weighting allows you to adjust how strongly different parts of your prompt influence the generated image.
   * @maxLength 1000
   */
  promptWeighting?: string | null;
  /**
   * Applies optimized acceleration presets that automatically configure multiple generation parameters.
   * Available values: "none", "low", "medium", "high".
   * @maxLength 100
   */
  acceleration?: string | null;
  /**
   * An scheduler is a component that manages the inference process. Different schedulers can be used to achieve different results.
   * @maxLength 1000
   */
  scheduler?: string | null;
  /**
   * Controls the output resolution tier when the aspect ratio is automatically determined from reference images.
   * Available values: "1k", "2k", "4k".
   * @maxLength 100
   */
  resolution?: string | null;
  /**
   * Extends the image boundaries in specified directions (outpainting).
   * An object with keys: top, right, bottom, left, blur.
   */
  outpaint?: Record<string, any> | null;
  /** Contains additional settings that customize the behavior of the generation process. */
  settings?: Record<string, any> | null;
}

export enum TextToImageRequestModel {
  UNKNOWN_MODEL = 'UNKNOWN_MODEL',
  /** runware:101@1 */
  FLUX_1_DEV = 'FLUX_1_DEV',
  /** runware:100@1 */
  FLUX_1_SCHNELL = 'FLUX_1_SCHNELL',
  /** bfl:4@1 */
  FLUX_1_KONTEXT_MAX = 'FLUX_1_KONTEXT_MAX',
  /** bfl:3@1 */
  FLUX_1_KONTEXT_PRO = 'FLUX_1_KONTEXT_PRO',
  /** runware:108@20 */
  QWEN_IMAGE_EDIT = 'QWEN_IMAGE_EDIT',
  /** ideogram:4@1 */
  IDEOGRAM_3_0 = 'IDEOGRAM_3_0',
  /** ideogram:4@3 */
  IDEOGRAM_3_0_EDIT = 'IDEOGRAM_3_0_EDIT',
  /** bfl:2@2 */
  FLUX_1_1_PRO_ULTRA = 'FLUX_1_1_PRO_ULTRA',
  /** bfl:1@2 */
  FLUX_1_FILL_PRO = 'FLUX_1_FILL_PRO',
  /** bytedance:5@0 */
  SEEDREAM_4 = 'SEEDREAM_4',
  /** runware:102@1 */
  FLUX_DEV_FILL = 'FLUX_DEV_FILL',
  /** bfl:1@5 */
  FLUX_DEPTH_PRO = 'FLUX_DEPTH_PRO',
  /** bfl:1@4 */
  FLUX_CANNY_PRO = 'FLUX_CANNY_PRO',
  /** Should be used together with model_id filed from allowed models list */
  FROM_MODEL_ID = 'FROM_MODEL_ID',
}

/** @enumType */
export type TextToImageRequestModelWithLiterals =
  | TextToImageRequestModel
  | 'UNKNOWN_MODEL'
  | 'FLUX_1_DEV'
  | 'FLUX_1_SCHNELL'
  | 'FLUX_1_KONTEXT_MAX'
  | 'FLUX_1_KONTEXT_PRO'
  | 'QWEN_IMAGE_EDIT'
  | 'IDEOGRAM_3_0'
  | 'IDEOGRAM_3_0_EDIT'
  | 'FLUX_1_1_PRO_ULTRA'
  | 'FLUX_1_FILL_PRO'
  | 'SEEDREAM_4'
  | 'FLUX_DEV_FILL'
  | 'FLUX_DEPTH_PRO'
  | 'FLUX_CANNY_PRO'
  | 'FROM_MODEL_ID';

export interface LoraModelSelect {
  /**
   * The unique identifier of the LoRA model, typically in the format "wix:<id>@<version>".
   * @minLength 1
   * @maxLength 255
   */
  model?: string | null;
  /**
   * The weight or influence of the LoRA model during the generation process.
   * A higher value indicates a stronger influence of the LoRA model on the output.
   * @min -4
   * @max 4
   */
  weight?: number | null;
}

export interface Inputs {
  /**
   * A list of reference images URLs to be used for the image generation process.
   * These images serve as visual references for the model.
   * @maxSize 10
   * @maxLength 10000
   */
  referenceImages?: string[] | null;
  /**
   * When doing inpainting, this parameter is required.
   * Specifies the mask image to be used for the inpainting process. The value must be a URL pointing to the image. The image must be accessible publicly.
   * Supported formats are: PNG, JPG and WEBP.
   * @maxLength 10000
   */
  maskImage?: string | null;
  /**
   * Specifies the seed image to be used for the diffusion process.
   * Must be a URL pointing to the image. The image must be accessible publicly.
   * @maxLength 10000
   */
  seedImage?: string | null;
}

export interface InvokeMlPlatformLlamaModelRequest {
  /**
   * The ML platform model id.
   * @minLength 1
   * @maxLength 50
   */
  modelId?: string;
  /**
   * The prompt that you want to pass to the model. With Llama 2 Chat, format the conversation with the following template.
   * @maxLength 1000000
   */
  prompt?: string | null;
  /**
   * Specify the maximum number of tokens to use in the generated response.
   * The model truncates the response once the generated text exceeds max_gen_len.
   * @min 1
   */
  maxGenLen?: number | null;
  /**
   * Use a lower value to decrease randomness in the response.
   * @max 1
   */
  temperature?: number | null;
  /**
   * Use a lower value to ignore less probable options. Set to 0 or 1.0 to disable.
   * @max 1
   */
  topP?: number | null;
}

export interface InvokeChatCompletionRequest {
  /** Model to invoke */
  model?: PerplexityModelWithLiterals;
  /**
   * A list of messages comprising the conversation so far
   * @maxSize 1000
   */
  messages?: PerplexityMessage[];
  /**
   * Max number of completion tokens.
   * Completion token count + prompt token count must not exceed the size of the context window
   * @max 200000
   */
  maxTokens?: number | null;
  /**
   * The amount of randomness in the response, valued between 0 inclusive and 2 exclusive.
   * Higher values are more random, and lower values are more deterministic.
   */
  temperature?: number | null;
  /**
   * The nucleus sampling threshold, valued between 0 and 1 inclusive.
   * For each subsequent token, the model considers the results of the tokens with top_p probability mass.
   * Perplexity recommends either altering top_k or top_p, but not both.
   */
  topP?: number | null;
  /**
   * Given a list of domains, limit the citations used by the online model to URLs from the specified domains.
   * Currently limited to only 3 domains for whitelisting and blacklisting.
   * For blacklisting add a - to the beginning of the domain string.
   * @maxLength 10000
   * @maxSize 3
   */
  searchDomainFilter?: string[];
  /** Determines whether or not a request to an online model should return images. */
  returnImages?: boolean | null;
  /** Determines whether or not a request to an online model should return related questions. */
  returnRelatedQuestions?: boolean | null;
  /**
   * Returns search results within the specified time interval - does not apply to images.
   * Must be one of "month", "week", "day", "hour"
   * @maxLength 10
   */
  searchRecencyFilter?: string | null;
  /**
   * The number of tokens to keep for highest top-k filtering, specified as an integer between 0 and 2048 inclusive.
   * If set to 0, top-k filtering is disabled. Perplexity recommends either altering top_k or top_p, but not both.
   */
  topK?: number | null;
  /**
   * A value between -2.0 and 2.0. Positive values penalize new tokens based on whether they appear in the text so far,
   * increasing the model's likelihood to talk about new topics. Incompatible with `frequency_penalty`.
   */
  presencePenalty?: number | null;
  /**
   * A multiplicative penalty greater than 0. Values greater than 1.0 penalize new tokens based on their existing
   * frequency in the text so far, decreasing the model's likelihood to repeat the same line verbatim.
   * A value of 1.0 means no penalty. Incompatible with `presence_penalty`.
   */
  frequencyPenalty?: number | null;
  /**
   * Enable structured outputs with a JSON or Regex schema.
   * https://docs.perplexity.ai/guides/structured-outputs
   */
  responseFormat?: InvokeChatCompletionRequestResponseFormat;
}

export interface InvokeChatCompletionRequestResponseFormat
  extends InvokeChatCompletionRequestResponseFormatFormatDetailsOneOf {
  /**
   * The schema should be a valid JSON schema object.
   * @maxLength 10000
   */
  jsonSchema?: string;
  /**
   * The regex is a regular expression string.
   * @maxLength 1000
   */
  regex?: string;
}

/** @oneof */
export interface InvokeChatCompletionRequestResponseFormatFormatDetailsOneOf {
  /**
   * The schema should be a valid JSON schema object.
   * @maxLength 10000
   */
  jsonSchema?: string;
  /**
   * The regex is a regular expression string.
   * @maxLength 1000
   */
  regex?: string;
}

/** mimics https://cloud.google.com/vertex-ai/generative-ai/docs/model-reference/imagen-api */
export interface V1GenerateImageRequest {
  /** ID of the model to use. */
  model?: ImagenModelWithLiterals;
  /**
   * The content of the current conversation with the model.
   * @minSize 1
   * @maxSize 1000
   */
  instances?: Instance[];
  /** The configuration for the generation. */
  parameters?: Parameters;
}

export enum ImagenModel {
  UNKNOWN_IMAGEN_MODEL = 'UNKNOWN_IMAGEN_MODEL',
  IMAGEN_3_0_GENERATE_002 = 'IMAGEN_3_0_GENERATE_002',
  IMAGEN_3_0_FAST_GENERATE_001 = 'IMAGEN_3_0_FAST_GENERATE_001',
  IMAGEN_4_0_GENERATE_001 = 'IMAGEN_4_0_GENERATE_001',
  IMAGEN_4_0_FAST_GENERATE_001 = 'IMAGEN_4_0_FAST_GENERATE_001',
  IMAGEN_4_0_ULTRA_GENERATE_001 = 'IMAGEN_4_0_ULTRA_GENERATE_001',
}

/** @enumType */
export type ImagenModelWithLiterals =
  | ImagenModel
  | 'UNKNOWN_IMAGEN_MODEL'
  | 'IMAGEN_3_0_GENERATE_002'
  | 'IMAGEN_3_0_FAST_GENERATE_001'
  | 'IMAGEN_4_0_GENERATE_001'
  | 'IMAGEN_4_0_FAST_GENERATE_001'
  | 'IMAGEN_4_0_ULTRA_GENERATE_001';

export interface Instance {
  /**
   * The text prompt for image generation
   * @maxLength 1000000
   */
  prompt?: string | null;
}

export interface Parameters {
  /**
   * The number of images to generate (1-4)
   * @min 1
   * @max 4
   */
  sampleCount?: number | null;
  /** Optional random seed for image generation */
  seed?: string | null;
  /** Optional parameter to use LLM-based prompt rewriting for higher quality images */
  enhancePrompt?: boolean | null;
  /**
   * Optional text to discourage in the generated images
   * @maxLength 480
   */
  negativePrompt?: string | null;
  /**
   * Optional aspect ratio for the image (1:1, 9:16, 16:9, 3:4, 4:3)
   * @maxLength 5
   */
  aspectRatio?: string | null;
  /** Optional output image format options */
  outputOptions?: OutputOptions;
  /**
   * Optional setting for allowing/disallowing generation of people
   * @maxLength 20
   */
  personGeneration?: string | null;
  /**
   * Optional safety filtering level
   * @maxLength 50
   */
  safetySetting?: string | null;
  /** Optional flag to add invisible watermark */
  addWatermark?: boolean | null;
}

export interface OutputOptions {
  /**
   * Image format (image/png or image/jpeg)
   * @maxLength 20
   */
  mimeType?: string | null;
  /**
   * Compression quality for JPEG (0-100)
   * @max 100
   */
  compressionQuality?: number | null;
}

export interface GenerateImageMlPlatformRequest
  extends GenerateImageMlPlatformRequestInputOneOf {
  /** The input parameters for FluxPulid model */
  fluxPulid?: V1FluxPulid;
  /** The model version ID */
  model?: GenerateImageMlPlatformModelWithLiterals;
}

/** @oneof */
export interface GenerateImageMlPlatformRequestInputOneOf {
  /** The input parameters for FluxPulid model */
  fluxPulid?: V1FluxPulid;
}

export enum GenerateImageMlPlatformModel {
  /** The model version ID */
  UNKNOWN_CREATE_PREDICTION_MODEL = 'UNKNOWN_CREATE_PREDICTION_MODEL',
  /** The model version ID */
  FLUX_PULID = 'FLUX_PULID',
}

/** @enumType */
export type GenerateImageMlPlatformModelWithLiterals =
  | GenerateImageMlPlatformModel
  | 'UNKNOWN_CREATE_PREDICTION_MODEL'
  | 'FLUX_PULID';

export interface V1FluxPulid {
  /**
   * The prompt for image generation
   * @maxLength 1000
   */
  prompt?: string | null;
  /** Starting step for the generation process */
  startStep?: number | null;
  /**
   * URL of the main face image
   * @maxLength 2000
   */
  mainFaceImage?: string | null;
  /**
   * Negative prompt to specify what to avoid in generation
   * @maxLength 1000
   */
  negativePrompt?: string | null;
  /**
   * Set a random seed for generation (leave blank or -1 for random)
   * @min -1
   */
  seed?: number | null;
  /**
   * Set the width of the generated image (256-1536 pixels)
   * @min 256
   * @max 1536
   */
  width?: number | null;
  /**
   * Set the height of the generated image (256-1536 pixels)
   * @min 256
   * @max 1536
   */
  height?: number | null;
  /**
   * Set the Classifier-Free Guidance (CFG) scale. 1.0 uses standard CFG, while values >1.0 enable
   * True CFG for more precise control over generation. Higher values increase adherence to the prompt at the cost of image quality.
   * @min 1
   * @max 10
   */
  trueCfg?: number | null;
  /**
   * Set the weight of the ID image influence (0.0-3.0)
   * @max 3
   */
  idWeight?: number | null;
  /**
   * Set the number of denoising steps (1-20)
   * @min 1
   * @max 20
   */
  numSteps?: number | null;
  /**
   * Choose the format of the output image
   * @maxLength 100
   */
  outputFormat?: string | null;
  /**
   * Set the guidance scale for text prompt influence (1.0-10.0)
   * @min 1
   * @max 10
   */
  guidanceScale?: number | null;
  /**
   * Set the max sequence length for prompt (T5), smaller is faster (128-512)
   * @min 128
   * @max 512
   */
  maxSequenceLength?: number | null;
  /** Time step to start CFG - new field for ml platform */
  timestepToStartCfg?: number | null;
  /** Option to disable the NSFW safety checker */
  disableSafetyChecker?: boolean | null;
}

export interface CreateImageOpenAiRequest {
  /**
   * A text description of the desired image(s). The maximum length is 32000 characters for the GPT image models,
   * 1000 characters for dall-e-2 and 4000 characters for dall-e-3.
   * @maxLength 50000
   */
  prompt?: string | null;
  /** The model to use for image generation. */
  model?: OpenAiImageModelWithLiterals;
  /**
   * The number of images to be generated.
   * Default is 1
   */
  n?: number | null;
  /**
   * The quality of the image that will be generated.
   * low, medium, high , Default: high
   * @maxLength 4000
   */
  quality?: string | null;
  /**
   * The dimensions of the requested image.
   * Square: 1024x1024
   * Landscape: 1536x1024
   * Portrait: 1024x1536
   * Default: 1024x1024
   * @maxLength 4000
   */
  size?: string | null;
  /**
   * Output format png,webp jpeg
   * @maxLength 50
   */
  outputFormat?: string | null;
  /**
   * 0-100% compression for JPEG + WebP
   * Default: 100%
   */
  outputCompression?: number | null;
  /**
   * Moderation flag - values low and auto.
   * Setting moderation to low will include relaxed safety refusals for violence, self-harm
   * @maxLength 10
   */
  moderation?: string | null;
  /**
   * Allows to set transparency for the background of the generated image(s). This parameter is only supported for gpt-image-1.
   * Must be one of transparent, opaque or auto (default value).
   * When auto is used, the model will automatically determine the best background for the image.
   * If transparent, the output format needs to support transparency, so it should be set to either png (default value) or webp.
   * @maxLength 200
   */
  background?: string | null;
}

export interface EditImageOpenAiRequest {
  /**
   * A text description of the desired image(s). The maximum length is 32000 characters for the GPT image models,
   * 1000 characters for dall-e-2 and 4000 characters for dall-e-3.
   * @maxLength 50000
   */
  prompt?: string | null;
  /** The model to use for image generation. */
  model?: OpenAiImageModelWithLiterals;
  /**
   * The number of images to be generated.
   * Default is 1
   */
  n?: number | null;
  /**
   * The quality of the image that will be generated.
   * low, medium, high , Default: high
   * @maxLength 4000
   */
  quality?: string | null;
  /**
   * The dimensions of the requested image.
   * Square: 1024x1024
   * Landscape: 1536 x 1024
   * Portrait: 1024 x 1536
   * Default: 1024x1024
   * @maxLength 4000
   */
  size?: string | null;
  /**
   * Output format png,webp jpeg
   * @maxLength 50
   */
  outputFormat?: string | null;
  /**
   * 0-100% compression for JPEG + WebP
   * Default: 100%
   */
  outputCompression?: number | null;
  /**
   * The image to be edited.
   * @maxLength 10000
   */
  imageUrl?: string | null;
  /**
   * The image mask to be edited.
   * @maxLength 10000
   */
  imageMaskUrl?: string | null;
  /**
   * Additional images to be edited.
   * @maxSize 10
   * @maxLength 10000
   */
  imageUrls?: string[] | null;
  /**
   * Moderation flag - values low and auto.
   * Setting moderation to low will include relaxed safety refusals for violence, self-harm
   * @maxLength 10
   */
  moderation?: string | null;
  /**
   * Allows to set transparency for the background of the generated image(s). This parameter is only supported for gpt-image-1.
   * Must be one of transparent, opaque or auto (default value).
   * When auto is used, the model will automatically determine the best background for the image.
   * If transparent, the output format needs to support transparency, so it should be set to either png (default value) or webp.
   * @maxLength 200
   */
  background?: string | null;
  /**
   * Control how much effort the model will exert to match the style and features, especially facial features, of input images.
   * This parameter is only supported for gpt-image-1. Supports high and low. Defaults to low.
   * @maxLength 10
   */
  inputFidelity?: string | null;
}

/** Mirrors https://cloud.google.com/vertex-ai/generative-ai/docs/model-reference/veo-video-generation */
export interface GenerateVideoRequest {
  /** ID of the Video generation model to use. */
  model?: VideoGenModelWithLiterals;
  /**
   * The content of the current conversation with the model.
   * @minSize 1
   * @maxSize 100
   */
  instances?: GenerateVideoInstance[];
  /** Generation-time settings. */
  parameters?: GenerateVideoParameters;
}

export enum VideoGenModel {
  UNKNOWN_VIDEO_GEN_MODEL = 'UNKNOWN_VIDEO_GEN_MODEL',
  VEO_2_0_GENERATE_001 = 'VEO_2_0_GENERATE_001',
  VEO_3_0_GENERATE_001 = 'VEO_3_0_GENERATE_001',
  VEO_3_0_FAST_GENERATE_001 = 'VEO_3_0_FAST_GENERATE_001',
}

/** @enumType */
export type VideoGenModelWithLiterals =
  | VideoGenModel
  | 'UNKNOWN_VIDEO_GEN_MODEL'
  | 'VEO_2_0_GENERATE_001'
  | 'VEO_3_0_GENERATE_001'
  | 'VEO_3_0_FAST_GENERATE_001';

export interface GenerateVideoInstance {
  /**
   * Mandatory (text-to-video), optional if an input image prompt is provided (image-to-video)
   * Text input for guiding video generation.
   * @maxLength 10000
   */
  prompt?: string | null;
  /**
   * Mandatory (image-to-video), optional if a text prompt is provided (text-to-video)
   * Image input for guiding video generation.
   */
  image?: V1ImageInput;
}

export interface V1ImageInput {
  /**
   * A publicly available image URL
   * @maxLength 10000
   */
  imageUrl?: string | null;
  /**
   * MIME type of the image (image/jpeg or image/png)
   * @maxLength 20
   */
  mimeType?: string | null;
}

export interface GenerateVideoParameters {
  /**
   * Requested video length in seconds (4, 6, or 8. The default is 8)
   * @min 4
   * @max 8
   */
  durationSeconds?: number | null;
  /**
   * A text string that describes anything you want to discourage the model from generating.
   * @maxLength 10000
   */
  negativePrompt?: string | null;
  /** Use gemini to enhance your prompts (default is True) */
  enhancePrompt?: boolean | null;
  /**
   * A number to request to make generated videos deterministic.
   * Adding a seed number with your request without changing other parameters will cause the model to produce the same videos.
   */
  seed?: string | null;
  /**
   * Number of videos to generate (1–4)
   * @min 1
   * @max 4
   */
  sampleCount?: number | null;
  /**
   * Aspect ratio: 16:9 (default, landscape) or 9:16 (portrait)
   * @maxLength 50
   */
  aspectRatio?: string | null;
  /**
   * The safety setting that controls whether people or face generation is allowed:
   * "allow_adult" (default value): allow generation of adults only
   * "disallow": disallows inclusion of people/faces in images
   * @maxLength 50
   */
  personGeneration?: string | null;
  /** Whether to generate audio for the video */
  generateAudio?: boolean | null;
  /**
   * The resolution of the generated video. Supported values: 720p, 1080p. Default: 1080p
   * @maxLength 50
   */
  resolution?: string | null;
}

/** Add to your existing proto file */
export interface V1CreateChatCompletionRequest {
  /** Model identifier */
  model?: ChatCompletionModelWithLiterals;
  /**
   * A list of messages comprising the conversation so far.
   * @minSize 1
   * @maxSize 1000
   */
  messages?: GoogleproxyV1ChatCompletionMessage[];
  /**
   * An upper bound for the number of tokens that can be generated for a completion,
   * including visible output tokens and reasoning tokens.
   * @min 1
   * @max 4096
   */
  maxCompletionTokens?: number | null;
  /**
   * What sampling temperature to use, between 0 and 2. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic.
   * We generally recommend altering this or top_p but not both.
   * @max 2
   */
  temperature?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both. Defaults to 1.
   * @max 1
   */
  topP?: number | null;
  /** How many chat completion choices to generate for each input message. Defaults to 1. */
  n?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on whether they appear in the text so far,
   * increasing the model's likelihood to talk about new topics.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  presencePenalty?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on their existing frequency in the text so far,
   * decreasing the model's likelihood to repeat the same line verbatim.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  frequencyPenalty?: number | null;
  /**
   * json_object: Interpreted as passing "application/json" to the API.
   * json_schema. Fully recursive schemas are not supported. additional_properties is supported.
   * text: Interpreted as passing "text/plain" to the API.
   * Any other MIME type is passed as is to the model, such as passing "application/json" directly.
   */
  responseFormat?: V1CreateChatCompletionRequestResponseFormat;
}

export interface V1CreateChatCompletionRequestResponseFormat {
  /**
   * Must be one of text, json_object or json_schema.
   * @maxLength 100
   */
  type?: string | null;
  /** The schema object describes the output object for the model. Currently, only the JSON Schema Object is supported. */
  schema?: Record<string, any> | null;
}

export interface InvokeMlPlatformOpenAIChatCompletionRawRequest {
  /**
   * ML Platform model identifier
   * @maxLength 10000
   */
  modelId?: string;
  /**
   * A list of messages comprising the conversation so far.
   * @minSize 1
   * @maxSize 1000
   */
  messages?: ChatCompletionMessage[];
  /**
   * An upper bound for the number of tokens that can be generated for a completion,
   * including visible output tokens and reasoning tokens.
   * @min 1
   * @max 4096
   */
  maxCompletionTokens?: number | null;
  /**
   * What sampling temperature to use, between 0 and 2. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic.
   * We generally recommend altering this or top_p but not both.
   * @max 2
   */
  temperature?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both. Defaults to 1.
   * @max 1
   */
  topP?: number | null;
  /** How many chat completion choices to generate for each input message. Defaults to 1. */
  n?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on whether they appear in the text so far,
   * increasing the model's likelihood to talk about new topics.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  presencePenalty?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on their existing frequency in the text so far,
   * decreasing the model's likelihood to repeat the same line verbatim.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  frequencyPenalty?: number | null;
  /**
   * json_object: Interpreted as passing "application/json" to the API.
   * json_schema. Fully recursive schemas are not supported. additional_properties is supported.
   * text: Interpreted as passing "text/plain" to the API.
   * Any other MIME type is passed as is to the model, such as passing "application/json" directly.
   */
  responseFormat?: ResponseFormat;
}

export interface ResponseFormat {
  /**
   * Must be one of text, json_object or json_schema.
   * @maxLength 100
   */
  type?: string | null;
  /** The schema object describes the output object for the model. Currently, only the JSON Schema Object is supported. */
  schema?: Record<string, any> | null;
}

export interface VideoInferenceRequest {
  /** Specifies the format of the output video. Supported formats are: MP4 and WEBM. Default: MP4. */
  outputFormat?: OutputFormatWithLiterals;
  /**
   * Sets the compression quality of the output video. Higher values preserve more quality but increase file size. Default: 95.
   * @min 20
   * @max 99
   */
  outputQuality?: number | null;
  /**
   * The text description that guides the video generation process. This prompt defines what you want to see in the video.
   * The length of the prompt must be at least 2 characters.
   * @minLength 2
   * @maxLength 100000
   */
  positivePrompt?: string | null;
  /**
   * Specifies what you want to avoid in the generated video.
   * @maxLength 100000
   */
  negativePrompt?: string | null;
  /**
   * An array of objects that define key frames to guide video generation.
   * @maxSize 100
   */
  frameImages?: FrameImage[];
  /**
   * An array containing reference images used to condition the generation process. Must be URLs pointing to the images. The images must be accessible publicly.
   * @maxSize 10
   * @maxLength 100000
   */
  referenceImages?: string[] | null;
  /**
   * The width of the generated video in pixels. Must be a multiple of 8 for compatibility with video encoding standards.
   * @min 256
   * @max 10000
   */
  width?: number | null;
  /**
   * The height of the generated video in pixels. Must be a multiple of 8 for compatibility with video encoding standards.
   * @min 256
   * @max 10000
   */
  height?: number | null;
  /** The AI model to use for video generation. */
  model?: VideoModelWithLiterals;
  /**
   * Video model as a string
   * @maxLength 1000
   */
  modelId?: string | null;
  /**
   * The length of the generated video in seconds.
   * @min 1
   * @max 10
   */
  duration?: number | null;
  /**
   * The frame rate (frames per second) of the generated video. Default: 24.
   * @min 15
   * @max 60
   */
  fps?: number | null;
  /**
   * The number of denoising steps the model performs during video generation.
   * @min 10
   * @max 50
   */
  steps?: number | null;
  /** A seed is a value used to randomize the video generation. */
  seed?: string | null;
  /**
   * Controls how closely the video generation follows your prompt. Recommended range is 6.0-10.0 for most video models.
   * @max 50
   */
  cfgScale?: number | null;
  /**
   * Specifies how many videos to generate for the given parameters. Default: 1.
   * @min 1
   * @max 4
   */
  numberResults?: number | null;
  /**
   * Contains provider-specific configuration settings that customize the behavior of different AI models and services.
   * @deprecated Contains provider-specific configuration settings that customize the behavior of different AI models and services.
   * @replacedBy provider_settings_string
   * @targetRemovalDate 2030-01-01
   */
  providerSettings?: Record<string, any> | null;
  /**
   * Contains provider-specific configuration settings that customize the behavior of different AI models and services.
   * Uses a string representation that's parsed directly into JSON to avoid issues with number parsing.
   * https://stackoverflow.com/questions/51818125/how-to-use-ints-in-a-protobuf-struct
   * @maxLength 1000000
   */
  providerSettingsString?: string | null;
  /**
   * Skip polling flag - if set to false, will poll until video generation is complete
   * If not set or true, returns immediately with task UUID for manual polling
   */
  skipPolling?: boolean | null;
  /** Inputs for the video generation process. */
  inputs?: VideoInputs;
}

export enum OutputFormat {
  UNKNOWN_OUTPUT_FORMAT = 'UNKNOWN_OUTPUT_FORMAT',
  /** MPEG-4 video format, widely compatible and recommended for most use cases.MPEG-4 video format, widely compatible and recommended for most use cases. */
  MP4 = 'MP4',
  /** WebM video format, optimized for web delivery and smaller file sizes. */
  WEBM = 'WEBM',
}

/** @enumType */
export type OutputFormatWithLiterals =
  | OutputFormat
  | 'UNKNOWN_OUTPUT_FORMAT'
  | 'MP4'
  | 'WEBM';

export interface FrameImage {
  /**
   * Specifies the input image that will be used to constrain the video content at the specified frame position.
   * Must be a URL pointing to the image. The image must be accessible publicly.
   * Deprecated: use `image` instead. Kept for backward compatibility.
   * @maxLength 100000
   */
  inputImage?: string;
  /**
   * Specifies the position of this frame constraint within the video timeline.
   * Can be "first", "last", or a numeric frame number.
   * @maxLength 20
   */
  frame?: string | null;
  /**
   * Specifies the input image that will be used to constrain the video content at the specified frame position.
   * Must be a URL pointing to the image. The image must be accessible publicly.
   * This is the standard field name going forward; takes priority over input_image if both are set.
   * @maxLength 100000
   */
  image?: string | null;
}

export enum VideoModel {
  UNKNOWN_VIDEO_MODEL = 'UNKNOWN_VIDEO_MODEL',
  SEEDANCE_1_0_PRO = 'SEEDANCE_1_0_PRO',
  SEEDANCE_1_0_LITE = 'SEEDANCE_1_0_LITE',
  SEEDANCE_1_0_PRO_FAST = 'SEEDANCE_1_0_PRO_FAST',
  FROM_MODEL_ID = 'FROM_MODEL_ID',
}

/** @enumType */
export type VideoModelWithLiterals =
  | VideoModel
  | 'UNKNOWN_VIDEO_MODEL'
  | 'SEEDANCE_1_0_PRO'
  | 'SEEDANCE_1_0_LITE'
  | 'SEEDANCE_1_0_PRO_FAST'
  | 'FROM_MODEL_ID';

export interface VideoInputs {
  /**
   * An array of objects that define key frames to guide video generation.
   * @maxSize 100
   */
  frameImages?: FrameImage[];
  /**
   * An array containing reference images used to condition the generation process.
   * Must be URLs pointing to the images. The images must be accessible publicly.
   * @maxSize 10
   * @maxLength 100000
   */
  referenceImages?: string[] | null;
  /**
   * An array containing reference videos used to influence the generation process.
   * These videos provide visual and motion guidance for style and movement patterns.
   * Must be URLs pointing to the videos. The videos must be accessible publicly.
   * @maxSize 10
   * @maxLength 100000
   */
  referenceVideos?: string[] | null;
}

export interface V1OpenAiResponsesRequest {
  /** ID of the model to use. */
  model?: V1ResponsesModelWithLiterals;
  /**
   * Specify additional output data to include in the model response. Currently supported values are:
   * code_interpreter_call.outputs: Includes the outputs of python code execution in code interpreter tool call items.
   * computer_call_output.output.image_url: Include image urls from the computer call output.
   * file_search_call.results: Include the search results of the file search tool call.
   * message.input_image.image_url: Include image urls from the input message.
   * message.output_text.logprobs: Include logprobs with assistant messages.
   * reasoning.encrypted_content: Includes an encrypted version of reasoning tokens in reasoning item outputs.
   * This enables reasoning items to be used in multi-turn conversations when using the Responses API statelessly
   * (like when the store parameter is set to false, or when an organization is enrolled in the zero data retention program).
   * @maxSize 20
   * @maxLength 10000
   */
  include?: string[] | null;
  /**
   * Text, image, or file inputs to the model, used to generate a response.
   * @maxSize 1000
   */
  input?: V1ResponsesInputItem[];
  /**
   * A system (or developer) message inserted into the model's context.
   * @maxLength 100000000
   */
  instructions?: string | null;
  /** An upper bound for the number of tokens that can be generated for a response. */
  maxOutputTokens?: number | null;
  /** The maximum number of total calls to built-in tools that can be processed in a response. */
  maxToolCalls?: number | null;
  /** Whether to allow the model to run tool calls in parallel. */
  parallelToolCalls?: boolean | null;
  /**
   * The unique ID of the previous response to the model. Use this to create multi-turn conversations.
   * @maxLength 100
   */
  previousResponseId?: string | null;
  /** o-series models only */
  reasoning?: V1ResponsesReasoning;
  /** What sampling temperature to use, between 0 and 2. */
  temperature?: number | null;
  /** Configuration options for a text response from the model. Can be plain text or structured JSON data. */
  text?: V1ResponsesTextFormat;
  /** How the model should select which tool (or tools) to use. */
  toolChoice?: V1ResponsesToolChoice;
  /**
   * A list of tools the model may call.
   * @maxSize 1000
   */
  tools?: V1ResponsesTool[];
  /**
   * An integer between 0 and 20 specifying the number of most likely tokens to return at each token position, each with an associated log probability.
   * @max 20
   */
  topLogprobs?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling,
   * where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both.
   */
  topP?: number | null;
  /**
   * The truncation strategy to use for the model response.
   * auto: If the context of this response and previous ones exceeds the model's context window size, the model will truncate the response to fit the context window by dropping input items in the middle of the conversation.
   * disabled (default): If a model response will exceed the context window size for a model, the request will fail with a 400 error.
   * @maxLength 100
   */
  truncation?: string | null;
  /** Whether to store the generated model response for later retrieval via API. */
  store?: boolean | null;
}

export interface V1ResponsesInputItem extends V1ResponsesInputItemItemOneOf {
  /**
   * A message input to the model with a role indicating instruction following hierarchy.
   * Instructions given with the developer or system role take precedence over instructions given with the user role.
   * Messages with the assistant role are presumed to have been generated by the model in previous interactiontons.
   */
  message?: V1ResponsesInputMessage;
  /** An output message from the model. */
  outputMessage?: V1ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: V1ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: V1ResponsesFunctionToolCall;
  /** The output/result of a function call. */
  functionToolCallOutput?: V1ResponsesFunctionToolCallOutput;
  /** A reasoning item output from the model. */
  reasoning?: V1ResponsesReasoningOutput;
  /** A code interpreter tool call made by the model. */
  codeInterpreterToolCall?: V1ResponsesCodeInterpreterToolCall;
}

/** @oneof */
export interface V1ResponsesInputItemItemOneOf {
  /**
   * A message input to the model with a role indicating instruction following hierarchy.
   * Instructions given with the developer or system role take precedence over instructions given with the user role.
   * Messages with the assistant role are presumed to have been generated by the model in previous interactiontons.
   */
  message?: V1ResponsesInputMessage;
  /** An output message from the model. */
  outputMessage?: V1ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: V1ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: V1ResponsesFunctionToolCall;
  /** The output/result of a function call. */
  functionToolCallOutput?: V1ResponsesFunctionToolCallOutput;
  /** A reasoning item output from the model. */
  reasoning?: V1ResponsesReasoningOutput;
  /** A code interpreter tool call made by the model. */
  codeInterpreterToolCall?: V1ResponsesCodeInterpreterToolCall;
}

export interface V1ResponsesInputMessage {
  /** The role of the message input. One of user, system, or developer. */
  role?: ResponsesInputMessageResponsesMessageRoleWithLiterals;
  /**
   * The content of the message, which can be text, image, or file.
   * @maxSize 2000
   */
  content?: V1ResponsesInputMessageContent[];
}

export enum ResponsesInputMessageResponsesMessageRole {
  UNKNOWN_RESPONSE = 'UNKNOWN_RESPONSE',
  USER = 'USER',
  SYSTEM = 'SYSTEM',
  DEVELOPER = 'DEVELOPER',
}

/** @enumType */
export type ResponsesInputMessageResponsesMessageRoleWithLiterals =
  | ResponsesInputMessageResponsesMessageRole
  | 'UNKNOWN_RESPONSE'
  | 'USER'
  | 'SYSTEM'
  | 'DEVELOPER';

export interface V1ResponsesInputMessageContent
  extends V1ResponsesInputMessageContentContentValueOneOf {
  /**
   * Text content
   * @maxLength 1000000
   */
  text?: string | null;
  /** Image content */
  imageUrl?: ResponsesInputMessageContentImageInput;
  /** File content */
  fileInput?: ResponsesInputMessageContentFileInput;
  /**
   * The type of the content part
   * @maxLength 100
   */
  type?: string | null;
}

/** @oneof */
export interface V1ResponsesInputMessageContentContentValueOneOf {
  /**
   * Text content
   * @maxLength 1000000
   */
  text?: string | null;
  /** Image content */
  imageUrl?: ResponsesInputMessageContentImageInput;
  /** File content */
  fileInput?: ResponsesInputMessageContentFileInput;
}

export interface ResponsesInputMessageContentImageInput {
  /**
   * The URL or file_id of the image
   * @maxLength 100000
   */
  imageUrl?: string | null;
  /**
   * Detail level: high, low, or auto
   * @maxLength 10
   */
  detail?: string | null;
}

export interface ResponsesInputMessageContentFileInput {
  /**
   * File identification - one of these should be provided
   * @maxLength 100000
   */
  fileUrl?: string | null;
  /**
   * filename
   * @maxLength 255
   */
  filename?: string | null;
}

export interface V1ResponsesFunctionToolCallOutput {
  /**
   * The type of the output. Always "function_call_output".
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the function call output.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The output/result of the function call.
   * @maxLength 1000000000
   */
  output?: string | null;
  /**
   * The call ID that links this output to its original call.
   * @maxLength 100
   */
  callId?: string | null;
}

export interface V1ResponsesTextFormat
  extends V1ResponsesTextFormatFormatOneOf {
  /** Structured Outputs configuration options, including a JSON Schema. */
  jsonSchema?: ResponsesTextFormatJsonSchema;
  /**
   * Constrains the verbosity of the model's response. Lower values will result in more concise responses,
   * while higher values will result in more verbose responses. Currently supported values are low, medium, and high.
   * @maxLength 100
   */
  verbosity?: string | null;
}

/** @oneof */
export interface V1ResponsesTextFormatFormatOneOf {
  /** Structured Outputs configuration options, including a JSON Schema. */
  jsonSchema?: ResponsesTextFormatJsonSchema;
}

export interface ResponsesTextFormatJsonSchema {
  /**
   * The name of the response format. Must be a-z, A-Z, 0-9, or contain underscores and dashes, with a maximum length of 64.
   * @maxLength 64
   */
  name?: string | null;
  /** The schema object describes the output object for the model. Currently, only the JSON Schema Object is supported. */
  schema?: Record<string, any> | null;
  /**
   * The type of response format being defined. Always json_schema.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * A description of what the response format is for, used by the model to determine how to respond in the format.
   * @maxLength 100000
   */
  description?: string | null;
  /**
   * Whether to enable strict schema adherence when generating the output.
   * If set to true, the model will always follow the exact schema defined in the schema field.
   * Only a subset of JSON Schema is supported when strict is true. To learn more, read the
   */
  strict?: boolean | null;
}

export interface V1ResponsesToolChoice {
  /**
   * Tool choice mode
   * Controls which (if any) tool is called by the model.
   * none means the model will not call any tool and instead generates a message.
   * auto means the model can pick between generating a message or calling one or more tools.
   * required means the model must call one or more tools.
   * @maxLength 100
   */
  mode?: string | null;
  /**
   * The type of hosted tool choice.
   * Allowed values are:
   * file_search
   * web_search_preview
   * computer_use_preview
   * code_interpreter
   * image_generation
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The name of the function to be called.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The label of the MCP server to use.
   * @maxLength 100
   */
  serverLabel?: string | null;
}

export interface V1ResponsesTool extends V1ResponsesToolToolTypeOneOf {
  /** A hosted tool that provides web search capabilities. */
  webSearch?: V1ResponsesWebSearch;
  /** A function that the model can call to perform a specific action. */
  function?: V1ResponsesFunction;
  /** Add code interpreter */
  codeInterpreter?: V1ResponsesCodeInterpreter;
}

/** @oneof */
export interface V1ResponsesToolToolTypeOneOf {
  /** A hosted tool that provides web search capabilities. */
  webSearch?: V1ResponsesWebSearch;
  /** A function that the model can call to perform a specific action. */
  function?: V1ResponsesFunction;
  /** Add code interpreter */
  codeInterpreter?: V1ResponsesCodeInterpreter;
}

export interface V1ResponsesWebSearch {
  /**
   * The type of the web search tool. One of web_search_preview or web_search_preview_2025_03_11.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * High level guidance for the amount of context window space to use for the search. One of low, medium, or high. medium is the default.
   * @maxLength 100
   */
  searchContextSize?: string | null;
  /** To refine search results based on geography, you can specify an approximate user location using country, city, region, and/or timezone. */
  userLocation?: ResponsesWebSearchUserLocation;
}

export interface ResponsesWebSearchUserLocation {
  /**
   * The type of location approximation. Always approximate.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * Free text input for the city of the user, e.g. San Francisco.
   * @maxLength 100
   */
  city?: string | null;
  /**
   * The two-letter ISO country code of the user, e.g. US.
   * https://en.wikipedia.org/wiki/ISO_3166-1
   * @maxLength 2
   */
  country?: string | null;
  /**
   * Free text input for the region of the user, e.g. California.
   * @maxLength 100
   */
  region?: string | null;
  /**
   * The IANA timezone of the user, e.g. America/Los_Angeles.
   * https://timeapi.io/documentation/iana-timezones
   * @maxLength 100
   */
  timezone?: string | null;
}

export interface V1ResponsesFunction {
  /**
   * The type of the function tool. Always function.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The name of the function to call.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The description of what the function does.
   * @maxLength 100000
   */
  description?: string | null;
  /** The parameters the functions accepts, described as a JSON Schema object. */
  parameters?: Record<string, any> | null;
  /** If true, the model will strictly follow the function parameters schema (a.k.a. open-ai structured outputs). */
  strict?: boolean | null;
}

export interface V1ResponsesCodeInterpreter {
  /**
   * The type of the code interpreter tool. Always code_interpreter.
   * @maxLength 100
   */
  type?: string | null;
  /** The code interpreter container configuration */
  container?: V1ResponsesCodeInterpreterContainer;
}

export interface V1ResponsesCodeInterpreterContainer
  extends V1ResponsesCodeInterpreterContainerContainerTypeOneOf {
  /**
   * Container ID string
   * @maxLength 100
   */
  containerId?: string | null;
  /** Auto container with file IDs */
  autoContainer?: V1ResponsesCodeInterpreterContainerAuto;
}

/** @oneof */
export interface V1ResponsesCodeInterpreterContainerContainerTypeOneOf {
  /**
   * Container ID string
   * @maxLength 100
   */
  containerId?: string | null;
  /** Auto container with file IDs */
  autoContainer?: V1ResponsesCodeInterpreterContainerAuto;
}

export interface V1ResponsesCodeInterpreterContainerAuto {
  /**
   * Always "auto"
   * @maxLength 10
   */
  type?: string | null;
}

export interface OpenAiResponsesRequest {
  /** ID of the model to use. */
  model?: ResponsesModelWithLiterals;
  /**
   * Specify additional output data to include in the model response. Currently supported values are:
   * code_interpreter_call.outputs: Includes the outputs of python code execution in code interpreter tool call items.
   * computer_call_output.output.image_url: Include image urls from the computer call output.
   * file_search_call.results: Include the search results of the file search tool call.
   * message.input_image.image_url: Include image urls from the input message.
   * message.output_text.logprobs: Include logprobs with assistant messages.
   * reasoning.encrypted_content: Includes an encrypted version of reasoning tokens in reasoning item outputs.
   * This enables reasoning items to be used in multi-turn conversations when using the Responses API statelessly
   * (like when the store parameter is set to false, or when an organization is enrolled in the zero data retention program).
   * @maxSize 20
   * @maxLength 10000
   */
  include?: string[] | null;
  /**
   * Text, image, or file inputs to the model, used to generate a response.
   * @maxSize 1000
   */
  input?: ResponsesInputItem[];
  /**
   * A system (or developer) message inserted into the model's context.
   * @maxLength 100000000
   */
  instructions?: string | null;
  /** An upper bound for the number of tokens that can be generated for a response. */
  maxOutputTokens?: number | null;
  /** The maximum number of total calls to built-in tools that can be processed in a response. */
  maxToolCalls?: number | null;
  /** Whether to allow the model to run tool calls in parallel. */
  parallelToolCalls?: boolean | null;
  /**
   * The unique ID of the previous response to the model. Use this to create multi-turn conversations.
   * @maxLength 100
   */
  previousResponseId?: string | null;
  /** o-series models only */
  reasoning?: ResponsesReasoning;
  /** What sampling temperature to use, between 0 and 2. */
  temperature?: number | null;
  /** Configuration options for a text response from the model. Can be plain text or structured JSON data. */
  text?: ResponsesTextFormat;
  /** How the model should select which tool (or tools) to use. */
  toolChoice?: ResponsesToolChoice;
  /**
   * A list of tools the model may call.
   * @maxSize 1000
   */
  tools?: ResponsesTool[];
  /**
   * An integer between 0 and 20 specifying the number of most likely tokens to return at each token position, each with an associated log probability.
   * @max 20
   */
  topLogprobs?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling,
   * where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both.
   */
  topP?: number | null;
  /**
   * The truncation strategy to use for the model response.
   * auto: If the context of this response and previous ones exceeds the model's context window size, the model will truncate the response to fit the context window by dropping input items in the middle of the conversation.
   * disabled (default): If a model response will exceed the context window size for a model, the request will fail with a 400 error.
   * @maxLength 100
   */
  truncation?: string | null;
  /** Whether to store the generated model response for later retrieval via API. */
  store?: boolean | null;
}

export interface ResponsesInputItem extends ResponsesInputItemItemOneOf {
  /**
   * A message input to the model with a role indicating instruction following hierarchy.
   * Instructions given with the developer or system role take precedence over instructions given with the user role.
   * Messages with the assistant role are presumed to have been generated by the model in previous interactiontons.
   */
  message?: ResponsesInputMessage;
  /** An output message from the model. */
  outputMessage?: ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: ResponsesFunctionToolCall;
  /** The output/result of a function call. */
  functionToolCallOutput?: ResponsesFunctionToolCallOutput;
  /** A reasoning item output from the model. */
  reasoning?: ResponsesReasoningOutput;
  /** A code interpreter tool call made by the model. */
  codeInterpreterToolCall?: ResponsesCodeInterpreterToolCall;
}

/** @oneof */
export interface ResponsesInputItemItemOneOf {
  /**
   * A message input to the model with a role indicating instruction following hierarchy.
   * Instructions given with the developer or system role take precedence over instructions given with the user role.
   * Messages with the assistant role are presumed to have been generated by the model in previous interactiontons.
   */
  message?: ResponsesInputMessage;
  /** An output message from the model. */
  outputMessage?: ResponsesOutputMessage;
  /** The results of a web search tool call. See the web search guide for more information. */
  webSearchToolCall?: ResponsesWebSearchToolCall;
  /** A function call made by the model. */
  functionToolCall?: ResponsesFunctionToolCall;
  /** The output/result of a function call. */
  functionToolCallOutput?: ResponsesFunctionToolCallOutput;
  /** A reasoning item output from the model. */
  reasoning?: ResponsesReasoningOutput;
  /** A code interpreter tool call made by the model. */
  codeInterpreterToolCall?: ResponsesCodeInterpreterToolCall;
}

export interface ResponsesInputMessage {
  /** The role of the message input. One of user, system, or developer. */
  role?: ResponsesMessageRoleWithLiterals;
  /**
   * The content of the message, which can be text, image, or file.
   * @maxSize 2000
   */
  content?: ResponsesInputMessageContent[];
}

export enum ResponsesMessageRole {
  UNKNOWN_RESPONSE = 'UNKNOWN_RESPONSE',
  USER = 'USER',
  SYSTEM = 'SYSTEM',
  DEVELOPER = 'DEVELOPER',
}

/** @enumType */
export type ResponsesMessageRoleWithLiterals =
  | ResponsesMessageRole
  | 'UNKNOWN_RESPONSE'
  | 'USER'
  | 'SYSTEM'
  | 'DEVELOPER';

export interface ResponsesInputMessageContent
  extends ResponsesInputMessageContentContentValueOneOf {
  /**
   * Text content
   * @maxLength 1000000
   */
  text?: string | null;
  /** Image content */
  imageUrl?: ImageInput;
  /** File content */
  fileInput?: FileInput;
  /**
   * The type of the content part
   * @maxLength 100
   */
  type?: string | null;
}

/** @oneof */
export interface ResponsesInputMessageContentContentValueOneOf {
  /**
   * Text content
   * @maxLength 1000000
   */
  text?: string | null;
  /** Image content */
  imageUrl?: ImageInput;
  /** File content */
  fileInput?: FileInput;
}

export interface ImageInput {
  /**
   * The URL or file_id of the image
   * @maxLength 100000
   */
  imageUrl?: string | null;
  /**
   * Detail level: high, low, or auto
   * @maxLength 10
   */
  detail?: string | null;
}

export interface FileInput {
  /**
   * File identification - one of these should be provided
   * @maxLength 100000
   */
  fileUrl?: string | null;
  /**
   * filename
   * @maxLength 255
   */
  filename?: string | null;
}

export interface ResponsesFunctionToolCallOutput {
  /**
   * The type of the output. Always "function_call_output".
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The status of the function call output.
   * @maxLength 100
   */
  status?: string | null;
  /**
   * The output/result of the function call.
   * @maxLength 1000000000
   */
  output?: string | null;
  /**
   * The call ID that links this output to its original call.
   * @maxLength 100
   */
  callId?: string | null;
}

export interface ResponsesTextFormat extends ResponsesTextFormatFormatOneOf {
  /** Structured Outputs configuration options, including a JSON Schema. */
  jsonSchema?: JsonSchema;
  /**
   * Constrains the verbosity of the model's response. Lower values will result in more concise responses,
   * while higher values will result in more verbose responses. Currently supported values are low, medium, and high.
   * @maxLength 100
   */
  verbosity?: string | null;
}

/** @oneof */
export interface ResponsesTextFormatFormatOneOf {
  /** Structured Outputs configuration options, including a JSON Schema. */
  jsonSchema?: JsonSchema;
}

export interface JsonSchema {
  /**
   * The name of the response format. Must be a-z, A-Z, 0-9, or contain underscores and dashes, with a maximum length of 64.
   * @maxLength 64
   */
  name?: string | null;
  /** The schema object describes the output object for the model. Currently, only the JSON Schema Object is supported. */
  schema?: Record<string, any> | null;
  /**
   * The type of response format being defined. Always json_schema.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * A description of what the response format is for, used by the model to determine how to respond in the format.
   * @maxLength 100000
   */
  description?: string | null;
  /**
   * Whether to enable strict schema adherence when generating the output.
   * If set to true, the model will always follow the exact schema defined in the schema field.
   * Only a subset of JSON Schema is supported when strict is true. To learn more, read the
   */
  strict?: boolean | null;
}

export interface ResponsesToolChoice {
  /**
   * Tool choice mode
   * Controls which (if any) tool is called by the model.
   * none means the model will not call any tool and instead generates a message.
   * auto means the model can pick between generating a message or calling one or more tools.
   * required means the model must call one or more tools.
   * @maxLength 100
   */
  mode?: string | null;
  /**
   * The type of hosted tool choice.
   * Allowed values are:
   * file_search
   * web_search_preview
   * computer_use_preview
   * code_interpreter
   * image_generation
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The name of the function to be called.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The label of the MCP server to use.
   * @maxLength 100
   */
  serverLabel?: string | null;
}

export interface ResponsesTool extends ResponsesToolToolTypeOneOf {
  /** A hosted tool that provides web search capabilities. */
  webSearch?: ResponsesWebSearch;
  /** A function that the model can call to perform a specific action. */
  function?: ResponsesFunction;
  /** Add code interpreter */
  codeInterpreter?: ResponsesCodeInterpreter;
}

/** @oneof */
export interface ResponsesToolToolTypeOneOf {
  /** A hosted tool that provides web search capabilities. */
  webSearch?: ResponsesWebSearch;
  /** A function that the model can call to perform a specific action. */
  function?: ResponsesFunction;
  /** Add code interpreter */
  codeInterpreter?: ResponsesCodeInterpreter;
}

export interface ResponsesWebSearch {
  /**
   * The type of the web search tool. One of web_search_preview or web_search_preview_2025_03_11.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * High level guidance for the amount of context window space to use for the search. One of low, medium, or high. medium is the default.
   * @maxLength 100
   */
  searchContextSize?: string | null;
  /** To refine search results based on geography, you can specify an approximate user location using country, city, region, and/or timezone. */
  userLocation?: UserLocation;
}

export interface UserLocation {
  /**
   * The type of location approximation. Always approximate.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * Free text input for the city of the user, e.g. San Francisco.
   * @maxLength 100
   */
  city?: string | null;
  /**
   * The two-letter ISO country code of the user, e.g. US.
   * https://en.wikipedia.org/wiki/ISO_3166-1
   * @maxLength 2
   */
  country?: string | null;
  /**
   * Free text input for the region of the user, e.g. California.
   * @maxLength 100
   */
  region?: string | null;
  /**
   * The IANA timezone of the user, e.g. America/Los_Angeles.
   * https://timeapi.io/documentation/iana-timezones
   * @maxLength 100
   */
  timezone?: string | null;
}

export interface ResponsesFunction {
  /**
   * The type of the function tool. Always function.
   * @maxLength 100
   */
  type?: string | null;
  /**
   * The name of the function to call.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The description of what the function does.
   * @maxLength 100000
   */
  description?: string | null;
  /** The parameters the functions accepts, described as a JSON Schema object. */
  parameters?: Record<string, any> | null;
  /** If true, the model will strictly follow the function parameters schema (a.k.a. open-ai structured outputs). */
  strict?: boolean | null;
}

export interface ResponsesCodeInterpreter {
  /**
   * The type of the code interpreter tool. Always code_interpreter.
   * @maxLength 100
   */
  type?: string | null;
  /** The code interpreter container configuration */
  container?: ResponsesCodeInterpreterContainer;
}

export interface ResponsesCodeInterpreterContainer
  extends ResponsesCodeInterpreterContainerContainerTypeOneOf {
  /**
   * Container ID string
   * @maxLength 100
   */
  containerId?: string | null;
  /** Auto container with file IDs */
  autoContainer?: ResponsesCodeInterpreterContainerAuto;
}

/** @oneof */
export interface ResponsesCodeInterpreterContainerContainerTypeOneOf {
  /**
   * Container ID string
   * @maxLength 100
   */
  containerId?: string | null;
  /** Auto container with file IDs */
  autoContainer?: ResponsesCodeInterpreterContainerAuto;
}

export interface ResponsesCodeInterpreterContainerAuto {
  /**
   * Always "auto"
   * @maxLength 10
   */
  type?: string | null;
}

/** More info and default values at https://platform.openai.com/docs/api-reference/videos/create */
export interface CreateVideoRequest {
  /**
   * Text prompt that describes the video to generate.
   * @maxLength 10000
   */
  prompt?: string;
  /** The video generation model to use. */
  model?: V1VideoModelWithLiterals;
  /**
   * Size of the generated video (width x height in pixels). Examples: "720x1280", "1280x720".
   * @maxLength 50
   */
  size?: string | null;
  /**
   * Clip duration in seconds. Default is 4 seconds if not specified.
   * @min 1
   * @max 180
   */
  seconds?: number | null;
  /**
   * Optional publicly accessible URL to an image reference that guides generation.
   * @maxLength 5000
   * @format WEB_URL
   */
  inputReferenceUrl?: string | null;
}

export enum V1VideoModel {
  UNKNOWN_VIDEO_MODEL = 'UNKNOWN_VIDEO_MODEL',
  SORA_2 = 'SORA_2',
  SORA_2_PRO = 'SORA_2_PRO',
}

/** @enumType */
export type V1VideoModelWithLiterals =
  | V1VideoModel
  | 'UNKNOWN_VIDEO_MODEL'
  | 'SORA_2'
  | 'SORA_2_PRO';

export interface InvokeCustomOpenAiModelRequest
  extends InvokeCustomOpenAiModelRequestFunctionCallOneOf {
  /** Specifying a particular function via {"name":\ "my_function"} forces the model to call that function. */
  forceCallFunctionCallConfig?: Record<string, any> | null;
  /**
   * "none" means the model does not call a function, and responds to the end-user.
   * "auto" means the model can pick between an end-user or calling a function.
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10
   */
  defaultFunctionCallConfig?: string | null;
  /**
   * ID of the model to use.
   * @maxLength 10000
   */
  modelId?: string;
  /**
   * A list of messages comprising the conversation so far.
   * @minSize 1
   * @maxSize 1000
   */
  messages?: Fireworks_proxyV1ChatCompletionMessage[];
  /**
   * A list of functions the model may generate JSON inputs for.
   * @maxSize 100
   * @deprecated
   * @replacedBy tools
   */
  functions?: InvokeCustomOpenAiModelRequestFunctionSignature[];
  /**
   * What sampling temperature to use, between 0 and 2. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic.
   * We generally recommend altering this or top_p but not both.
   * @max 2
   */
  temperature?: number | null;
  /**
   * An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass.
   * So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * We generally recommend altering this or temperature but not both. Defaults to 1.
   */
  topP?: number | null;
  /** How many chat completion choices to generate for each input message. Defaults to 1. */
  n?: number | null;
  /**
   * Stream: Up to 4 sequences where the API will stop generating further tokens.
   * @maxSize 4
   * @maxLength 100
   */
  stop?: string[];
  /**
   * The maximum number of tokens allowed for the generated answer.
   * By default, the number of tokens the model can return will be (4096 - prompt tokens).
   */
  maxTokens?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on whether they appear in the text so far,
   * increasing the model's likelihood to talk about new topics.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  presencePenalty?: number | null;
  /**
   * Number between -2.0 and 2.0.
   * Positive values penalize new tokens based on their existing frequency in the text so far,
   * decreasing the model's likelihood to repeat the same line verbatim.
   * Defaults to 0.
   * @min -2
   * @max 2
   */
  frequencyPenalty?: number | null;
  /**
   * Modify the likelihood of specified tokens appearing in the completion.
   * Accepts a json object that maps tokens (specified by their token ID in the tokenizer) to an associated bias value from -100 to 100.
   * Mathematically, the bias is added to the logits generated by the model prior to sampling.
   * The exact effect will vary per model, but values between -1 and 1 should decrease or increase likelihood of selection;
   * values like -100 or 100 should result in a ban or exclusive selection of the relevant token.
   */
  logitBias?: Record<string, number>;
  /**
   * A unique identifier representing your end-user, which can help OpenAI to monitor and detect abuse.
   * @maxLength 100
   */
  user?: string | null;
  /**
   * This feature is in Beta. If specified, our system will make a best effort to sample deterministically, such that
   * repeated requests with the same "seed" and parameters should return the same result. Determinism is not guaranteed,
   * and you should refer to the "system_fingerprint" response parameter to monitor changes in the backend.
   */
  seed?: string | null;
  /**
   * Controls which (if any) function is called by the model.
   * "none" means the model will not call a function and instead generates a message.
   * "auto" means the model can pick between generating a message or calling a function.
   * Specifying a particular function via {"type: "function", "function": {"name": "my_function"}} forces the model to call that function.
   *
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10000
   */
  toolChoice?: string | null;
  /**
   * A list of tools the model may call. Currently, only functions are supported as a tool. Use this to provide a list of functions the model may generate JSON inputs for.
   * @maxSize 1000
   */
  tools?: InvokeCustomOpenAiModelRequestTool[];
  /**
   * An object specifying the format that the model must output. Compatible with gpt-4-1106-preview and gpt-3.5-turbo-1106.
   * Setting to type to "json_object" enables JSON mode, which guarantees the message the model generates is valid JSON.
   * Important: when using JSON mode, you must also instruct the model to produce JSON yourself via a system or user message.
   * Without this, the model may generate an unending stream of whitespace until the generation reaches the token limit,
   * resulting in a long-running and seemingly "stuck" request. Also note that the message content may be partially cut off if finish_reason="length",
   * which indicates the generation exceeded max_tokens or the conversation exceeded the max context length.
   */
  responseFormat?: InvokeCustomOpenAiModelRequestResponseFormat;
  /**
   * An upper bound for the number of tokens that can be generated for a completion,
   * including visible output tokens and reasoning tokens.
   */
  maxCompletionTokens?: number | null;
  /**
   * Constrains effort on reasoning for reasoning models. Currently supported values are low, medium, and high.
   * Reducing reasoning effort can result in faster responses and fewer tokens used on reasoning in a response.
   * o1 models only
   * @maxLength 100
   */
  reasoningEffort?: string | null;
  /** Whether to enable parallel function calling during tool use. */
  parallelToolCalls?: boolean | null;
  /**
   * Constrains the verbosity of the model's response. Lower values will result in more concise responses,
   * while higher values will result in more verbose responses.
   * Currently supported values are low, medium, and high.
   * @maxLength 100
   */
  verbosity?: string | null;
}

/** @oneof */
export interface InvokeCustomOpenAiModelRequestFunctionCallOneOf {
  /** Specifying a particular function via {"name":\ "my_function"} forces the model to call that function. */
  forceCallFunctionCallConfig?: Record<string, any> | null;
  /**
   * "none" means the model does not call a function, and responds to the end-user.
   * "auto" means the model can pick between an end-user or calling a function.
   * "none" is the default when no functions are present. "auto" is the default if functions are present.
   * @maxLength 10
   */
  defaultFunctionCallConfig?: string | null;
}

export interface InvokeCustomOpenAiModelRequestFunctionSignature {
  /**
   * The name of the function to be called.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The description of what the function does.
   * @maxLength 100000
   */
  description?: string | null;
  /** The parameters the functions accepts, described as a JSON Schema object. */
  parameters?: Record<string, any> | null;
  /** If true, the model will strictly follow the function parameters schema (a.k.a. open-ai structured outputs). */
  strict?: boolean | null;
}

export interface InvokeCustomOpenAiModelRequestTool {
  /**
   * The type of the tool. Currently, only "function" is supported.
   * @maxLength 100
   */
  type?: string;
  /** Function definition object. */
  function?: InvokeCustomOpenAiModelRequestFunctionSignature;
}

export interface InvokeCustomOpenAiModelRequestResponseFormat {
  /**
   * Must be one of text, json_object or json_schema.
   * @maxLength 100
   */
  type?: string | null;
  /** The schema object describes the output object for the model. Currently, only the JSON Schema Object is supported. */
  schema?: Record<string, any> | null;
}

export interface CreateContentTaskRequest {
  /**
   * The model to use for content creation.
   * @maxLength 255
   */
  model?: string;
  /**
   * The context items to create the content from.
   * @maxSize 50
   */
  content?: ContentItem[];
  /**
   * Returns the last frame image of the generated video. Default: false
   * Useful for generating multiple consecutive videos using the last frame as the first frame of the next video.
   */
  returnLastFrame?: boolean | null;
  /**
   * Service tier for processing the request. Default: default
   * can be one of:
   * default: Online inference mode (lower RPM, lower latency)
   * flex: Offline inference mode (higher TPD quota, 50% price, higher latency)
   * @maxLength 255
   */
  serviceTier?: string | null;
  /**
   * Whether the generated video includes audio synchronized with the visuals. Default: true
   * Only supported by Seedance 1.5 pro
   */
  generateAudio?: boolean | null;
  /**
   * Whether to enable Draft sample mode. Default: false
   * Only supported by Seedance 1.5 pro
   * true: Generate a 480p preview video (lower cost)
   * false: Generate a standard video
   */
  draft?: boolean | null;
  /**
   * The resolution of the output video. Default: 720p for Seedance 1.5 pro/lite, 1080p for pro/pro-fast
   * Valid values: 480p, 720p, 1080p
   * @maxLength 255
   */
  resolution?: string | null;
  /**
   * The aspect ratio of the output video. Default varies by model and scenario.
   * Valid values: 16:9, 4:3, 1:1, 3:4, 9:16, 21:9, adaptive
   * @maxLength 255
   */
  ratio?: string | null;
  /**
   * The duration of the output video in seconds. Default: 5
   * Value range: 2-12s
   * Seedance 1.5 pro: Use -1 to let the model decide duration (4-12s)
   * set max to to 120 for future changes
   * Choose either duration or frames; frames takes priority.
   * @min -1
   * @max 120
   */
  duration?: number | null;
  /**
   * Number of frames for the output video. Not supported by Seedance 1.5 pro.
   * Valid range: integers within [29, 289] that conform to format 25 + 4n (where n is positive integer)
   * Choose either duration or frames; frames takes priority.
   * Set max to 2890 for fuure changes
   * @min 29
   * @max 2890
   */
  frames?: number | null;
  /**
   * The seed controls randomness of output. Default: -1 (random)
   * Valid values: integers within [-1, 2^32-1]
   * @min -1
   * @max 4294967295
   */
  seed?: number | null;
  /**
   * Specifies whether to fix the camera. Default: false
   * Not supported for reference-image-to-video
   */
  cameraFixed?: boolean | null;
  /** Specifies whether to add watermarks to the output video. Default: false */
  watermark?: boolean | null;
  /**
   * Skip polling flag - if set to false, will poll until video generation is complete
   * If not set or true, returns immediately with task ID for manual polling
   */
  skipPolling?: boolean | null;
}

export interface ContentItem {
  /**
   * The type of the input content
   * Can be ont of 'text', 'image_url', 'draft_task'
   * @maxLength 255
   */
  type?: string | null;
  /**
   * The input text information for the model (required when type=TEXT)
   * Describes the video to be generated. Can include text prompt and optional parameters.
   * @maxLength 100000
   */
  text?: string | null;
  /** The input image object for the model (required when type=IMAGE_URL) */
  imageUrl?: ImageUrl;
  /**
   * The location or purpose of the image (required under certain conditions when type=IMAGE_URL)
   * Used for Image-to-Video scenarios
   * available values are:
   * 'last_frame', 'first_frame', 'reference_image'
   * @maxLength 255
   */
  role?: string | null;
  /**
   * The draft task object for generating official video from a draft.
   * Only supported by Seedance 1.5 Pro.
   * The platform will reuse the inputs from the draft video to generate the official video.
   */
  draftTask?: DraftTask;
}

export interface ImageUrl {
  /**
   * The image information, can be an image URL or Base64-encoded content
   * URL: Must be accessible
   * Base64: Format must be data:image/<format>;base64,<content> (e.g., data:image/png;base64,...)
   * Requirements: JPEG/PNG/WebP/BMP/TIFF/GIF (Seedance 1.5 Pro also supports HEIC/HEIF)
   * Aspect ratio: 0.4 to 2.5, shorter side > 300px, longer side < 6000px, size < 30MB
   * @maxLength 100000
   */
  url?: string | null;
}

export interface DraftTask {
  /**
   * The draft video task ID.
   * The platform will automatically reuse the user inputs applied by the draft video
   * (including model, content.text, content.image_url, generate_audio, seed, ratio, duration, frames, camera_fixed)
   * to generate the official video.
   * @maxLength 1000
   */
  id?: string | null;
}

export interface GenerateImageRequest {
  /**
   * The model to use for image generation.
   * Accepts a model ID (e.g. "seedream-5.0-lite", "seedream-4.5", "seedream-4.0") or an Endpoint ID.
   * @maxLength 255
   */
  model?: string;
  /**
   * Text prompt describing the image to generate.
   * Recommended: keep under 600 English words. Excessively long prompts may cause the model
   * to overlook details and focus only on major elements.
   * @maxLength 100000
   */
  prompt?: string;
  /**
   * Reference image(s) as URL or base64-encoded string. Max 14 reference images.
   *
   * Input formats:
   * - Image URL: must be publicly accessible.
   * - Base64: format must be "data:image/<format>;base64,<content>" (e.g. "data:image/png;base64,...").
   *
   * Image requirements:
   * - Formats: JPEG, PNG, WEBP, BMP, TIFF, GIF
   * - Aspect ratio (width/height): between [1/16, 16]
   * - Width and height: > 14px
   * - Size: up to 10 MB per image
   * - Total pixels: no more than 6000x6000 = 36,000,000 per image
   * @maxLength 3825
   * @maxSize 15
   */
  image?: string[];
  /**
   * Output image dimensions. Two methods available (cannot be combined):
   *
   * Method 1 - Resolution shorthand (let the model determine width/height from prompt context):
   * seedream-5.0-lite: "2K", "3K"
   * seedream-4.5:      "2K", "4K"
   * seedream-4.0:      "1K", "2K", "4K"
   *
   * Method 2 - Explicit pixel dimensions (e.g. "2048x2048"):
   * Both total pixel range AND aspect ratio [1/16, 16] must be satisfied.
   * seedream-5.0-lite: total pixels [3,686,400 .. 10,404,496], default "2048x2048"
   * seedream-4.5:      total pixels [3,686,400 .. 16,777,216], default "2048x2048"
   * seedream-4.0:      total pixels [921,600 .. 16,777,216],   default "2048x2048"
   *
   * Common recommended dimensions (2K):
   * 1:1 = 2048x2048, 4:3 = 2304x1728, 3:4 = 1728x2304,
   * 16:9 = 2848x1600, 9:16 = 1600x2848, 21:9 = 3136x1344
   * @maxLength 255
   */
  size?: string | null;
  /**
   * How the generated images are returned. Default: "url".
   * "url":      returns a download link valid for 24 hours after generation.
   * "b64_json": returns the image data as a Base64-encoded string in JSON.
   * @maxLength 255
   */
  responseFormat?: string | null;
  /** Whether to add an "AI generated" watermark on the bottom-right corner. Default: true. */
  watermark?: boolean | null;
  /**
   * Whether to enable batch (sequential) image generation. Default: "disabled".
   * "auto":     the model decides whether to return multiple images and how many, based on the prompt.
   * "disabled": only one image is generated.
   * @maxLength 255
   */
  sequentialImageGeneration?: string | null;
  /** Configuration for batch image generation. Only effective when sequential_image_generation = "auto". */
  sequentialImageGenerationOptions?: SequentialImageGenerationOptions;
  /**
   * Output image file format. Default: "jpeg".
   * "jpeg": supported by all models (default, cannot be changed on seedream-4.5/4.0).
   * "png":  only supported by seedream-5.0-lite.
   * @maxLength 255
   */
  outputFormat?: string | null;
  /**
   * Configuration for prompt optimization.
   * Supported by seedream-5.0-lite, seedream-4.5 (standard mode only), and seedream-4.0.
   */
  optimizePromptOptions?: OptimizePromptOptions;
}

export interface SequentialImageGenerationOptions {
  /**
   * Maximum number of images to generate in this request. Default: 15. Range: [1, 15].
   * Actual count is constrained by: number of input reference images + generated images <= 15.
   */
  maxImages?: number | null;
}

export interface OptimizePromptOptions {
  /**
   * Prompt optimization mode. Default: "standard".
   * "standard": higher quality, longer generation time.
   * "fast":     faster but lower quality.
   * @maxLength 255
   */
  mode?: string | null;
}

export interface UserRequestInfo {
  /**
   * Interaction id
   * @maxLength 100
   */
  interactionId?: string | null;
  /**
   * Additional tags ,use comma separation format for multiple tags.
   * @maxLength 1000
   */
  additionalTags?: string | null;
  /**
   * GenAI feature name, required by FinOps for evaluation
   * @maxLength 1000
   */
  featureName?: string | null;
  /**
   * AppDefId to which the cost will be attributed to instead of the one that signs the request.
   * Will not work unless your application is explicitly allowed to override costs attribution.
   * Please reach out to #ai-tools-support if you think you need this field.
   * @format GUID
   */
  costAttributionOverrideId?: string | null;
}

export interface FallbackProperties {
  /**
   * Flag to indicate whether to opt out of the request forwarding as a fallback.
   * Currently, only the fallback from OpenAI to Azure is supported for certain OpenAI models.
   * If set to true, the request will not be redirected to Azure in the event of a server failure by OpenAI.
   */
  optOut?: boolean | null;
  /** FallbackPromptConfig object that describes optional second Prompt that can be invoked in case main invocation fails. */
  fallbackPromptConfig?: FallbackPromptConfig;
}

export interface AsyncGenerationConfig {
  /**
   * If specified, the response will be asynchronously sent to this topic via event.
   * @maxLength 1000
   */
  asyncResultTopic?: string | null;
  /** Skip polling flag. */
  skipPolling?: boolean | null;
  /** SPI generation configuration. */
  spiGenerationConfig?: SpiGenerationConfig;
}

export interface SpiGenerationConfig {
  /**
   * SPI client app_id.
   * @maxLength 100
   */
  appId?: string | null;
  /**
   * SPI client app_id.
   * @maxLength 100
   */
  componentId?: string | null;
}

export interface DynamicRequestConfig {
  /**
   * List of GatewayToolDefinition's, used to overwrite tools in the prompt.
   * @maxSize 1000
   */
  gatewayToolDefinitions?: GatewayToolDefinition[];
  /**
   * List of GatewayMessageDefinition's, which will be converted to model-specific format and appended to the messages saved in the prompt.
   * @maxSize 10000
   */
  gatewayMessageDefinitions?: GatewayMessageDefinition[];
}

export interface GatewayToolDefinition extends GatewayToolDefinitionToolOneOf {
  /** Custom tool */
  customTool?: GatewayToolDefinitionCustomTool;
  /** Built-in tool */
  builtInTool?: BuiltInTool;
}

/** @oneof */
export interface GatewayToolDefinitionToolOneOf {
  /** Custom tool */
  customTool?: GatewayToolDefinitionCustomTool;
  /** Built-in tool */
  builtInTool?: BuiltInTool;
}

export interface GatewayToolDefinitionCustomTool {
  /**
   * The name of the tool to be called.
   * @maxLength 64
   */
  name?: string | null;
  /**
   * The description of what the tool does.
   * @maxLength 100000
   */
  description?: string | null;
  /** The parameters the tool accepts, described as a JSON Schema object. */
  parameters?: Record<string, any> | null;
}

export interface BuiltInTool {
  /**
   * The name of the tool to be called.
   * @maxLength 64
   */
  name?: string | null;
  /** Optional parameters specific to the built-in tool. */
  parameters?: Record<string, any> | null;
}

export interface GatewayMessageDefinition {
  /** The role of the message author. */
  role?: GatewayMessageDefinitionRoleWithLiterals;
  /**
   * The content of the message.
   * @maxSize 4096
   */
  content?: GatewayContentBlock[];
}

export enum GatewayMessageDefinitionRole {
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
  TOOL = 'TOOL',
  DEVELOPER = 'DEVELOPER',
}

/** @enumType */
export type GatewayMessageDefinitionRoleWithLiterals =
  | GatewayMessageDefinitionRole
  | 'UNKNOWN'
  | 'USER'
  | 'ASSISTANT'
  | 'SYSTEM'
  | 'TOOL'
  | 'DEVELOPER';

export interface GatewayContentBlock extends GatewayContentBlockTypeOneOf {
  /** Text content. */
  text?: TextContent;
  /** Media content, represented as URL. */
  media?: MediaContent;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: ToolUseContent;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: ToolResultContent;
  /** Represents model's internal thought process. */
  thinking?: ThinkingTextContent;
}

/** @oneof */
export interface GatewayContentBlockTypeOneOf {
  /** Text content. */
  text?: TextContent;
  /** Media content, represented as URL. */
  media?: MediaContent;
  /** Tool use content, describes which tool should be used and with which parameters. */
  toolUse?: ToolUseContent;
  /** Tool result content, describes the result of tool invocation. */
  toolResult?: ToolResultContent;
  /** Represents model's internal thought process. */
  thinking?: ThinkingTextContent;
}

export interface ToolResultContent {
  /**
   * Tool use id
   * @maxLength 100
   */
  toolUseId?: string | null;
  /** Tool result is error. */
  error?: boolean | null;
  /**
   * Tool result content.
   * @maxSize 4096
   */
  content?: GatewayContentBlock[];
}

export interface GenerateContentByPromptObjectResponse {
  /** Model response object that describes the content generation result. */
  response?: GenerateContentModelResponse;
  /** Prompt's final form that was used to issue a GenerateImage request. */
  materializedPrompt?: Prompt;
  /**
   * Unique interaction identifier, generated during the generation request.
   * @format GUID
   */
  predictionId?: string;
}

export interface GenerateTextByPromptObjectRequest {
  /** Prompt object that describes the text generation request. */
  prompt?: Prompt;
  /** Key-value pairs that will be used to substitute templated parameters in the prompt. */
  params?: Record<string, string>;
  /** Contains additional information for the request, such as interaction ID and additional tags (comma-separated string). */
  userRequestInfo?: UserRequestInfo;
  /** Fallback properties for the request. */
  fallbackProperties?: FallbackProperties;
  /** Key-value pairs that will be used to overwrite dynamic properties in the prompt. */
  dynamicProperties?: Record<string, string>;
  /** Dynamic request configuration containing tools and other dynamic properties. */
  dynamicRequestConfig?: DynamicRequestConfig;
  /**
   * If specified, the response will be asynchronously sent to this topic via event.
   * This field is ignored for streaming requests.
   * @maxLength 1000
   */
  asyncResultTopic?: string | null;
}

export interface GeneratedTextChunk extends GeneratedTextChunkModelChunkOneOf {
  /** Azure OpenAI chat completion chunk. */
  azureChatCompletionChunk?: ChatCompletionChunk;
  /** OpenAI chat completion chunk. */
  openaiChatCompletionChunk?: OpenaiproxyV1ChatCompletionChunk;
  /** Anthropic (via Google proxy) chat completion chunk. */
  googleAnthropicStreamChunk?: GoogleproxyV1AnthropicStreamChunk;
  /** Google Gemini GenerateContentResponse chunk. */
  googleGeminiStreamChunk?: GenerateContentResponse;
  /** Anthropic (via Amazon proxy) chat completion chunk. */
  amazonAnthropicStreamChunk?: AnthropicStreamChunk;
  /** Native Anthropic API proxy stream chunk. */
  anthropicStreamChunk?: V1AnthropicStreamChunk;
  /** Fireworks chat completion chunk. */
  fireworksChatCompletionChunk?: V1ChatCompletionChunk;
  /**
   * Extracted text content from the chunk.
   * @maxLength 100
   */
  content?: string | null;
  /**
   * Unique interaction identifier, generated during the generation request.
   * @format GUID
   */
  predictionId?: string;
}

/** @oneof */
export interface GeneratedTextChunkModelChunkOneOf {
  /** Azure OpenAI chat completion chunk. */
  azureChatCompletionChunk?: ChatCompletionChunk;
  /** OpenAI chat completion chunk. */
  openaiChatCompletionChunk?: OpenaiproxyV1ChatCompletionChunk;
  /** Anthropic (via Google proxy) chat completion chunk. */
  googleAnthropicStreamChunk?: GoogleproxyV1AnthropicStreamChunk;
  /** Google Gemini GenerateContentResponse chunk. */
  googleGeminiStreamChunk?: GenerateContentResponse;
  /** Anthropic (via Amazon proxy) chat completion chunk. */
  amazonAnthropicStreamChunk?: AnthropicStreamChunk;
  /** Native Anthropic API proxy stream chunk. */
  anthropicStreamChunk?: V1AnthropicStreamChunk;
  /** Fireworks chat completion chunk. */
  fireworksChatCompletionChunk?: V1ChatCompletionChunk;
}

export interface ChatCompletionChunk {
  /**
   * A unique identifier for the chat completion. Each chunk has the same ID.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * A list of chat completion choices. Can contain more than one elements if n is greater than 1.
   * Can also be empty for the last chunk if you set stream_options: {"include_usage": true}.
   */
  choices?: ChunkChoice[];
  /**
   * The Unix timestamp (in seconds) of when the chat completion was created.
   * Each chunk has the same timestamp.
   */
  created?: number | null;
  /** Model that produced the completion. */
  model?: V1ModelWithLiterals;
  /**
   * This fingerprint represents the backend configuration that the model runs with. Can be used in conjunction with the
   * seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
  /**
   * The object type, which is always chat.completion.chunk.
   * @maxLength 100
   */
  object?: string | null;
  /**
   * An optional field that will only be present when you set stream_options: {"include_usage": true} in your request.
   * When present, it contains a null value except for the last chunk which contains the token usage statistics for the entire request.
   */
  usage?: CreateChatCompletionResponseTokenUsage;
  /** Cost of the entire request in micro cents. Calculated manually and is present only in the last chunk. */
  microcentsSpent?: string | null;
}

export interface ChunkDelta {
  /**
   * The contents of the chunk message.
   * @maxLength 100
   */
  content?: string | null;
  /** The role of the author of this message. */
  role?: ChatCompletionMessageMessageRoleWithLiterals;
  /**
   * Tool call requested by the model. Function arguments can be partial jsons and have to be assembled manually.
   * @maxSize 100
   */
  toolCalls?: ToolCall[];
}

export interface ChunkChoice {
  /** A chat completion delta generated by streamed model responses */
  delta?: ChunkDelta;
  /**
   * The reason the model stopped generating tokens. This will be
   * "stop" if the model hit a natural stop point or a provided stop sequence,
   * "length" if the maximum number of tokens specified in the request was reached,
   * "content_filter" if content was omitted due to a flag from our content filters,
   * "tool_calls" if the model called a tool
   * @maxLength 100
   */
  finishReason?: string | null;
  /** The index of the choice in the list of choices. */
  index?: number | null;
}

export interface OpenaiproxyV1ChatCompletionChunk {
  /**
   * A unique identifier for the chat completion. Each chunk has the same ID.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * A list of chat completion choices. Can contain more than one elements if n is greater than 1.
   * Can also be empty for the last chunk if you set stream_options: {"include_usage": true}.
   */
  choices?: V1ChatCompletionChunkChunkChoice[];
  /**
   * The Unix timestamp (in seconds) of when the chat completion was created.
   * Each chunk has the same timestamp.
   */
  created?: number | null;
  /** Model that produced the completion. */
  model?: OpenaiproxyV1ModelWithLiterals;
  /**
   * This fingerprint represents the backend configuration that the model runs with. Can be used in conjunction with the
   * seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
  /**
   * The object type, which is always chat.completion.chunk.
   * @maxLength 100
   */
  object?: string | null;
  /**
   * An optional field that will only be present when you set stream_options: {"include_usage": true} in your request.
   * When present, it contains a null value except for the last chunk which contains the token usage statistics for the entire request.
   */
  usage?: OpenaiproxyV1CreateChatCompletionResponseTokenUsage;
  /** Cost of the entire request in micro cents. Calculated manually and is present only in the last chunk. */
  microcentsSpent?: string | null;
}

export interface ChatCompletionChunkChunkChoiceChunkDelta {
  /**
   * The contents of the chunk message.
   * @maxLength 1000
   */
  content?: string | null;
  /** The role of the author of this message. */
  role?: OpenaiproxyV1ChatCompletionMessageMessageRoleWithLiterals;
  /**
   * Tool call requested by the model. Function arguments can be partial jsons and have to be assembled manually.
   * @maxSize 100
   */
  toolCalls?: V1ChatCompletionMessageToolCall[];
}

export interface V1ChatCompletionChunkChunkChoice {
  /** A chat completion delta generated by streamed model responses */
  delta?: ChatCompletionChunkChunkChoiceChunkDelta;
  /**
   * The reason the model stopped generating tokens. This will be
   * "stop" if the model hit a natural stop point or a provided stop sequence,
   * "length" if the maximum number of tokens specified in the request was reached,
   * "content_filter" if content was omitted due to a flag from our content filters,
   * "tool_calls" if the model called a tool
   * @maxLength 100
   */
  finishReason?: string | null;
  /** The index of the choice in the list of choices. */
  index?: number | null;
}

export interface GoogleproxyV1AnthropicStreamChunk
  extends GoogleproxyV1AnthropicStreamChunkContentOneOf {
  toolUse?: GoogleproxyV1ToolUse;
  contentBlockDelta?: GoogleproxyV1ContentBlockDelta;
  messageDelta?: V1AnthropicStreamChunkMessageDelta;
  redactedThinking?: GoogleproxyV1RedactedThinking;
  /**
   * The unique identifier for the response. The format and length of the ID might change over time.
   * @maxLength 512
   */
  responseId?: string;
  /**
   * The ID for the Anthropic Claude model that made the request.
   * @maxLength 512
   */
  model?: string;
  index?: number | null;
}

/** @oneof */
export interface GoogleproxyV1AnthropicStreamChunkContentOneOf {
  toolUse?: GoogleproxyV1ToolUse;
  contentBlockDelta?: GoogleproxyV1ContentBlockDelta;
  messageDelta?: V1AnthropicStreamChunkMessageDelta;
  redactedThinking?: GoogleproxyV1RedactedThinking;
}

export interface GoogleproxyV1ContentBlockDelta
  extends GoogleproxyV1ContentBlockDeltaDeltaOneOf {
  /** @maxLength 1000000 */
  text?: string;
  /** @maxLength 1000000 */
  partialJson?: string;
  /** @maxLength 1000000 */
  thinking?: string;
  /** @maxLength 1000000 */
  signature?: string;
}

/** @oneof */
export interface GoogleproxyV1ContentBlockDeltaDeltaOneOf {
  /** @maxLength 1000000 */
  text?: string;
  /** @maxLength 1000000 */
  partialJson?: string;
  /** @maxLength 1000000 */
  thinking?: string;
  /** @maxLength 1000000 */
  signature?: string;
}

export interface V1AnthropicStreamChunkMessageDelta {
  /**
   * The reason why Anthropic Claude stopped generating the response:
   * `end_turn` – The model reached a natural stopping point.
   * `max_tokens` – The generated text exceeded the value of the max_tokens input field or exceeded the maximum number of tokens that the model supports.
   * `stop_sequence` – The model generated one of the stop sequences that you specified in the stop_sequences input field.
   * @maxLength 512
   */
  stopReason?: string | null;
  /**
   * Which custom stop sequence was generated, if any.
   * @maxLength 512
   */
  stopSequence?: string | null;
  /** Token usage statistics. */
  usage?: GoogleproxyV1Usage;
  microcentsSpent?: string | null;
}

export interface AnthropicStreamChunk extends AnthropicStreamChunkContentOneOf {
  toolUse?: ToolUse;
  contentBlockDelta?: ContentBlockDelta;
  messageDelta?: MessageDelta;
  redactedThinking?: RedactedThinking;
  /**
   * The unique identifier for the response. The format and length of the ID might change over time.
   * @maxLength 512
   */
  responseId?: string;
  /**
   * The ID for the Anthropic Claude model that made the request.
   * @maxLength 512
   */
  model?: string;
  index?: number | null;
}

/** @oneof */
export interface AnthropicStreamChunkContentOneOf {
  toolUse?: ToolUse;
  contentBlockDelta?: ContentBlockDelta;
  messageDelta?: MessageDelta;
  redactedThinking?: RedactedThinking;
}

export interface ContentBlockDelta extends ContentBlockDeltaDeltaOneOf {
  /** @maxLength 1000000 */
  text?: string;
  /** @maxLength 1000000 */
  partialJson?: string;
  /** @maxLength 1000000 */
  thinking?: string;
  /** @maxLength 1000000 */
  signature?: string;
}

/** @oneof */
export interface ContentBlockDeltaDeltaOneOf {
  /** @maxLength 1000000 */
  text?: string;
  /** @maxLength 1000000 */
  partialJson?: string;
  /** @maxLength 1000000 */
  thinking?: string;
  /** @maxLength 1000000 */
  signature?: string;
}

export interface MessageDelta {
  /**
   * The reason why Anthropic Claude stopped generating the response:
   * `end_turn` – The model reached a natural stopping point.
   * `max_tokens` – The generated text exceeded the value of the max_tokens input field or exceeded the maximum number of tokens that the model supports.
   * `stop_sequence` – The model generated one of the stop sequences that you specified in the stop_sequences input field.
   * @maxLength 512
   */
  stopReason?: string | null;
  /**
   * Which custom stop sequence was generated, if any.
   * @maxLength 512
   */
  stopSequence?: string | null;
  /** Token usage statistics. */
  usage?: Usage;
  microcentsSpent?: string | null;
}

export interface V1AnthropicStreamChunk
  extends V1AnthropicStreamChunkContentOneOf {
  /** Announcement of a model-initiated tool call (client tools or Anthropic-run tools) */
  toolUse?: V1ToolUse;
  /**
   * Start of a server tool block at `index` (e.g., "web_search", "web_fetch", "code_execution").
   * The tool input will stream via ContentBlockDelta.partial_json for the SAME `index`,
   * and is finalized by ContentBlockStop for that `index`.
   */
  serverToolUse?: ServerToolUse;
  /** Start of a Web Search result block at `index`. Completion is marked by ContentBlockStop. */
  webSearchToolResult?: WebSearchToolResult;
  /** Start of a Web Fetch result block at `index`. Completion is marked by ContentBlockStop. */
  webFetchToolResult?: WebFetchToolResult;
  /** Start of a Code Execution result block at `index`. Completion is marked by ContentBlockStop. */
  codeExecutionToolResult?: CodeExecutionToolResult;
  /**
   * Incremental data that refines the content block at `index`
   * (text characters, tool-input JSON fragments, thinking text, or thinking signature).
   */
  contentBlockDelta?: V1ContentBlockDelta;
  /**
   * Top-level message updates:
   * - stop reason / stop sequence (when known),
   * - cumulative token usage (input, output, cache, server-tool counters),
   * - optional cost fields (e.g., microcents).
   */
  messageDelta?: AnthropicStreamChunkMessageDelta;
  /**
   * Redacted variant of thinking content when Claude’s safety systems redact internal reasoning.
   * Pass back unchanged in a follow-up request to let Claude continue without losing context.
   */
  redactedThinking?: V1RedactedThinking;
  /**
   * The unique identifier for the response. The format and length of the ID might change over time.
   * @maxLength 512
   */
  responseId?: string;
  /**
   * The ID for the Anthropic Claude model that made the request.
   * @maxLength 512
   */
  model?: string;
  /**
   * Index of the content block this chunk refers to (when relevant).
   * For example, text and tool-input deltas apply to the block at this index.
   */
  index?: number | null;
}

/** @oneof */
export interface V1AnthropicStreamChunkContentOneOf {
  /** Announcement of a model-initiated tool call (client tools or Anthropic-run tools) */
  toolUse?: V1ToolUse;
  /**
   * Start of a server tool block at `index` (e.g., "web_search", "web_fetch", "code_execution").
   * The tool input will stream via ContentBlockDelta.partial_json for the SAME `index`,
   * and is finalized by ContentBlockStop for that `index`.
   */
  serverToolUse?: ServerToolUse;
  /** Start of a Web Search result block at `index`. Completion is marked by ContentBlockStop. */
  webSearchToolResult?: WebSearchToolResult;
  /** Start of a Web Fetch result block at `index`. Completion is marked by ContentBlockStop. */
  webFetchToolResult?: WebFetchToolResult;
  /** Start of a Code Execution result block at `index`. Completion is marked by ContentBlockStop. */
  codeExecutionToolResult?: CodeExecutionToolResult;
  /**
   * Incremental data that refines the content block at `index`
   * (text characters, tool-input JSON fragments, thinking text, or thinking signature).
   */
  contentBlockDelta?: V1ContentBlockDelta;
  /**
   * Top-level message updates:
   * - stop reason / stop sequence (when known),
   * - cumulative token usage (input, output, cache, server-tool counters),
   * - optional cost fields (e.g., microcents).
   */
  messageDelta?: AnthropicStreamChunkMessageDelta;
  /**
   * Redacted variant of thinking content when Claude’s safety systems redact internal reasoning.
   * Pass back unchanged in a follow-up request to let Claude continue without losing context.
   */
  redactedThinking?: V1RedactedThinking;
}

export interface V1ContentBlockDelta extends V1ContentBlockDeltaDeltaOneOf {
  /**
   * Characters belonging to a text content block.
   * @maxLength 1000000000
   */
  text?: string;
  /**
   * A fragment of the tool `input` JSON (as a string) for a tool_use/server_tool_use block.
   * Multiple fragments across chunks together represent the final JSON value.
   * @maxLength 1000000000
   */
  partialJson?: string;
  /**
   * Portion of the model’s extended-thinking content for a thinking block.
   * @maxLength 1000000000
   */
  thinking?: string;
  /**
   * Signature data associated with a thinking block (emitted immediately before that block completes).
   * @maxLength 1000000000
   */
  signature?: string;
}

/** @oneof */
export interface V1ContentBlockDeltaDeltaOneOf {
  /**
   * Characters belonging to a text content block.
   * @maxLength 1000000000
   */
  text?: string;
  /**
   * A fragment of the tool `input` JSON (as a string) for a tool_use/server_tool_use block.
   * Multiple fragments across chunks together represent the final JSON value.
   * @maxLength 1000000000
   */
  partialJson?: string;
  /**
   * Portion of the model’s extended-thinking content for a thinking block.
   * @maxLength 1000000000
   */
  thinking?: string;
  /**
   * Signature data associated with a thinking block (emitted immediately before that block completes).
   * @maxLength 1000000000
   */
  signature?: string;
}

export interface AnthropicStreamChunkMessageDelta {
  /**
   * Why generation concluded for this assistant message, when applicable:
   * "end_turn" | "max_tokens" | "stop_sequence" | "tool_use" | "pause_turn" | "refusal".
   * @maxLength 512
   */
  stopReason?: string | null;
  /**
   * The specific custom stop sequence that was produced, if any.
   * @maxLength 512
   */
  stopSequence?: string | null;
  /** Cumulative token usage at this point in the stream. */
  usage?: V1Usage;
  /** Cost of the request so far, in microcents. */
  microcentsSpent?: string | null;
}

export interface V1ChatCompletionChunk {
  /**
   * A unique identifier for the chat completion. Each chunk has the same ID.
   * @maxLength 100
   */
  responseId?: string | null;
  /**
   * A list of chat completion choices. Can contain more than one elements if n is greater than 1.
   * Can also be empty for the last chunk if you set stream_options: {"include_usage": true}.
   */
  choices?: ChatCompletionChunkChunkChoice[];
  /**
   * The Unix timestamp (in seconds) of when the chat completion was created.
   * Each chunk has the same timestamp.
   */
  created?: number | null;
  /** Model that produced the completion. */
  modelId?: string;
  /**
   * This fingerprint represents the backend configuration that the model runs with. Can be used in conjunction with the
   * seed request parameter to understand when backend changes have been made that might impact determinism.
   * @maxLength 10000
   */
  systemFingerprint?: string | null;
  /**
   * The object type, which is always chat.completion.chunk.
   * @maxLength 100
   */
  object?: string | null;
  /**
   * An optional field that will only be present when you set stream_options: {"include_usage": true} in your request.
   * When present, it contains a null value except for the last chunk which contains the token usage statistics for the entire request.
   */
  usage?: InvokeCustomOpenAiModelResponseTokenUsage;
  /** Cost of the entire request in micro cents. Calculated manually and is present only in the last chunk. */
  microcentsSpent?: string | null;
}

export interface ChunkChoiceChunkDelta {
  /**
   * The contents of the chunk message.
   * @maxLength 1000
   */
  content?: string | null;
  /** The role of the author of this message. */
  role?: Fireworks_proxyV1ChatCompletionMessageMessageRoleWithLiterals;
  /**
   * Tool call requested by the model. Function arguments can be partial jsons and have to be assembled manually.
   * @maxSize 100
   */
  toolCalls?: ChatCompletionMessageToolCall[];
}

export interface ChatCompletionChunkChunkChoice {
  /** A chat completion delta generated by streamed model responses */
  delta?: ChunkChoiceChunkDelta;
  /**
   * The reason the model stopped generating tokens. This will be
   * "stop" if the model hit a natural stop point or a provided stop sequence,
   * "length" if the maximum number of tokens specified in the request was reached,
   * "content_filter" if content was omitted due to a flag from our content filters,
   * "tool_calls" if the model called a tool
   * @maxLength 100
   */
  finishReason?: string | null;
  /** The index of the choice in the list of choices. */
  index?: number | null;
}

export interface GenerateAudioRequest
  extends GenerateAudioRequestAudioRequestOneOf {
  /** OpenAi create speech request */
  openAiCreateSpeechRequest?: CreateSpeechRequest;
  /** ElevenLabs text to speech request */
  elevenlabsTextToSpeechRequest?: TextToSpeechRequest;
  /** Contains additional information for the request. */
  userRequestInfo?: UserRequestInfo;
}

/** @oneof */
export interface GenerateAudioRequestAudioRequestOneOf {
  /** OpenAi create speech request */
  openAiCreateSpeechRequest?: CreateSpeechRequest;
  /** ElevenLabs text to speech request */
  elevenlabsTextToSpeechRequest?: TextToSpeechRequest;
}

export interface CreateSpeechRequest {
  /** One of the available TTS models: https://platform.openai.com/docs/models#tts */
  model?: SpeechModelWithLiterals;
  /**
   * The text to generate audio for. The maximum length is 4096 characters.
   * @maxLength 4096
   */
  input?: string;
  /**
   * The voice to use when generating the audio. Supported voices are alloy, echo, fable, onyx, nova, and shimmer. Previews of the voices are available in the Text to speech guide.
   * @maxLength 100
   */
  voice?: string;
  /**
   * The format to audio in. Supported formats are mp3, opus, aac, flac, wav, and pcm.
   * @maxLength 100
   */
  responseFormat?: string | null;
  /**
   * The speed of the generated audio. Select a value from 0.25 to 4.0. 1.0 is the default.
   * @min 0.25
   * @max 4
   */
  speed?: number | null;
}

export enum SpeechModel {
  UNKNOWN_SPEECH_MODEL = 'UNKNOWN_SPEECH_MODEL',
  TTS_1 = 'TTS_1',
  TTS_1_HD = 'TTS_1_HD',
}

/** @enumType */
export type SpeechModelWithLiterals =
  | SpeechModel
  | 'UNKNOWN_SPEECH_MODEL'
  | 'TTS_1'
  | 'TTS_1_HD';

export interface TextToSpeechRequest {
  /**
   * Voice ID to be used, you can use https://api.elevenlabs.io/v1/voices to list all the available voices.
   * @maxLength 100
   */
  voiceId?: string;
  /**
   * The output format of the generated audio. List of supported values: mp3_22050_32, mp3_44100_32, mp3_44100_64, mp3_44100_96, mp3_44100_128, mp3_44100_192, pcm_16000, pcm_22050, pcm_24000, pcm_44100, ulaw_8000
   * @maxLength 100
   */
  outputFormat?: string | null;
  /**
   * When enable_logging is set to false full privacy mode will be used for the request.
   * This will mean history features are unavailable for this request, including request stitching.
   * Full privacy mode may only be used by enterprise customers.
   */
  enableLogging?: boolean;
  /**
   * The text that will get converted into speech.
   * @maxLength 10000000
   */
  text?: string;
  /** Identifier of the model that will be used, you can query them using GET /v1/models. The model needs to have support for text to speech, you can check this using the can_do_text_to_speech property. */
  modelId?: ElevenLabsTextToSpeechModelWithLiterals;
  /**
   * Language code (ISO 639-1) used to enforce a language for the model. Currently only Turbo v2.5 supports language enforcement. For other models, an error will be returned if language code is provided.
   * @maxLength 100
   */
  languageCode?: string | null;
  /** Voice settings overriding stored settings for the given voice. They are applied only on the given request. */
  voiceSettings?: VoiceSettings;
  /**
   * A list of pronunciation dictionary locators (id, version_id) to be applied to the text. They will be applied in order. You may have up to 3 locators per request
   * @maxSize 10
   */
  pronunciationDictionaryLocators?: PronunciationDictionaryLocator[];
  /** If specified, our system will make a best effort to sample deterministically, such that repeated requests with the same seed and parameters should return the same result. Determinism is not guaranteed. Must be integer between 0 and 4294967295. */
  seed?: string | null;
  /**
   * The text that came before the text of the current request. Can be used to improve the flow of prosody when concatenating together multiple generations or to influence the prosody in the current generation.
   * @maxLength 10000000
   */
  previousText?: string | null;
  /**
   * The text that comes after the text of the current request. Can be used to improve the flow of prosody when concatenating together multiple generations or to influence the prosody in the current generation.
   * @maxLength 10000000
   */
  nextText?: string | null;
  /**
   * A list of request_id of the samples that were generated before this generation. Can be used to improve the flow of prosody when splitting up a large task into multiple requests.
   * The results will be best when the same model is used across the generations. In case both previous_text and previous_request_ids is send, previous_text will be ignored. A maximum of 3 request_ids can be send.
   * @maxSize 100
   * @maxLength 10
   */
  previousRequestIds?: string[];
  /**
   * A list of request_id of the samples that were generated before this generation. Can be used to improve the flow of prosody when splitting up a large task into multiple requests.
   * The results will be best when the same model is used across the generations. In case both next_text and next_request_ids is send, next_text will be ignored. A maximum of 3 request_ids can be send.
   * @maxSize 100
   * @maxLength 10
   */
  nextRequestIds?: string[];
  /**
   * This parameter controls text normalization with three modes: ‘auto’, ‘on’, and ‘off’. When set to ‘auto’, the system will automatically decide whether to apply text normalization (e.g., spelling out numbers).
   * With ‘on’, text normalization will always be applied, while with ‘off’, it will be skipped. Cannot be turned on for ‘eleven_turbo_v2_5’ model.
   * Defaults to ‘auto’.
   * @maxLength 100
   */
  applyTextNormalization?: string | null;
  /** When set to true, response chunks will include with precise character-level timing information for audio-text synchronization. */
  withTimings?: boolean;
}

export enum ElevenLabsTextToSpeechModel {
  UNKNOWN_ELEVEN_LABS_TEXT_TO_SPEECH_MODEL = 'UNKNOWN_ELEVEN_LABS_TEXT_TO_SPEECH_MODEL',
  ELEVEN_MULTILINGUAL_V2 = 'ELEVEN_MULTILINGUAL_V2',
  ELEVEN_FLASH_V2_5 = 'ELEVEN_FLASH_V2_5',
  ELEVEN_FLASH_V2 = 'ELEVEN_FLASH_V2',
}

/** @enumType */
export type ElevenLabsTextToSpeechModelWithLiterals =
  | ElevenLabsTextToSpeechModel
  | 'UNKNOWN_ELEVEN_LABS_TEXT_TO_SPEECH_MODEL'
  | 'ELEVEN_MULTILINGUAL_V2'
  | 'ELEVEN_FLASH_V2_5'
  | 'ELEVEN_FLASH_V2';

export interface VoiceSettings {
  /** Defines the stability for voice settings. */
  stability?: number;
  /** Defines the similarity boost for voice settings. */
  similarityBoost?: number;
  /** Defines the style for voice settings. This parameter is available on V2+ models. */
  style?: number | null;
  /** Defines the use speaker boost for voice settings. This parameter is available on V2+ models. */
  useSpeakerBoost?: boolean;
}

export interface PronunciationDictionaryLocator {
  /**
   * pronunciation_dictionary_id
   * @maxLength 100
   */
  pronunciationDictionaryId?: string;
  /**
   * version_id
   * @maxLength 100
   */
  versionId?: string;
}

export interface GeneratedAudioChunk
  extends GeneratedAudioChunkAudioChunkOneOf {
  /** OpenAi create speech chunk */
  openAiSpeechChunk?: SpeechChunk;
  /** ElevenLabs create speech chunk */
  elevenlabsSpeechChunk?: TextToSpeechChunk;
}

/** @oneof */
export interface GeneratedAudioChunkAudioChunkOneOf {
  /** OpenAi create speech chunk */
  openAiSpeechChunk?: SpeechChunk;
  /** ElevenLabs create speech chunk */
  elevenlabsSpeechChunk?: TextToSpeechChunk;
}

export interface SpeechChunk {
  /** Partial audio file bytes. */
  content?: Uint8Array;
}

export interface TextToSpeechChunk {
  /** Base64 encoded audio chunk */
  audioBase64?: Uint8Array;
  /** Alignment information for the generated audio given the input text sequence. */
  alignment?: AlignmentInfoInChunk;
  /** Alignment information for the generated audio given the input normalized text sequence. */
  normalizedAlignment?: AlignmentInfoInChunk;
}

export interface AlignmentInfoInChunk {
  /**
   * Array of individual characters from the input or normalized text
   * @maxSize 1000000
   */
  characterStartTimesSeconds?: number[];
  /**
   * Array of start times (in seconds) for each character
   * @maxSize 1000000
   */
  characterEndTimesSeconds?: number[];
  /**
   * Array of end times (in seconds) for each character
   * @maxSize 1000000
   * @maxLength 1
   */
  characters?: string[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
